import {
  formatReferencingStyle,
  getTenantStyles,
  isFootnotingEnabled,
  isHangingIndent,
  isReferencingStyle,
  referencesTitle,
  validateReferencingStyle
} from "./chunk-LXYOQ4MX.mjs";

// src/features/feature-flag/provider.tsx
import { useQuery } from "@apollo/client";
import { useMemo } from "react";

// src/features/feature-flag/context.ts
import { createContext } from "react";
var FeatureFlagsContext = createContext({
  enabled: /* @__PURE__ */ new Set(),
  loading: false
});
FeatureFlagsContext.displayName = "FeatureFlagsContext";

// src/features/feature-flag/query.ts
import { gql } from "@apollo/client";
var ENABLED_FLAGS_QUERY = gql`
  query EnabledFlags($assessmentId: ID!) {
    enabledFlags(assessmentId: $assessmentId) {
      name
      enabled
    }
  }
`;

// src/features/feature-flag/provider.tsx
import { jsx } from "react/jsx-runtime";
function FeatureFlagsProvider({
  children,
  assessmentId,
  fetchPolicy = "cache-first"
}) {
  const { data, loading } = useQuery(
    ENABLED_FLAGS_QUERY,
    {
      variables: { assessmentId },
      fetchPolicy
    }
  );
  const value = useMemo(() => {
    const names = (data?.enabledFlags ?? []).filter((flag) => flag.enabled).map((flag) => flag.name);
    return { enabled: new Set(names), loading };
  }, [data, loading]);
  return /* @__PURE__ */ jsx(FeatureFlagsContext.Provider, { value, children });
}

// src/features/feature-flag/use-feature-flag.ts
import { useContext } from "react";
function useFeatureFlag(name) {
  const { enabled } = useContext(FeatureFlagsContext);
  return enabled.has(name);
}
function useFeatureFlagState(name) {
  const { enabled, loading } = useContext(FeatureFlagsContext);
  return { enabled: enabled.has(name), loading };
}

// src/features/feature-flag/feature-flag.tsx
import { Fragment, jsx as jsx2 } from "react/jsx-runtime";
function FeatureFlag({ name, children, fallback = null }) {
  return useFeatureFlag(name) ? /* @__PURE__ */ jsx2(Fragment, { children }) : /* @__PURE__ */ jsx2(Fragment, { children: fallback });
}

// src/multiformat/question-preview/question-preview.tsx
import { Spacer as Spacer2, Text as Text11 } from "@vericus/cadmus-ui";
import clsx4 from "clsx";

// src/components/block-attachments/block-attachments.tsx
import { ArrowTopRightOnSquareIcon, Icon } from "@vericus/cadmus-icons";
import {
  Anchored,
  fileTypeToIconMap,
  getFileType,
  SectionLabel,
  Text
} from "@vericus/cadmus-ui";

// src/components/block-attachments/block-attachments.css.ts
var container = "v8wqkq0";

// src/components/block-attachments/block-attachments.tsx
import { jsx as jsx3, jsxs } from "react/jsx-runtime";
function BlockAttachments({ attachments }) {
  if (!attachments || attachments.length === 0) {
    return null;
  }
  return /* @__PURE__ */ jsxs("div", { className: container, children: [
    /* @__PURE__ */ jsx3(SectionLabel, { title: "Uploaded Files" }),
    /* @__PURE__ */ jsx3(
      "ul",
      {
        style: { listStyle: "none", padding: 0, margin: 0, marginTop: "16px" },
        children: attachments.map((attachment) => {
          const fileSize = attachment.file_size ? formatBytes(attachment.file_size) : null;
          return /* @__PURE__ */ jsx3(
            "li",
            {
              style: {
                display: "flex",
                alignItems: "center",
                marginBottom: "12px",
                gap: 12
              },
              children: /* @__PURE__ */ jsx3(Anchored, { href: attachment.media_url ?? "#", newTab: true, children: /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center", gap: 8 }, children: [
                /* @__PURE__ */ jsx3(
                  Icon,
                  {
                    iconName: fileTypeToIconMap[getFileType(attachment.name)]
                  }
                ),
                /* @__PURE__ */ jsx3(Text, { kind: "systemMd", children: attachment.name }),
                fileSize && /* @__PURE__ */ jsxs(Text, { kind: "systemSm", children: [
                  "(",
                  fileSize,
                  ")"
                ] }),
                /* @__PURE__ */ jsx3(ArrowTopRightOnSquareIcon, { size: 20, label: "" })
              ] }) })
            },
            attachment.media_id
          );
        })
      }
    )
  ] });
}
var formatBytes = (bytes) => {
  if (bytes === 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

// src/components/feedback-comments/feedback-comments.tsx
import { Fragment as Fragment2, useState } from "react";
import { EyeIcon } from "@vericus/cadmus-icons";
import { Button, LinkButton, Spacer, Text as Text2 } from "@vericus/cadmus-ui";
import clsx from "clsx";

// src/components/feedback-comments/comment-previewer.tsx
import { SlimEditorWrapper, SlimKit } from "@vericus/cadmus-editor-prosemirror";
import { generateHTML } from "@tiptap/html";

// src/components/feedback-comments/feedback-comments.css.ts
var commentBoxShadow = "_1dpawzi0";
var commentHeader = "_1dpawzi1";
var commentRow = "_1dpawzi2";
var commentText = "_1dpawzi5";
var previewer = "_1dpawzi3";
var webOnlyLinkButton = "_1dpawzi4";

// src/components/feedback-comments/comment-previewer.tsx
import { jsx as jsx4 } from "react/jsx-runtime";
function CommentPreviewer(props) {
  let html = props.doc;
  try {
    const doc = JSON.parse(props.doc);
    html = doc !== "{}" ? generateHTML(doc, [
      SlimKit.configure({
        editorId: props.id,
        editorA11yLabel: "Feedback Comment"
      })
    ]) : html;
  } catch (_e) {
    html = props.doc;
  }
  return /* @__PURE__ */ jsx4(SlimEditorWrapper, { className: previewer, children: html && /* @__PURE__ */ jsx4(
    "div",
    {
      className: "ProseMirror",
      contentEditable: "false",
      dangerouslySetInnerHTML: { __html: html }
    }
  ) });
}

// src/components/feedback-comments/feedback-comments.tsx
import { jsx as jsx5, jsxs as jsxs2 } from "react/jsx-runtime";
function FeedbackComments(props) {
  const [showFeedback, setShowFeedback] = useState(true);
  return /* @__PURE__ */ jsxs2("div", { className: clsx(showFeedback && commentBoxShadow), children: [
    /* @__PURE__ */ jsxs2("div", { className: commentHeader, children: [
      /* @__PURE__ */ jsx5(Text2, { kind: "headingSix", color: "neutralgrey500", children: "Comments" }),
      /* @__PURE__ */ jsxs2(
        LinkButton,
        {
          kind: "primary",
          iconName: showFeedback ? "UpCaret" : "DownCaret",
          onClick: () => setShowFeedback(!showFeedback),
          className: webOnlyLinkButton,
          children: [
            showFeedback ? "Hide" : "Show",
            " feedback"
          ]
        }
      )
    ] }),
    /* @__PURE__ */ jsxs2("div", { children: [
      showFeedback && props.comments && props.comments.map((comment) => /* @__PURE__ */ jsxs2(Fragment2, { children: [
        /* @__PURE__ */ jsxs2("div", { className: commentRow, children: [
          /* @__PURE__ */ jsx5(Text2, { kind: "bodyMd", bold: true, children: comment.feedback_section.title }),
          /* @__PURE__ */ jsx5(
            CommentPreviewer,
            {
              id: comment.id.toString(),
              doc: comment.content
            }
          )
        ] }, comment.id),
        /* @__PURE__ */ jsx5(Spacer, { spacing: 10 })
      ] }, comment.id)),
      props.comments && /* @__PURE__ */ jsx5(Spacer, { spacing: 16 })
    ] })
  ] });
}
function FeedbackViewerButton(props) {
  return /* @__PURE__ */ jsxs2("div", { className: commentHeader, children: [
    /* @__PURE__ */ jsxs2(
      Text2,
      {
        kind: "headingSix",
        color: "neutralgrey500",
        className: commentText,
        children: [
          props.commentCount,
          " Comment",
          props.commentCount === 1 ? "" : "s"
        ]
      }
    ),
    /* @__PURE__ */ jsx5(
      Button,
      {
        kind: "secondary",
        icon: /* @__PURE__ */ jsx5(EyeIcon, { label: "View Detailed Feedback", size: 24 }),
        onClick: props.onExpandFeedback,
        children: "View Detailed Feedback"
      }
    )
  ] });
}

// src/multiformat/question-preview/editor-previewer.tsx
import { useMemo as useMemo2 } from "react";
import {
  CadmusEditorContent,
  CadmusEditorWrapper
} from "@vericus/cadmus-editor-prosemirror";
import { generateHTML as generateHTML2 } from "@tiptap/html";
import clsx2 from "clsx";

// src/multiformat/question-preview/editor-previewer.css.ts
var root = "_1dd3fzq0";

// src/multiformat/question-preview/use-task-block-preview-editor.ts
import {
  createCadmusEditor,
  useCadmusEditor
} from "@vericus/cadmus-editor-prosemirror";
var useTaskBlockPreviewEditor = (props) => useCadmusEditor(
  {
    editorId: props.id,
    content: deserialisePMDoc(props.promptDoc),
    editable: false,
    // Read-only surface, no paste risk: always render saved font marks.
    enableFontControls: true,
    enableBlanks: props.enableBlanks,
    enableFootnoting: props.enableFootnoting
  },
  [props.promptDoc, props.id, props.enableBlanks, props.enableFootnoting]
);
function deserialisePMDoc(content4) {
  try {
    if (!content4) return null;
    if (typeof content4 === "object" && content4["type"] == "doc") return content4;
    if (typeof content4 === "string") return JSON.parse(content4);
    return null;
  } catch (_e) {
    return null;
  }
}
function docContainsNodeType(content4, types) {
  const doc = deserialisePMDoc(content4);
  if (!doc) return false;
  const wanted = new Set(types);
  const visit = (node) => node.type != null && wanted.has(node.type) || (node.content?.some(visit) ?? false);
  return visit(doc);
}

// src/multiformat/question-preview/editor-previewer.tsx
import { jsx as jsx6 } from "react/jsx-runtime";
var EditorPreviewer = (props) => {
  if (props.extensions) {
    return /* @__PURE__ */ jsx6(HTMLPreviewer, { ...props });
  } else if (props.promptEditor) {
    return /* @__PURE__ */ jsx6("div", { className: clsx2(root, props.className), children: /* @__PURE__ */ jsx6(
      CadmusEditorContent,
      {
        editor: props.promptEditor,
        fullWidth: props.fullWidth
      }
    ) });
  }
  return /* @__PURE__ */ jsx6(DefaultPreviewer, { ...props });
};
var DefaultPreviewer = ({
  id,
  promptDoc,
  fullWidth,
  enableBlanks,
  enableFootnoting,
  className
}) => {
  const editor = useTaskBlockPreviewEditor({
    id,
    promptDoc,
    enableBlanks,
    enableFootnoting
  });
  return /* @__PURE__ */ jsx6("div", { className: clsx2(root, className), children: /* @__PURE__ */ jsx6(
    CadmusEditorContent,
    {
      editor,
      pageMode: false,
      fullWidth
    }
  ) });
};
var HTMLPreviewer = ({
  promptDoc,
  extensions,
  fullWidth = false
}) => {
  const html = useMemo2(() => {
    const doc = deserialisePMDoc(promptDoc);
    return doc && extensions ? generateHTML2(doc, extensions) : null;
  }, [promptDoc, extensions]);
  return /* @__PURE__ */ jsx6(
    CadmusEditorWrapper,
    {
      style: { width: "100%" },
      "data-page-mode": "false",
      fullWidth,
      children: html && /* @__PURE__ */ jsx6(
        "div",
        {
          className: "ProseMirror",
          contentEditable: "false",
          dangerouslySetInnerHTML: { __html: html }
        }
      )
    }
  );
};

// src/multiformat/question-preview/feedback/feedback.tsx
import { Text as Text3 } from "@vericus/cadmus-ui";

// src/multiformat/question-preview/feedback/feedback.css.ts
var feedback = "_1xi5cou1 _1xi5cou0";

// src/multiformat/question-preview/feedback/feedback.tsx
import { jsx as jsx7 } from "react/jsx-runtime";
var Feedback = ({ feedback: feedback2 }) => /* @__PURE__ */ jsx7("div", { className: feedback, children: /* @__PURE__ */ jsx7(Text3, { kind: "bodySm", color: "navy700", children: feedback2 }) });

// src/multiformat/question-preview/blanks-response-preview/blanks-response-preview.tsx
import { CheckRoundIcon, CloseRoundIcon } from "@vericus/cadmus-icons";
import { colors, Text as Text6 } from "@vericus/cadmus-ui";

// src/multiformat/option-list/item.tsx
import { forwardRef, useId } from "react";
import { Radio, Text as Text4, vars } from "@vericus/cadmus-ui";

// ../../node_modules/.pnpm/@vanilla-extract+private@1.0.9/node_modules/@vanilla-extract/private/dist/vanilla-extract-private.esm.js
function getVarName(variable) {
  var matches = variable.match(/^var\((.*)\)$/);
  if (matches) {
    return matches[1];
  }
  return variable;
}
function get(obj, path) {
  var result = obj;
  for (var key of path) {
    if (!(key in result)) {
      throw new Error("Path ".concat(path.join(" -> "), " does not exist in object"));
    }
    result = result[key];
  }
  return result;
}
function walkObject(obj, fn) {
  var path = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : [];
  var clone = {};
  for (var key in obj) {
    var _value = obj[key];
    var currentPath = [...path, key];
    if (typeof _value === "string" || typeof _value === "number" || _value == null) {
      clone[key] = fn(_value, currentPath);
    } else if (typeof _value === "object" && !Array.isArray(_value)) {
      clone[key] = walkObject(_value, fn, currentPath);
    } else {
      console.warn('Skipping invalid key "'.concat(currentPath.join("."), '". Should be a string, number, null or object. Received: "').concat(Array.isArray(_value) ? "Array" : typeof _value, '"'));
    }
  }
  return clone;
}

// ../../node_modules/.pnpm/@vanilla-extract+dynamic@2.1.5/node_modules/@vanilla-extract/dynamic/dist/vanilla-extract-dynamic.esm.js
function assignInlineVars(varsOrContract, tokens) {
  var styles = {};
  if (typeof tokens === "object") {
    var _contract = varsOrContract;
    walkObject(tokens, (value2, path) => {
      if (value2 == null) {
        return;
      }
      var varName2 = get(_contract, path);
      styles[getVarName(varName2)] = String(value2);
    });
  } else {
    var _vars = varsOrContract;
    for (var varName in _vars) {
      var value = _vars[varName];
      if (value == null) {
        continue;
      }
      styles[getVarName(varName)] = value;
    }
  }
  Object.defineProperty(styles, "toString", {
    value: function value2() {
      return Object.keys(this).map((key) => "".concat(key, ":").concat(this[key])).join(";");
    },
    writable: false
  });
  return styles;
}

// src/multiformat/option-list/item.tsx
import clsx3 from "clsx";

// src/multiformat/option-list/option-list.css.ts
import { createRuntimeFn as _7a468 } from "@vanilla-extract/recipes/createRuntimeFn";
var bottomAction = "_1yyoazzn _1yyoazz3";
var icon = "_1yyoazzk";
var itemBgColor = "var(--_1yyoazz0)";
var option = { grey300: "_1yyoazze _1yyoazzd", sgrey100: "_1yyoazzf _1yyoazzd", functionalSuccess: "_1yyoazzg _1yyoazzd", primary: "_1yyoazzh _1yyoazzd", functionalFail: "_1yyoazzi _1yyoazzd" };
var optionColumn = _7a468({ defaultClassName: "_1yyoazz7 _1yyoazz2", variantClassNames: { label: { true: "_1yyoazz4 _1yyoazz3" }, background: { true: "_1yyoazz5 _1yyoazz6" }, hover: { true: "_1yyoazza" } }, defaultVariants: {}, compoundVariants: [] });
var optionItemBg = "_1yyoazz6";
var optionText = "_1yyoazzj";
var optionsGrid = "_1yyoazz1";
var radioInput = "_1yyoazzc";
var rightActionColumn = { "default": "_1yyoazzl", withBg: "_1yyoazzm" };
var root2 = "_1yyoazzb";

// src/multiformat/option-list/item.tsx
import { Fragment as Fragment3, jsx as jsx8, jsxs as jsxs3 } from "react/jsx-runtime";
var Item = forwardRef(
  (props, ref) => {
    const assignBgColor = props.background ? assignInlineVars({
      [itemBgColor]: vars.colors[props.background]
    }) : void 0;
    const id = useId();
    return /* @__PURE__ */ jsxs3(Fragment3, { children: [
      /* @__PURE__ */ jsxs3(
        "div",
        {
          className: optionColumn({
            label: props.withLabel,
            background: props.background !== void 0,
            hover: props.onClick !== void 0
          }),
          "data-option": true,
          style: assignBgColor,
          onClick: props.onClick,
          ref,
          ...props.dataAttributes,
          children: [
            props.leftAction && /* @__PURE__ */ jsx8("div", { children: props.leftAction }),
            /* @__PURE__ */ jsxs3("div", { className: option[props.colorVariant ?? "grey300"], children: [
              props.showToggle && /* @__PURE__ */ jsx8(
                Radio,
                {
                  id,
                  tabIndex: -1,
                  className: radioInput,
                  colorVariant: props.colorVariant,
                  contentEditable: false,
                  readOnly: true,
                  checked: props.selected
                }
              ),
              props.children ? props.children : /* @__PURE__ */ jsx8(
                Text4,
                {
                  as: "label",
                  htmlFor: id,
                  kind: "bodyMd",
                  color: "navy700",
                  className: optionText,
                  children: props.text
                }
              )
            ] }),
            props.icon && /* @__PURE__ */ jsx8("div", { className: icon, children: props.icon })
          ]
        }
      ),
      props.rightAction ? /* @__PURE__ */ jsx8(
        "div",
        {
          "data-testid": "option-feedback",
          className: clsx3({
            [rightActionColumn["default"]]: props.background === void 0,
            [rightActionColumn["withBg"]]: props.background !== void 0,
            [optionItemBg]: props.background !== void 0
          }),
          style: assignBgColor,
          children: props.rightAction
        }
      ) : /* @__PURE__ */ jsx8("div", { className: rightActionColumn.default }),
      props.bottomAction && /* @__PURE__ */ jsx8("div", { className: clsx3({ [bottomAction]: props.withLabel }), children: props.bottomAction })
    ] });
  }
);

// src/multiformat/option-list/list.tsx
import { jsx as jsx9, jsxs as jsxs4 } from "react/jsx-runtime";
var List = (props) => /* @__PURE__ */ jsxs4("div", { className: root2, children: [
  props.topAction,
  /* @__PURE__ */ jsx9("div", { className: optionsGrid, children: props.children })
] });

// src/multiformat/option-list/index.ts
var OptionList = {
  Item,
  List
};

// src/multiformat/question-preview/accepted-phrases-preview/accepted-phrases-preview.tsx
import { Text as Text5 } from "@vericus/cadmus-ui";

// src/multiformat/question-preview/accepted-phrases-preview/accepted-phrases-preview.css.ts
var phrase = "ul5goc1";
var root3 = "ul5goc0";

// src/multiformat/question-preview/accepted-phrases-preview/accepted-phrases-preview.tsx
import { jsx as jsx10 } from "react/jsx-runtime";
var AcceptedPhrasesPreview = ({ phrases }) => /* @__PURE__ */ jsx10("div", { className: root3, "data-testid": "accepted-phrases", children: phrases.map((phrase2) => /* @__PURE__ */ jsx10("div", { className: phrase, children: /* @__PURE__ */ jsx10(Text5, { kind: "bodyMd", color: "functionalSuccess", children: phrase2 }) }, phrase2)) });

// src/multiformat/question-preview/utils.ts
function formatPoints(points2, isAwarded = false, maxPoints = 0) {
  const denominator = maxPoints > 0 && maxPoints > points2 ? ` / ${maxPoints}` : "";
  const suffix = points2 > 1 || points2 === 0 || points2 === 0 || points2 <= -2 ? "points" : "point";
  return `${formatPointsSimple(points2, isAwarded)}${denominator} ${suffix}`;
}
function formatPointsSimple(points2, isAwarded = false) {
  const truncatedPoints = Number.parseFloat(points2.toFixed(2));
  const prefix = points2 > 0 && isAwarded ? "+" : "";
  return `${prefix}${truncatedPoints}`;
}
function headerFormatPoints(points2, maxPoints = 0) {
  const truncatedPoints = Number.parseFloat(points2.toFixed(2));
  const denominator = maxPoints > 0 && maxPoints >= points2 ? `/${maxPoints}` : "";
  return `${truncatedPoints}${denominator}`;
}
function formatNumberString(num) {
  const parsed = parseFloat(num);
  return isNaN(parsed) ? num : parsed.toString();
}
function formatNumber(num) {
  return num.toString();
}

// src/multiformat/question-preview/blanks-response-preview/blanks-response-preview.css.ts
var acceptedPhrases = "_1febc1n4";
var awardedPoints = "_1febc1n5";
var blankLabel = "_1febc1n3";
var blankRoot = "_1febc1n1";
var labelWrapper = "_1febc1n2";
var root4 = "_1febc1n0";

// src/multiformat/question-preview/blanks-response-preview/blanks-response-preview.tsx
import { jsx as jsx11, jsxs as jsxs5 } from "react/jsx-runtime";
function BlanksResponsePreview(props) {
  const { response: response2, mode } = props;
  if (mode === "response")
    return /* @__PURE__ */ jsx11("div", { className: root4, children: response2.blanks.map((blank, index) => /* @__PURE__ */ jsxs5("div", { className: blankRoot, children: [
      /* @__PURE__ */ jsx11("div", { className: labelWrapper, children: /* @__PURE__ */ jsxs5(
        Text6,
        {
          className: blankLabel,
          kind: "bodyMd",
          color: "neutralBlack300",
          children: [
            `${index + 1}.`,
            " "
          ]
        }
      ) }),
      /* @__PURE__ */ jsx11(AcceptedPhrasesPreview, { phrases: blank.matchPhrases ?? [] })
    ] }, blank.id)) });
  let datas = [];
  switch (mode) {
    case "question":
      if (response2.blanks) {
        datas = response2.blanks.map((blank) => ({
          id: blank.id,
          text: "Add your answer",
          withLabel: true
        }));
      }
      break;
    case "answer":
      datas = response2.blanks.map((blank) => ({
        id: blank.id,
        text: getBlankAnswerPhrase(
          response2.answerPhrases,
          blank,
          "Add your answer"
        ),
        withLabel: true
      }));
      break;
    case "marked":
    case "feedback":
      datas = response2.blanks.map((blank) => {
        let targetAnswer;
        if (response2.answerPhrases) {
          const answerIndex = response2.answerPhrases.findIndex(
            (answerPhrase) => answerPhrase.blankId === blank.id
          );
          targetAnswer = response2.answerPhrases[answerIndex];
        }
        let colorVariant = "grey300";
        let icon3 = void 0;
        if (targetAnswer?.markedCorrect === true) {
          icon3 = /* @__PURE__ */ jsx11(
            CheckRoundIcon,
            {
              label: "Marked correct",
              size: 16,
              color: colors.success
            }
          );
          colorVariant = "functionalSuccess";
        } else if (targetAnswer?.markedCorrect === false) {
          icon3 = /* @__PURE__ */ jsx11(CloseRoundIcon, { label: "Marked incorrect", size: 16 });
          colorVariant = "functionalFail";
        }
        const bottomAction2 = targetAnswer?.markedCorrect === false && blank.matchPhrases && /* @__PURE__ */ jsxs5(Text6, { kind: "bodySm", color: "neutralgrey500", children: [
          "Accepted Answer:",
          " ",
          /* @__PURE__ */ jsx11("span", { className: acceptedPhrases, children: blank.matchPhrases.join(" | ") })
        ] });
        const rightAction = targetAnswer?.markedPoints !== void 0 && mode === "marked" && /* @__PURE__ */ jsx11("div", { className: awardedPoints, children: /* @__PURE__ */ jsx11(Text6, { kind: "systemMd", color: "navy500", children: formatPoints(targetAnswer.markedPoints, true) }) });
        return {
          id: blank.id,
          text: targetAnswer?.phrase ?? "Add your answer",
          colorVariant,
          bottomAction: bottomAction2,
          icon: icon3,
          rightAction,
          withLabel: true
        };
      });
      break;
  }
  return /* @__PURE__ */ jsx11(OptionList.List, { children: datas.map((data) => {
    const { text, ...rest } = data;
    return /* @__PURE__ */ jsx11(OptionList.Item, { ...rest, children: /* @__PURE__ */ jsx11(Text6, { kind: "bodyMd", color: "navy700", children: text }) }, data.id);
  }) });
}
function getBlankAnswerPhrase(answerPhrases, blank, placeHolder) {
  if (!answerPhrases) return placeHolder;
  const answerIndex = answerPhrases.findIndex(
    (answerPhrase) => answerPhrase.blankId === blank.id
  );
  if (answerIndex === -1) return placeHolder;
  if (answerPhrases[answerIndex]?.phrase === "") return placeHolder;
  return answerPhrases[answerIndex]?.phrase ?? placeHolder;
}

// src/multiformat/question-preview/option-list-preview/option-list-preview.tsx
import {
  CheckRoundIcon as CheckRoundIcon2,
  CloseRoundIcon as CloseRoundIcon2,
  ErrorIcon
} from "@vericus/cadmus-icons";
import { colors as colors2, Text as Text7 } from "@vericus/cadmus-ui";

// src/multiformat/question-preview/option-list-preview/option-list-preview.css.ts
var callout = "_1b8q0512";
var correctAnswerFeedback = "_1b8q0511";
var pointsFeedback = "_1b8q0510";

// src/multiformat/question-preview/option-list-preview/utils.ts
function getChoiceState(choice, answerIds, mode, passed, partialScoring) {
  if (mode === "response") {
    if (choice.isCorrect === true) {
      return 1 /* SelectedByTeacher */;
    }
  }
  if (mode === "answer") {
    if (answerIds.includes(choice.id)) {
      return 2 /* SelectedByStudent */;
    }
  }
  if (mode === "marked" || mode === "feedback") {
    if (choice.isCorrect === true) {
      if (answerIds.includes(choice.id)) {
        if (partialScoring === false && passed === false) {
          return 6 /* NotPassedAndIsCorrect */;
        }
        return 3 /* MarkedCorrect */;
      } else {
        return 5 /* NotSelectedAndIsCorrect */;
      }
    } else if (choice.isCorrect === false) {
      if (answerIds.includes(choice.id)) {
        return 4 /* MarkedIncorrect */;
      } else {
        return 0 /* Unselected */;
      }
    }
  }
  return 0 /* Unselected */;
}
function isChecked(state) {
  if (state === 1 /* SelectedByTeacher */) return true;
  if (state === 2 /* SelectedByStudent */) return true;
  if (state === 3 /* MarkedCorrect */) return true;
  if (state === 4 /* MarkedIncorrect */) return true;
  if (state === 6 /* NotPassedAndIsCorrect */) return true;
  return false;
}
function getBackground(state) {
  if (state === 1 /* SelectedByTeacher */) return "functionalSuccess";
  if (state === 2 /* SelectedByStudent */) return "primary";
  if (state === 3 /* MarkedCorrect */) return "functionalSuccess";
  if (state === 4 /* MarkedIncorrect */) return "functionalFail";
  if (state === 5 /* NotSelectedAndIsCorrect */) return "functionalSuccess";
  if (state === 6 /* NotPassedAndIsCorrect */) return "functionalSuccess";
  return "grey300";
}

// src/multiformat/question-preview/option-list-preview/option-list-preview.tsx
import { jsx as jsx12, jsxs as jsxs6 } from "react/jsx-runtime";
var OptionListPreview = ({
  mode,
  response: { choices, answerIds, partialScoring },
  passed
}) => {
  const datas = choices.map((choice) => {
    const state = getChoiceState(
      choice,
      answerIds ?? [],
      mode,
      passed,
      partialScoring
    );
    const optionColor = getBackground(state);
    const checked = isChecked(state);
    let icon3 = void 0;
    if (state === 3 /* MarkedCorrect */ || state === 6 /* NotPassedAndIsCorrect */) {
      icon3 = /* @__PURE__ */ jsx12(
        CheckRoundIcon2,
        {
          color: colors2.success,
          label: "Marked correct",
          size: 16
        }
      );
    } else if (state === 4 /* MarkedIncorrect */) {
      icon3 = /* @__PURE__ */ jsx12(CloseRoundIcon2, { label: "Marked incorrect", size: 16 });
    }
    const feedback2 = getFeedback(state, choice);
    return {
      id: choice.id,
      text: choice.text,
      colorVariant: optionColor,
      selected: checked,
      rightAction: mode !== "feedback" ? feedback2 : void 0,
      showToggle: true,
      icon: icon3,
      dataAttributes: {
        "data-testid": "option-list-content",
        "data-answered-correctly": state === 3 /* MarkedCorrect */ ? true : state === 4 /* MarkedIncorrect */ ? false : void 0,
        "data-correct-answer": choice.isCorrect !== null ? choice.isCorrect : void 0
      }
    };
  });
  const partialScoringCallout = partialScoring === false && (mode === "marked" || mode === "feedback") && passed === false && /* @__PURE__ */ jsxs6("div", { className: callout, children: [
    /* @__PURE__ */ jsx12(ErrorIcon, { label: "", size: 16 }),
    /* @__PURE__ */ jsx12(
      Text7,
      {
        kind: "bodySm",
        color: "functionalFail",
        "aria-live": "assertive",
        as: "span",
        children: "Note: All correct answers are required."
      }
    )
  ] });
  return /* @__PURE__ */ jsx12(OptionList.List, { topAction: partialScoringCallout, children: datas.map((data) => /* @__PURE__ */ jsx12(OptionList.Item, { ...data }, data.id)) });
};
function getFeedback(state, choice) {
  if (state === 6 /* NotPassedAndIsCorrect */) {
    return /* @__PURE__ */ jsx12(Text7, { kind: "systemMd", className: pointsFeedback, children: formatPoints(0, true) });
  } else if (state === 3 /* MarkedCorrect */ && choice.markedPoints !== void 0) {
    return /* @__PURE__ */ jsx12(Text7, { kind: "systemMd", className: pointsFeedback, children: formatPoints(choice.markedPoints, true) });
  }
  if (state === 4 /* MarkedIncorrect */ && choice.markedPoints !== void 0) {
    return /* @__PURE__ */ jsx12(Text7, { kind: "systemMd", className: pointsFeedback, children: formatPoints(0, true) });
  }
  if (state === 5 /* NotSelectedAndIsCorrect */) {
    return /* @__PURE__ */ jsx12(
      Text7,
      {
        kind: "bodySm",
        color: "navy500",
        className: correctAnswerFeedback,
        as: "span",
        children: "Correct answer"
      }
    );
  }
  return null;
}

// src/multiformat/question-preview/boolean-response-preview/boolean-response-preview.tsx
import { jsx as jsx13 } from "react/jsx-runtime";
var TrueChoiceId = "true";
var FalseChoiceId = "false";
var BooleanResponsePreview = ({
  mode,
  response: { isTrue, answerTrue }
}) => {
  const choices = [
    { id: TrueChoiceId, text: "True", isCorrect: isTrue },
    {
      id: FalseChoiceId,
      text: "False",
      isCorrect: typeof isTrue === "boolean" ? !isTrue : void 0
    }
  ];
  const answerIds = typeof answerTrue === "boolean" ? [answerTrue ? TrueChoiceId : FalseChoiceId] : void 0;
  return /* @__PURE__ */ jsx13(OptionListPreview, { mode, response: { choices, answerIds } });
};

// src/multiformat/question-preview/editor-response-preview/editor-response-preview.tsx
import { EditorContentSkeleton, Text as Text8 } from "@vericus/cadmus-ui";

// src/multiformat/question-preview/editor-response-preview/editor-response-preview.css.ts
var answerWrapper = "zbuszd1";
var divider = "zbuszd2 zbuszd0";
var editorSkeletonWrapper = "zbuszd3";
var wordLimit = "zbuszd4";

// src/multiformat/question-preview/editor-response-preview/editor-response-preview.tsx
import { Fragment as Fragment4, jsx as jsx14, jsxs as jsxs7 } from "react/jsx-runtime";
var EditorResponsePreview = ({
  id,
  answerDoc,
  extensions,
  wordLimit: wordLimit3
}) => answerDoc ? /* @__PURE__ */ jsxs7("div", { className: answerWrapper, children: [
  wordLimit3 && /* @__PURE__ */ jsx14("div", { className: wordLimit, children: /* @__PURE__ */ jsxs7(Text8, { kind: "bodySm", bold: true, children: [
    "Word Limit: ",
    wordLimit3
  ] }) }),
  /* @__PURE__ */ jsx14("div", { className: divider }),
  /* @__PURE__ */ jsx14(EditorPreviewer, { id, promptDoc: answerDoc, extensions })
] }) : /* @__PURE__ */ jsxs7(Fragment4, { children: [
  wordLimit3 && /* @__PURE__ */ jsx14("div", { className: wordLimit, style: { marginBottom: 24 }, children: /* @__PURE__ */ jsxs7(Text8, { kind: "bodySm", bold: true, children: [
    "Word Limit: ",
    wordLimit3
  ] }) }),
  /* @__PURE__ */ jsx14("div", { className: editorSkeletonWrapper, children: /* @__PURE__ */ jsx14(EditorContentSkeleton, {}) })
] });

// src/multiformat/question-preview/multi-response-preview.tsx
import { jsx as jsx15 } from "react/jsx-runtime";
var MultiResponsePreview = (props) => /* @__PURE__ */ jsx15(OptionListPreview, { ...props });

// src/multiformat/question-preview/short-answer-preview/short-answer-preview.tsx
import { CheckRoundIcon as CheckRoundIcon3, CloseRoundIcon as CloseRoundIcon3 } from "@vericus/cadmus-icons";
import { colors as colors3, Text as Text9 } from "@vericus/cadmus-ui";

// src/multiformat/question-preview/short-answer-preview/short-answer-preview.css.ts
var acceptedPhrasesWrapper = "pubnzy2";
var inputWrapper = "pubnzy1";
var shortAnswerPreview = "pubnzy0";

// src/multiformat/question-preview/short-answer-preview/short-answer-preview.tsx
import { jsx as jsx16, jsxs as jsxs8 } from "react/jsx-runtime";
var ShortAnswerPreview = ({
  answerShort,
  mode,
  markedCorrect,
  matchPhrases
}) => {
  if (mode === "answer" || mode === "question") {
    let text = "Write your answer here\u2026";
    if (mode === "answer" && answerShort) text = answerShort;
    return /* @__PURE__ */ jsx16("div", { className: shortAnswerPreview, children: /* @__PURE__ */ jsx16("div", { className: inputWrapper, children: /* @__PURE__ */ jsx16(
      Text9,
      {
        kind: "bodyMd",
        color: mode === "answer" ? "neutralBlack300" : "grey400",
        children: text
      }
    ) }) });
  }
  if (mode === "response")
    return /* @__PURE__ */ jsxs8("div", { className: shortAnswerPreview, children: [
      /* @__PURE__ */ jsx16(Text9, { kind: "headingSix", children: "Accepted answers" }),
      /* @__PURE__ */ jsx16("div", { className: acceptedPhrasesWrapper, children: /* @__PURE__ */ jsx16(AcceptedPhrasesPreview, { phrases: matchPhrases ?? [] }) })
    ] });
  let colorVariant = "grey300";
  const markedMode = mode === "marked" || mode === "feedback";
  if (markedMode && markedCorrect) colorVariant = "functionalSuccess";
  else if (markedMode && !markedCorrect) colorVariant = "functionalFail";
  const bottomAction2 = markedMode && matchPhrases && markedCorrect === false && /* @__PURE__ */ jsxs8("div", { children: [
    /* @__PURE__ */ jsxs8(Text9, { kind: "bodySm", color: "neutralgrey500", as: "span", children: [
      "Accepted Answers:",
      " "
    ] }),
    /* @__PURE__ */ jsx16(Text9, { kind: "bodySm", color: "neutralBlack500", as: "span", children: matchPhrases.join(" | ") })
  ] });
  let icon3 = void 0;
  if (markedMode && markedCorrect) {
    icon3 = /* @__PURE__ */ jsx16(CheckRoundIcon3, { color: colors3.success, label: "Marked correct", size: 16 });
  } else if (markedMode && !markedCorrect) {
    icon3 = /* @__PURE__ */ jsx16(CloseRoundIcon3, { label: "Marked incorrect", size: 16 });
  }
  const data = {
    id: "short-response",
    colorVariant,
    bottomAction: bottomAction2,
    icon: icon3
  };
  return /* @__PURE__ */ jsx16(OptionList.List, { children: /* @__PURE__ */ jsx16(OptionList.Item, { ...data, children: /* @__PURE__ */ jsx16(
    Text9,
    {
      kind: "bodyMd",
      color: "neutralBlack300",
      style: { display: "block" },
      children: answerShort ?? "Write your answer here\u2026"
    }
  ) }) });
};

// src/multiformat/question-preview/question-body-preview.tsx
import { jsx as jsx17 } from "react/jsx-runtime";
function QuestionBodyPreview(props) {
  switch (props.response.type) {
    case "EditorResponse":
      return /* @__PURE__ */ jsx17(
        EditorResponsePreview,
        {
          id: props.id,
          ...props.response,
          extensions: props.extensions
        }
      );
    case "ShortResponse":
      return /* @__PURE__ */ jsx17(
        ShortAnswerPreview,
        {
          answerShort: props.response.answerShort,
          markedCorrect: props.response.markedCorrect,
          mode: props.mode,
          points: props.response.markedPoints,
          matchPhrases: props.response.matchPhrases
        }
      );
    case "MultiResponse":
      return /* @__PURE__ */ jsx17(
        MultiResponsePreview,
        {
          response: props.response,
          mode: props.mode,
          passed: props.passed
        }
      );
    case "BooleanResponse":
      return /* @__PURE__ */ jsx17(BooleanResponsePreview, { response: props.response, mode: props.mode });
    case "BlanksResponse":
      return /* @__PURE__ */ jsx17(BlanksResponsePreview, { mode: props.mode, response: props.response });
  }
}

// src/multiformat/question-preview/question-header-preview/question-header-preview.tsx
import {
  BookMarkFilledIcon,
  BookMarkIcon,
  GridIcon
} from "@vericus/cadmus-icons";
import { Button as Button2, Text as Text10 } from "@vericus/cadmus-ui";

// src/multiformat/question-preview/question-header-preview/question-header-preview.css.ts
var button = "_1djnm215";
var points = { "default": "_1djnm212 _1djnm211", fail: "_1djnm213 _1djnm211" };
var rightSection = "_1djnm214";
var root5 = "_1djnm210";
var unmarkedLabel = "_1djnm216";

// src/multiformat/question-preview/question-header-preview/question-header-preview.tsx
import { jsx as jsx18, jsxs as jsxs9 } from "react/jsx-runtime";
var QuestionHeaderPreview = ({
  questionTitle,
  points: points2,
  bookmarked,
  markedPoints,
  isSubQuestion,
  automarked = false,
  manualMarked = false,
  showPoints = true,
  withMarking = false,
  withBookmark,
  onToggleBookmark,
  onRubricClick
}) => {
  const markableQuestion = points2 !== void 0;
  return /* @__PURE__ */ jsxs9("div", { className: root5, children: [
    /* @__PURE__ */ jsx18(Text10, { kind: "headingFour", as: isSubQuestion === true ? "h3" : "h2", children: questionTitle }),
    /* @__PURE__ */ jsxs9("div", { className: rightSection, children: [
      onRubricClick && /* @__PURE__ */ jsx18(
        Button2,
        {
          icon: /* @__PURE__ */ jsx18(GridIcon, { size: 24, label: "" }),
          onClick: onRubricClick,
          children: "View Rubric"
        }
      ),
      markableQuestion && (withMarking ? /* @__PURE__ */ jsx18(
        MarkingDetails,
        {
          points: points2,
          markedPoints,
          automarked,
          manualMarked
        }
      ) : showPoints && /* @__PURE__ */ jsx18(
        MaxScore,
        {
          points: points2,
          isSubQuestion: isSubQuestion ?? false
        }
      )),
      withBookmark && /* @__PURE__ */ jsx18(
        Bookmark,
        {
          bookmarked: bookmarked ?? false,
          onToggleBookmark
        }
      )
    ] })
  ] });
};
function MarkingDetails(props) {
  const { points: points2, markedPoints, automarked, manualMarked } = props;
  const isMarked = (automarked || manualMarked) && markedPoints !== void 0;
  if (isMarked) {
    return /* @__PURE__ */ jsx18("div", { children: /* @__PURE__ */ jsxs9(Text10, { kind: "systemMd", children: [
      "Awarded points",
      " ",
      /* @__PURE__ */ jsx18(Text10, { kind: "headingSix", as: "span", className: points.default, children: headerFormatPoints(markedPoints, points2) })
    ] }) });
  }
  return /* @__PURE__ */ jsx18("div", { className: unmarkedLabel, children: /* @__PURE__ */ jsxs9(Text10, { kind: "label", color: "navy500", children: [
    "to be marked out of ",
    points2
  ] }) });
}
function MaxScore(props) {
  return /* @__PURE__ */ jsx18("div", { className: points.default, children: /* @__PURE__ */ jsxs9(Text10, { kind: "systemMd", color: "navy500", children: [
    formatPoints(props.points),
    " ",
    props.isSubQuestion ? " total" : ""
  ] }) });
}
function Bookmark(props) {
  return /* @__PURE__ */ jsx18("button", { className: button, onClick: props.onToggleBookmark, children: props.bookmarked ? /* @__PURE__ */ jsx18(BookMarkFilledIcon, { label: "Marked", size: 16 }) : /* @__PURE__ */ jsx18(BookMarkIcon, { label: "Not Marked", size: 16 }) });
}

// src/multiformat/question-preview/question-preview.css.ts
var accordion = "_1ou25kc4";
var accordionTitle = "_1ou25kc5";
var content = "_1ou25kc1";
var feedbackContent = "_1ou25kc2";
var root6 = "_1ou25kc0";

// src/multiformat/question-preview/question-preview.tsx
import { Fragment as Fragment5, jsx as jsx19, jsxs as jsxs10 } from "react/jsx-runtime";
var QuestionPreview = ({
  questionId,
  promptDoc,
  questionTitle,
  points: points2,
  markedPoints,
  bookmarked = false,
  feedback: feedback2,
  response: response2,
  withHeader = true,
  withPromptDoc = true,
  promptEditor,
  isSubQuestion,
  passed,
  mode = "question",
  automarked = false,
  manualMarked = false,
  withBookmark = false,
  onUpdateBookmark,
  extensions,
  showAutomarkConditions = false,
  showFeedbackComments = false,
  feedbackComments,
  blockAttachments,
  attachmentEnabled,
  ...divProps
}) => {
  return /* @__PURE__ */ jsxs10(
    "div",
    {
      id: divProps.id,
      className: clsx4(root6, divProps.className),
      ...response2?.type ? { "data-response-type": response2.type } : {},
      children: [
        withHeader && /* @__PURE__ */ jsx19("div", { className: content, children: /* @__PURE__ */ jsx19(
          QuestionHeaderPreview,
          {
            questionTitle,
            bookmarked,
            points: points2,
            markedPoints,
            withMarking: response2 !== void 0 && mode === "marked",
            showPoints: mode !== "feedback",
            isSubQuestion,
            automarked,
            manualMarked,
            withBookmark,
            onToggleBookmark: () => {
              if (!onUpdateBookmark) return;
              onUpdateBookmark(!bookmarked);
            }
          }
        ) }),
        withPromptDoc && /* @__PURE__ */ jsxs10(Fragment5, { children: [
          /* @__PURE__ */ jsx19(Spacer2, { spacing: 8 }),
          /* @__PURE__ */ jsx19(
            EditorPreviewer,
            {
              id: questionId,
              promptDoc,
              promptEditor,
              enableBlanks: response2?.type === "BlanksResponse" || !response2?.type,
              extensions
            }
          )
        ] }),
        response2 && /* @__PURE__ */ jsxs10(Fragment5, { children: [
          /* @__PURE__ */ jsx19(Spacer2, { spacing: 24 }),
          /* @__PURE__ */ jsx19(
            QuestionBodyPreview,
            {
              id: questionId,
              response: response2,
              mode,
              passed,
              extensions,
              showAutomarkConditions
            }
          )
        ] }),
        (mode === "marked" || mode === "feedback") && feedback2 && /* @__PURE__ */ jsxs10(Fragment5, { children: [
          /* @__PURE__ */ jsx19(Spacer2, { spacing: 8 }),
          /* @__PURE__ */ jsx19("div", { className: content, children: feedback2 && /* @__PURE__ */ jsx19(Feedback, { feedback: feedback2 }) })
        ] }),
        ("ShortResponse" === response2?.type || "BlanksResponse" === response2?.type) && showAutomarkConditions && response2.matchSimilarity != void 0 && /* @__PURE__ */ jsx19(AutomarkInternals, { matchSimilarity: response2.matchSimilarity }),
        (response2?.type === "ShortResponse" || response2?.type === "EditorResponse") && attachmentEnabled && blockAttachments && /* @__PURE__ */ jsx19(BlockAttachments, { attachments: blockAttachments }),
        showFeedbackComments && feedbackComments && feedbackComments.length > 0 && /* @__PURE__ */ jsxs10(Fragment5, { children: [
          /* @__PURE__ */ jsx19(Spacer2, { spacing: 16 }),
          /* @__PURE__ */ jsx19("div", { className: content, children: /* @__PURE__ */ jsx19(FeedbackComments, { comments: feedbackComments }) })
        ] })
      ]
    }
  );
};
var AutomarkInternals = (props) => {
  return /* @__PURE__ */ jsxs10(Fragment5, { children: [
    /* @__PURE__ */ jsx19(Spacer2, { spacing: 36 }),
    /* @__PURE__ */ jsxs10("div", { style: { textAlign: "right" }, children: [
      /* @__PURE__ */ jsxs10(Text11, { kind: "bodySm", color: "neutralgrey500", as: "span", children: [
        "Automark Conditions:",
        " "
      ] }),
      /* @__PURE__ */ jsxs10(Text11, { kind: "bodySm", color: "neutralBlack500", as: "span", children: [
        Math.round(props.matchSimilarity * 100),
        "% Similarity"
      ] })
    ] })
  ] });
};

// src/multiformat/question-preview/shuffle.ts
import seedrandom from "seedrandom";
function shuffleWithSeed(arr, seed) {
  const rng = seedrandom(seed);
  return shuffleArray(arr, rng);
}
function shuffleArray(arr, randomiser = () => Math.random()) {
  const shuffled = arr.slice();
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(randomiser() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
function shuffleChoices(choices, seed) {
  if (seed === null || seed === void 0 || seed === "") return choices;
  return shuffleWithSeed(choices, seed);
}

// src/multiformat/question-preview/question-preview-next.tsx
import { Cog6ToothIcon } from "@vericus/cadmus-icons";
import { Pill as Pill3, Spacer as Spacer4, Text as Text29 } from "@vericus/cadmus-ui";
import clsx5 from "clsx";

// src/components/comment-box/comment-box.tsx
import { useMemo as useMemo3 } from "react";
import {
  generateHTML as generateHTML3,
  SlimKit as SlimKit2
} from "@vericus/cadmus-editor-prosemirror";
import { colors as colors4, Divider, Text as Text12 } from "@vericus/cadmus-ui";

// src/components/comment-box/comment-box.css.ts
var card = "_173b5c60";
var colorIndicator = "_173b5c63";
var header = "_173b5c61";
var titleSection = "_173b5c62";

// src/components/comment-box/comment-box.tsx
import { jsx as jsx20, jsxs as jsxs11 } from "react/jsx-runtime";
function CommentBox(props) {
  const { content: content4, criterion_id, index, feedback_section } = props;
  const { title: title2, color } = useMemo3(() => {
    if (!criterion_id)
      return {
        title: feedback_section.title || "Overall Comment",
        color: colors4.bgrey700
      };
    const criterion = props.criterion_outcomes?.find(
      (outcome) => outcome.criterion.id === criterion_id
    )?.criterion;
    return { title: criterion?.title, color: criterion?.color };
  }, [props.criterion_outcomes, criterion_id]);
  return /* @__PURE__ */ jsxs11("div", { className: card, children: [
    /* @__PURE__ */ jsxs11("div", { className: header, children: [
      /* @__PURE__ */ jsxs11("div", { className: titleSection, children: [
        /* @__PURE__ */ jsxs11(Text12, { kind: "bodyLine", children: [
          "[",
          index,
          "]"
        ] }),
        /* @__PURE__ */ jsx20(
          "div",
          {
            className: colorIndicator,
            style: { backgroundColor: color }
          }
        ),
        /* @__PURE__ */ jsx20(Text12, { kind: "headingSix", children: title2 })
      ] }),
      /* @__PURE__ */ jsx20(Text12, { kind: "bodySm", children: criterion_id ? title2 : "Unlinked" })
    ] }),
    /* @__PURE__ */ jsx20(Divider, {}),
    /* @__PURE__ */ jsx20(CommentView, { content: content4 })
  ] });
}
function CommentView(props) {
  const { content: content4 } = props;
  const html = useMemo3(() => parseCommentAsHTML(content4), [content4]);
  return /* @__PURE__ */ jsx20(
    "div",
    {
      className: "ProseMirror",
      contentEditable: "false",
      dangerouslySetInnerHTML: { __html: html }
    }
  );
}
function parseCommentAsHTML(content4) {
  try {
    const jsonContent = JSON.parse(content4);
    if (jsonContent && jsonContent["type"] === "doc") {
      const htmlContent = generateHTML3(jsonContent, [
        SlimKit2.configure({ editorId: "temp-parser" })
      ]);
      return htmlContent;
    }
  } catch (_e) {
    return content4;
  }
  return content4;
}

// src/components/marker-rubric.css.ts
var rubricContainer = "jkwu250";

// src/components/submission-outcome-summary/submission-outcome-summary.tsx
import { Text as Text13, vars as vars2 } from "@vericus/cadmus-ui";

// src/components/utils.ts
var formatDecimalNumber = (num) => num.toFixed(2).replace(/([0-9]+(\.[0-9]+[1-9])?)(\.?0+$)/, "$1");

// src/components/submission-outcome-summary/submission-outcome-summary.css.ts
var bandHeader = "fg8ql17";
var criterionColorVar = "var(--fg8ql10)";
var criterionLabel = "fg8ql12";
var criterionRow = { header: "fg8ql14 fg8ql13", criteria: "fg8ql15 fg8ql13" };
var criterionScore = { percentage: "fg8ql18 fg8ql16", points: "fg8ql19 fg8ql16", band: "fg8ql1a fg8ql16" };
var root7 = "fg8ql11";

// src/components/submission-outcome-summary/submission-outcome-summary.tsx
import { Fragment as Fragment6, jsx as jsx21, jsxs as jsxs12 } from "react/jsx-runtime";
function computeDisplayScore(score, finalScorePenalty) {
  return finalScorePenalty !== 0 && score != null ? score - finalScorePenalty : score;
}
function SubmissionOutcomeSummary(props) {
  const {
    markedScore,
    reportWeight,
    moderatorModifier = 0,
    finalScorePenalty = 0
  } = props;
  const hasMod = moderatorModifier !== 0;
  const hasPenalty = finalScorePenalty !== 0;
  const displayScore = computeDisplayScore(markedScore.score, finalScorePenalty);
  const hideScores = props.criterionOutcomes.some(
    (outcome) => outcome.hideScore === true
  );
  return /* @__PURE__ */ jsxs12("div", { className: root7, children: [
    /* @__PURE__ */ jsxs12("div", { className: criterionRow.header, children: [
      /* @__PURE__ */ jsx21(Text13, { kind: "label", children: "Criteria" }),
      /* @__PURE__ */ jsx21(Text13, { kind: "label", children: "Weighting" }),
      hideScores ? /* @__PURE__ */ jsx21(Text13, { kind: "label", textAlign: "center", className: bandHeader, children: "Band" }) : /* @__PURE__ */ jsxs12(Fragment6, { children: [
        /* @__PURE__ */ jsx21(Text13, { kind: "label", children: "Percentage" }),
        /* @__PURE__ */ jsx21(Text13, { kind: "label", children: "Points" })
      ] })
    ] }),
    props.criterionOutcomes.map((outcome) => /* @__PURE__ */ jsxs12("div", { className: criterionRow.criteria, children: [
      /* @__PURE__ */ jsx21(
        "div",
        {
          className: criterionLabel,
          style: assignInlineVars({
            [criterionColorVar]: outcome.color || ""
          }),
          children: /* @__PURE__ */ jsx21(Text13, { kind: "headingFive", children: outcome.label })
        }
      ),
      /* @__PURE__ */ jsx21("div", { style: { alignSelf: "center" }, children: /* @__PURE__ */ jsxs12(Text13, { kind: "bodyLine", children: [
        formatDecimalNumber(outcome.weightage),
        "%"
      ] }) }),
      outcome.hideScore === true ? /* @__PURE__ */ jsx21("div", { className: criterionScore.band, children: /* @__PURE__ */ jsx21(Text13, { kind: "bodyLine", color: "navy400", children: outcome.scaleLabel ?? "--" }) }) : /* @__PURE__ */ jsxs12(Fragment6, { children: [
        /* @__PURE__ */ jsx21("div", { className: criterionScore.percentage, children: /* @__PURE__ */ jsx21(Text13, { kind: "bodyLine", color: "navy400", children: outcome.percentage != null ? formatDecimalNumber(outcome.percentage) : "--" }) }),
        /* @__PURE__ */ jsxs12("div", { className: criterionScore.points, children: [
          /* @__PURE__ */ jsx21(Text13, { kind: "bodyLine", color: "navy400", children: outcome.score != null ? formatDecimalNumber(outcome.score) : "--" }),
          "/",
          /* @__PURE__ */ jsx21(Text13, { kind: "bodyLine", color: "navy400", children: formatDecimalNumber(outcome.maxScore) })
        ] })
      ] })
    ] }, outcome.id)),
    hasMod && /* @__PURE__ */ jsxs12("div", { className: criterionRow.criteria, children: [
      /* @__PURE__ */ jsx21("div", { style: { alignSelf: "center" }, children: /* @__PURE__ */ jsx21(Text13, { kind: "headingFive", children: "Adjusted points" }) }),
      /* @__PURE__ */ jsx21("div", {}),
      /* @__PURE__ */ jsx21("div", {}),
      /* @__PURE__ */ jsx21("div", { className: criterionScore.points, children: /* @__PURE__ */ jsxs12(Text13, { kind: "bodyLine", color: "navy400", children: [
        moderatorModifier > 0 ? "+" : "",
        formatDecimalNumber(moderatorModifier)
      ] }) })
    ] }),
    hasPenalty && /* @__PURE__ */ jsxs12("div", { className: criterionRow.criteria, children: [
      /* @__PURE__ */ jsx21("div", { style: { alignSelf: "center" }, children: /* @__PURE__ */ jsx21(Text13, { kind: "headingFive", children: "Penalties applied" }) }),
      /* @__PURE__ */ jsx21("div", {}),
      /* @__PURE__ */ jsx21("div", {}),
      /* @__PURE__ */ jsx21("div", { className: criterionScore.points, children: /* @__PURE__ */ jsxs12(Text13, { kind: "bodyLine", color: "navy400", children: [
        "-",
        formatDecimalNumber(finalScorePenalty)
      ] }) })
    ] }),
    /* @__PURE__ */ jsxs12("div", { className: criterionRow.criteria, children: [
      /* @__PURE__ */ jsxs12(
        "div",
        {
          className: criterionLabel,
          style: {
            alignSelf: "center",
            ...assignInlineVars({
              [criterionColorVar]: vars2.colors.bgrey700
            })
          },
          children: [
            /* @__PURE__ */ jsx21(Text13, { kind: "headingFour", children: "Total" }),
            reportWeight != null && /* @__PURE__ */ jsx21(Text13, { kind: "label", children: `${reportWeight}% of the overall grade` })
          ]
        }
      ),
      /* @__PURE__ */ jsx21("div", {}),
      /* @__PURE__ */ jsx21("div", { className: criterionScore.percentage, children: /* @__PURE__ */ jsx21(Text13, { kind: "bodyLine", color: "navy400", children: displayScore != null ? formatDecimalNumber(
        displayScore / markedScore.maxScore * 100
      ) : "--" }) }),
      /* @__PURE__ */ jsxs12("div", { className: criterionScore.points, children: [
        /* @__PURE__ */ jsx21(Text13, { kind: "bodyLine", color: "navy400", children: displayScore != null ? formatDecimalNumber(displayScore) : "--" }),
        "/",
        /* @__PURE__ */ jsx21(Text13, { kind: "bodyLine", color: "navy400", children: formatDecimalNumber(markedScore.maxScore) })
      ] })
    ] })
  ] });
}
function criterionOutcomesSummary(workOutcome) {
  const maxRubricPoints = workOutcome.submission_outcome?.criterion_outcomes.reduce(
    (acc, curr) => acc + curr.criterion.max_score,
    0
  ) ?? 0;
  const criterionOutcomes = workOutcome.submission_outcome?.criterion_outcomes?.toSorted(
    (a, b) => (a.criterion.order ?? 0) - (b.criterion.order ?? 0)
  ) ?? [];
  return criterionOutcomes.map((outcome) => {
    const {
      criterion,
      format: rubricFormat,
      score: markerScore
    } = outcome;
    const maxCriterionPoints = isPercentage(rubricFormat) ? criterion.max_score * maxRubricPoints / 100 : criterion.max_score;
    const weightage = isPercentage(rubricFormat) ? criterion.max_score : criterion.max_score / (maxRubricPoints || 1) * 100;
    const percentage = markerScore != null ? isPercentage(rubricFormat) ? markerScore : markerScore / maxCriterionPoints * 100 : null;
    const score = markerScore != null ? isPercentage(rubricFormat) ? markerScore * maxCriterionPoints / 100 : markerScore : null;
    return {
      id: criterion.id,
      label: criterion.title,
      weightage,
      score,
      maxScore: maxCriterionPoints,
      percentage,
      color: outcome.criterion.color,
      hideScore: outcome.hide_score,
      scaleLabel: outcome.scale_item?.scale_label
    };
  }) ?? [];
}
function isPercentage(format2) {
  return format2 === "percentage";
}

// src/components/marker-rubric.tsx
import { jsx as jsx22 } from "react/jsx-runtime";
function MarkerRubric(props) {
  const { citerionOutcomes, markedScore } = props;
  const outcome = structureCriterionOutcome(
    citerionOutcomes,
    markedScore.maxScore
  );
  return /* @__PURE__ */ jsx22("div", { className: rubricContainer, children: /* @__PURE__ */ jsx22(
    SubmissionOutcomeSummary,
    {
      criterionOutcomes: outcome,
      markedScore
    }
  ) });
}
function structureCriterionOutcome(criterionOutcome, maxScore) {
  return criterionOutcome.map((outcome) => {
    const {
      criterion,
      format: rubricFormat,
      score: markerScore
    } = outcome;
    const maxCriterionPoints = isPercentage2(rubricFormat) ? criterion.max_score * maxScore / 100 : criterion.max_score;
    const weightage = isPercentage2(rubricFormat) ? criterion.max_score : criterion.max_score / (maxScore || 1) * 100;
    const percentage = markerScore ? isPercentage2(rubricFormat) ? markerScore : markerScore / maxCriterionPoints * 100 : null;
    const score = markerScore ? isPercentage2(rubricFormat) ? markerScore * maxCriterionPoints / 100 : markerScore : null;
    return {
      id: criterion.id,
      label: criterion.title,
      weightage,
      score,
      maxScore: maxCriterionPoints,
      percentage,
      color: outcome.criterion.color,
      hideScore: outcome.hide_score,
      scaleLabel: outcome.scale_item?.scale_label
    };
  });
}
function isPercentage2(format2) {
  return format2 === "percentage";
}

// src/multiformat/utils.ts
function requiresManualMarking(questionType) {
  return ["extended", "video"].includes(questionType);
}
function formatSecondsAsTimer(seconds, padMinutes) {
  if (!Number.isFinite(seconds) || seconds < 0) {
    return padMinutes === true ? "00:00" : "0:00";
  }
  const wholeSeconds = Math.floor(seconds);
  const mins = Math.floor(wholeSeconds / 60);
  const secs = wholeSeconds % 60;
  return `${padMinutes === true ? mins.toString().padStart(2, "0") : mins}:${secs.toString().padStart(2, "0")}`;
}

// src/multiformat/question-preview/interaction-preview/interaction-preview.tsx
import { Text as Text28 } from "@vericus/cadmus-ui";

// src/multiformat/question-preview/interactions/hotspot-interaction-preview/hotspot-interaction-preview.tsx
import { Fragment as Fragment8 } from "react";
import { Alert, colors as colors9, Pill as Pill2, Spacer as Spacer3, Text as Text18 } from "@vericus/cadmus-ui";

// src/multiformat/question-preview/interaction-preview/choice-interaction-previews/inline-choice-interaction-preview.tsx
import {
  CheckRoundIcon as CheckRoundIcon4,
  ChevronDownIcon,
  CloseRoundIcon as CloseRoundIcon4
} from "@vericus/cadmus-icons";
import { colors as colors5, Pill, Text as Text15 } from "@vericus/cadmus-ui";

// src/multiformat/question-preview/inline-choice-list-preview/inline-choice-list-preview.tsx
import { Checkbox, Text as Text14 } from "@vericus/cadmus-ui";

// src/multiformat/question-preview/inline-choice-list-preview/inline-choice-list-preview.css.ts
var option2 = { "default": "xg63jo2 xg63jo1", selected: "xg63jo3 xg63jo1" };
var root8 = "xg63jo0";

// src/multiformat/question-preview/inline-choice-list-preview/inline-choice-list-preview.tsx
import { jsx as jsx23, jsxs as jsxs13 } from "react/jsx-runtime";
function InlineChoiceListPreview({ options, acceptedAnswerIds }) {
  const selectedOptions = options.filter(
    (option3) => acceptedAnswerIds.includes(option3.identifier)
  );
  const remainingOptions = options.filter(
    (option3) => !acceptedAnswerIds.includes(option3.identifier)
  );
  const orderedOptions = [...selectedOptions, ...remainingOptions];
  return /* @__PURE__ */ jsx23("div", { className: root8, children: orderedOptions.map((option3) => {
    const checked = acceptedAnswerIds.includes(option3.identifier);
    return /* @__PURE__ */ jsxs13(
      "div",
      {
        className: checked ? option2.selected : option2.default,
        children: [
          /* @__PURE__ */ jsx23(
            Checkbox,
            {
              onChange: () => {
              },
              checked,
              disabled: true,
              colorVariant: "functionalSuccess",
              "data-testid": `choice-${option3.identifier}`
            }
          ),
          /* @__PURE__ */ jsx23(
            Text14,
            {
              kind: "bodyMd",
              color: checked ? "functionalSuccess" : "navy400",
              children: option3.content
            }
          )
        ]
      },
      option3.identifier
    );
  }) });
}

// src/multiformat/question-preview/interaction-preview/interactions.css.ts
var acceptedPhrases2 = "_1jzszswd";
var answerWrapper2 = "_1jzszsw2";
var awardedPoints2 = "_1jzszswe";
var blankLabel2 = "_1jzszswc";
var blankRoot2 = "_1jzszswa";
var callout2 = "_1jzszswh";
var choicesList = "_1jzszsw1";
var correctAnswerFeedback2 = "_1jzszswg";
var divider2 = "_1jzszsw6 _1jzszsw0";
var dropdownIcon = "_1jzszsw5";
var dropdownItem = "_1jzszsw3";
var editorSkeletonWrapper2 = "_1jzszsw7";
var labelWrapper2 = "_1jzszswb";
var leftActionPrompt = "_1jzszswi";
var matchingDropdownItem = "_1jzszsw4";
var matchingPrintAlert = "_1jzszswk";
var matchingPrintContainer = "_1jzszswj";
var matchingPrintLeft = "_1jzszswl";
var matchingPrintRight = "_1jzszswm";
var matchingPrintRow = "_1jzszswn";
var pointsFeedback2 = "_1jzszswf";
var root9 = "_1jzszsw9";
var wordLimit2 = "_1jzszsw8";

// src/multiformat/question-preview/interaction-preview/choice-interaction-previews/inline-choice-interaction-preview.tsx
import { Fragment as Fragment7, jsx as jsx24, jsxs as jsxs14 } from "react/jsx-runtime";
function InlineChoiceInteractionPreview(props) {
  const {
    mode,
    field,
    outcome,
    answerChoiceId,
    fieldLabel,
    printView,
    questionType,
    shuffleSeed
  } = props;
  const withLabel = fieldLabel !== null;
  const choices = field.interaction.kind === "choice_interaction" ? field.interaction.choices : [];
  const shuffledChoices = shuffleChoices(choices, shuffleSeed);
  if (mode === "response") {
    return /* @__PURE__ */ jsx24("div", { className: root9, children: /* @__PURE__ */ jsxs14("div", { className: blankRoot2, children: [
      /* @__PURE__ */ jsx24("div", { className: labelWrapper2, children: withLabel && /* @__PURE__ */ jsxs14(
        Text15,
        {
          className: blankLabel2,
          kind: "bodyMd",
          color: "neutralBlack300",
          children: [
            `${fieldLabel}.`,
            " "
          ]
        }
      ) }),
      /* @__PURE__ */ jsx24(
        InlineChoiceListPreview,
        {
          options: shuffledChoices,
          acceptedAnswerIds: field.response.correct_values
        }
      )
    ] }, field.identifier) });
  }
  let itemProps;
  switch (mode) {
    case "question":
      itemProps = {
        id: field.identifier,
        text: !printView ? "Select an answer" : "",
        withLabel
      };
      break;
    case "answer": {
      const selectedAnswer = shuffledChoices.find(
        (choice) => choice.identifier === answerChoiceId
      )?.content;
      itemProps = {
        id: field.identifier,
        text: selectedAnswer ?? "Select an answer",
        withLabel,
        dataAttributes: {
          "data-testid": `field-${field.identifier}`
        }
      };
      break;
    }
    case "marked":
    case "feedback": {
      const automarkScore = outcome?.automark_score ?? null;
      const markedCorrect = automarkScore !== null ? automarkScore > 0 : null;
      const markedChoiceId = getMarkedAnswer(outcome);
      const selectedChoiceContent = shuffledChoices.find(
        (choice) => choice.identifier === markedChoiceId
      )?.content;
      const correctChoiceIds = field.response.correct_values;
      const correctChoices = shuffledChoices.filter((choice) => correctChoiceIds.includes(choice.identifier)).map((choice) => choice.content);
      let colorVariant = "grey300";
      let icon3 = void 0;
      if (markedCorrect === true) {
        icon3 = /* @__PURE__ */ jsx24(CheckRoundIcon4, { label: "Marked correct", color: colors5.success });
        colorVariant = "functionalSuccess";
      } else if (markedCorrect === false) {
        icon3 = /* @__PURE__ */ jsx24(CloseRoundIcon4, { label: "Marked incorrect" });
        colorVariant = "functionalFail";
      }
      const rightAction = automarkScore !== null && mode === "marked" && /* @__PURE__ */ jsx24("div", { className: awardedPoints2, children: /* @__PURE__ */ jsx24(Text15, { kind: "systemMd", color: "navy500", children: formatPoints(automarkScore, true) }) });
      const bottomAction2 = markedCorrect === false && /* @__PURE__ */ jsx24("div", { children: /* @__PURE__ */ jsxs14(
        Text15,
        {
          kind: "bodySm",
          color: "neutralgrey500",
          "data-testid": "correct-answer-display",
          children: [
            "Correct Answer:",
            " ",
            /* @__PURE__ */ jsx24("span", { className: acceptedPhrases2, children: correctChoices.join(" | ") })
          ]
        }
      ) });
      itemProps = {
        id: field.identifier,
        text: selectedChoiceContent ?? void 0,
        colorVariant,
        icon: icon3,
        rightAction,
        withLabel,
        bottomAction: bottomAction2,
        dataAttributes: {
          "data-testid": `field-${field.identifier}`,
          "data-answered-correctly": markedCorrect
        }
      };
      break;
    }
  }
  return /* @__PURE__ */ jsxs14(Fragment7, { children: [
    /* @__PURE__ */ jsx24(OptionList.Item, { ...itemProps, children: /* @__PURE__ */ jsxs14("div", { className: dropdownItem, children: [
      /* @__PURE__ */ jsx24(Text15, { kind: "bodyMd", color: "navy700", children: itemProps.text }),
      /* @__PURE__ */ jsx24("div", { className: dropdownIcon, children: !printView && /* @__PURE__ */ jsx24(ChevronDownIcon, { label: "", size: 24 }) })
    ] }) }, field.identifier),
    printView && shuffledChoices && shuffledChoices.length > 0 && questionType === "blanks" && /* @__PURE__ */ jsxs14("div", { className: choicesList, children: [
      /* @__PURE__ */ jsx24(Text15, { kind: "bodyMd", color: "navy700", children: "Select from:" }),
      shuffledChoices.map((choice) => /* @__PURE__ */ jsx24(
        Pill,
        {
          color: colors5.bgrey300,
          basic: true,
          lowerCase: true,
          children: choice.content
        },
        choice.identifier
      ))
    ] })
  ] });
}
function getMarkedAnswer(outcome) {
  if (outcome?.answers && outcome.answers.length > 0 && outcome.answers[0]) {
    return outcome.answers[0];
  }
  if (outcome?.answer) {
    return outcome.answer;
  }
  return null;
}

// src/multiformat/question-preview/interaction-preview/text-interaction-previews/text-entry-interaction-preview.tsx
import { CheckRoundIcon as CheckRoundIcon5, CloseRoundIcon as CloseRoundIcon5 } from "@vericus/cadmus-icons";
import { colors as colors6, Text as Text16 } from "@vericus/cadmus-ui";
import { jsx as jsx25, jsxs as jsxs15 } from "react/jsx-runtime";
function TextEntryInteractionPreview(props) {
  const { field, mode, outcome, answer: answer2, fieldLabel, printView } = props;
  const withLabel = fieldLabel !== null;
  if (mode === "response") {
    return /* @__PURE__ */ jsx25("div", { className: root9, children: /* @__PURE__ */ jsxs15("div", { className: blankRoot2, children: [
      withLabel && /* @__PURE__ */ jsx25("div", { className: labelWrapper2, children: /* @__PURE__ */ jsxs15(
        Text16,
        {
          className: blankLabel2,
          kind: "bodyMd",
          color: "neutralBlack300",
          children: [
            `${fieldLabel}.`,
            " "
          ]
        }
      ) }),
      /* @__PURE__ */ jsx25(AcceptedPhrasesPreview, { phrases: field.response.correct_values })
    ] }, field.identifier) });
  }
  let itemProps;
  switch (mode) {
    case "question":
      itemProps = {
        id: field.identifier,
        text: !printView ? "Add your answer" : "",
        withLabel
      };
      break;
    case "answer": {
      itemProps = {
        id: field.identifier,
        text: answer2 ?? "Add your answer",
        withLabel,
        dataAttributes: {
          "data-testid": `field-${field.identifier}`
        }
      };
      break;
    }
    case "marked":
    case "feedback": {
      const automarkScore = outcome?.automark_score ?? null;
      const markedCorrect = automarkScore !== null ? automarkScore > 0 : null;
      const markedAnswer = getMarkedAnswer2(outcome);
      let colorVariant = "grey300";
      let icon3 = void 0;
      if (markedCorrect === true) {
        icon3 = /* @__PURE__ */ jsx25(
          CheckRoundIcon5,
          {
            color: colors6.success,
            label: "Marked correct",
            size: 16
          }
        );
        colorVariant = "functionalSuccess";
      } else if (markedCorrect === false) {
        icon3 = /* @__PURE__ */ jsx25(CloseRoundIcon5, { label: "Marked incorrect", size: 16 });
        colorVariant = "functionalFail";
      }
      const bottomAction2 = markedCorrect === false && /* @__PURE__ */ jsxs15(
        Text16,
        {
          kind: "bodySm",
          color: "neutralgrey500",
          "data-testid": "correct-answer-display",
          children: [
            "Accepted Answer:",
            " ",
            /* @__PURE__ */ jsx25("span", { className: acceptedPhrases2, children: field.response.correct_values.join(" | ") })
          ]
        }
      );
      const rightAction = automarkScore !== null && mode === "marked" && /* @__PURE__ */ jsx25("div", { className: awardedPoints2, children: /* @__PURE__ */ jsx25(Text16, { kind: "systemMd", color: "navy500", children: formatPoints(automarkScore, true) }) });
      itemProps = {
        id: field.identifier,
        text: markedAnswer ?? void 0,
        colorVariant,
        bottomAction: bottomAction2,
        icon: icon3,
        rightAction,
        withLabel,
        dataAttributes: {
          "data-testid": `field-${field.identifier}`,
          "data-answered-correctly": markedCorrect
        }
      };
      break;
    }
  }
  return /* @__PURE__ */ jsx25(OptionList.Item, { ...itemProps, children: /* @__PURE__ */ jsx25(Text16, { kind: "bodyMd", color: "navy700", children: itemProps.text }) }, field.identifier);
}
function getMarkedAnswer2(outcome) {
  if (outcome?.answers && outcome.answers.length > 0 && outcome.answers[0]) {
    return outcome.answers[0];
  }
  if (outcome?.answer) {
    return outcome.answer;
  }
  return null;
}

// src/multiformat/question-preview/interactions/hotspot-interaction-preview/hooks.ts
import { useMemo as useMemo4, useState as useState3 } from "react";

// src/hotspot/hooks.ts
import { useCallback, useEffect, useRef, useState as useState2 } from "react";
import { colors as colors7 } from "@vericus/cadmus-ui";

// src/hotspot/assets/check.svg
var check_default = "./check-DSVHKHFK.svg";

// src/hotspot/assets/cross.svg
var cross_default = "./cross-2RYINMXR.svg";

// src/hotspot/assets/pin.ts
var pin_default = `<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M10.8977 0.100098L10.8986 2.8559C12.4845 3.05641 13.9588 3.77879 15.0891 4.90926C16.2194 6.03973 16.9416 7.51408 17.1419 9.1001H19.8977V10.9001L17.1419 10.901C16.9414 12.4869 16.2191 13.961 15.0888 15.0913C13.9586 16.2216 12.4844 16.9438 10.8986 17.1443L10.8977 19.9001H9.09766V17.1443C7.51164 16.944 6.03729 16.2218 4.90682 15.0915C3.77634 13.9612 3.05397 12.487 2.85346 10.901L0.0976562 10.9001V9.1001H2.85346C3.05378 7.51394 3.77608 6.0395 4.90657 4.90901C6.03706 3.77852 7.5115 3.05622 9.09766 2.8559V0.100098H10.8977ZM9.99766 4.6001C8.56549 4.6001 7.19198 5.16902 6.17928 6.18172C5.16658 7.19442 4.59766 8.56793 4.59766 10.0001C4.59766 11.4323 5.16658 12.8058 6.17928 13.8185C7.19198 14.8312 8.56549 15.4001 9.99766 15.4001C11.4298 15.4001 12.8033 14.8312 13.816 13.8185C14.8287 12.8058 15.3977 11.4323 15.3977 10.0001C15.3977 8.56793 14.8287 7.19442 13.816 6.18172C12.8033 5.16902 11.4298 4.6001 9.99766 4.6001ZM9.99766 8.2001C10.475 8.2001 10.9329 8.38974 11.2704 8.72731C11.608 9.06487 11.7977 9.52271 11.7977 10.0001C11.7977 10.4775 11.608 10.9353 11.2704 11.2729C10.9329 11.6105 10.475 11.8001 9.99766 11.8001C9.52027 11.8001 9.06243 11.6105 8.72486 11.2729C8.3873 10.9353 8.19766 10.4775 8.19766 10.0001C8.19766 9.52271 8.3873 9.06487 8.72486 8.72731C9.06243 8.38974 9.52027 8.2001 9.99766 8.2001V8.2001Z" fill="#6355F6"/>
</svg>
`;

// src/hotspot/hooks.ts
var PIN_ICON_WIDTH = 20;
var PIN_ICON_HEIGHT = 20;
var LABEL_HEIGHT = 32;
var CANVAS_WIDTH_PADDING = 1;
var CANVAS_HEIGHT_PADDING = 2;
var useHotspotCanvas = (promptImage, drawCallback) => {
  const canvasRef = useRef(null);
  const wrapperRef = useRef(null);
  const [currentScale, setCurrentScale] = useState2(1);
  const [cachedImageCanvas, setCachedImageCanvas] = useState2(null);
  const [prevWrapperWidth, setPrevWrapperWidth] = useState2(null);
  const setupCanvas = useCallback(() => {
    if (canvasRef.current && promptImage?.url && promptImage.height && promptImage.width && wrapperRef.current) {
      const wrapperWidth = wrapperRef.current.offsetWidth - CANVAS_WIDTH_PADDING;
      const wrapperHeight = wrapperRef.current.offsetHeight - CANVAS_HEIGHT_PADDING;
      const imageAspectRatio = promptImage.width / promptImage.height;
      let canvasWidth, canvasHeight, scale;
      if (imageAspectRatio > 1) {
        canvasWidth = wrapperWidth;
        scale = canvasWidth / promptImage.width;
        canvasHeight = promptImage.height * scale;
      } else {
        canvasHeight = wrapperHeight;
        scale = canvasHeight / promptImage.height;
        canvasWidth = promptImage.width * scale;
      }
      setCurrentScale(scale);
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      const pixelRatio = window.devicePixelRatio || 1;
      canvas.width = canvasWidth * pixelRatio;
      canvas.height = canvasHeight * pixelRatio;
      canvas.style.width = `${canvasWidth}px`;
      canvas.style.height = `${canvasHeight}px`;
      ctx.scale(pixelRatio, pixelRatio);
      const borderRadius = 8;
      ctx.beginPath();
      ctx.moveTo(borderRadius, 0);
      ctx.lineTo(canvasWidth - borderRadius, 0);
      ctx.quadraticCurveTo(canvasWidth, 0, canvasWidth, borderRadius);
      ctx.lineTo(canvasWidth, canvasHeight - borderRadius);
      ctx.quadraticCurveTo(
        canvasWidth,
        canvasHeight,
        canvasWidth - borderRadius,
        canvasHeight
      );
      ctx.lineTo(borderRadius, canvasHeight);
      ctx.quadraticCurveTo(0, canvasHeight, 0, canvasHeight - borderRadius);
      ctx.lineTo(0, borderRadius);
      ctx.quadraticCurveTo(0, 0, borderRadius, 0);
      ctx.closePath();
      ctx.clip();
      if (cachedImageCanvas && cachedImageCanvas.url === promptImage.url) {
        ctx.drawImage(cachedImageCanvas.canvas, 0, 0, canvasWidth, canvasHeight);
        drawCallback(ctx, scale);
      } else {
        const offScreenCanvas = document.createElement("canvas");
        offScreenCanvas.width = promptImage.width;
        offScreenCanvas.height = promptImage.height;
        const offScreenCtx = offScreenCanvas.getContext("2d");
        const img = new Image();
        img.src = promptImage.url;
        ctx.fillStyle = colors7.bgrey400;
        ctx.fillRect(0, 0, canvasWidth, canvasHeight);
        ctx.fillStyle = colors7.primary;
        ctx.font = "1rem Open Sauce Two";
        ctx.textAlign = "center";
        ctx.fillText("Loading...", canvasWidth / 2, canvasHeight / 2);
        img.onload = () => {
          offScreenCtx.drawImage(img, 0, 0);
          if (promptImage.url) {
            setCachedImageCanvas({ canvas: offScreenCanvas, url: promptImage.url });
          }
          ctx.drawImage(offScreenCanvas, 0, 0, canvasWidth, canvasHeight);
          drawCallback(ctx, scale);
        };
      }
    }
  }, [canvasRef, wrapperRef, promptImage, drawCallback, cachedImageCanvas]);
  useEffect(() => {
    setupCanvas();
    const resizeObserver = new ResizeObserver(() => {
      const currentWidth = wrapperRef.current?.offsetWidth || 0;
      if (currentWidth !== prevWrapperWidth) {
        setPrevWrapperWidth(currentWidth);
        setupCanvas();
      }
    });
    if (wrapperRef.current) {
      resizeObserver.observe(wrapperRef.current);
    }
    return () => {
      resizeObserver.disconnect();
    };
  }, [wrapperRef, setupCanvas, prevWrapperWidth]);
  return { canvasRef, wrapperRef, currentScale };
};
var drawPolygon = (ctx, points2, label, isCorrect) => {
  let bgColor = "rgba(99, 85, 246, 0.2)";
  let borderColor = colors7.primary;
  if (isCorrect !== void 0) {
    bgColor = isCorrect ? "rgba(0, 168, 84, 0.05)" : "rgba(219, 0, 37, 0.2)";
    borderColor = isCorrect ? colors7.success : colors7.failure;
  }
  ctx.beginPath();
  points2.forEach(([x = 0, y = 0], index) => {
    if (index === 0) {
      ctx.moveTo(x, y);
    } else {
      ctx.lineTo(x, y);
    }
  });
  ctx.closePath();
  ctx.fillStyle = bgColor;
  ctx.fill();
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = 2;
  ctx.stroke();
  const centerX = points2.reduce((acc, [x = 0]) => acc + x, 0) / points2.length;
  const centerY = points2.reduce((acc, [, y = 0]) => acc + y, 0) / points2.length;
  const circleRadius = 12;
  ctx.beginPath();
  ctx.arc(centerX, centerY, circleRadius, 0, Math.PI * 2);
  ctx.fillStyle = "white";
  ctx.fill();
  ctx.fillStyle = colors7.primary;
  ctx.font = `bold 14px Open Sauce Two`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(label.toString(), centerX, centerY);
};
var drawLabel = (ctx, x, y, label, isCorrect) => {
  let borderColor = colors7.bgrey400;
  let bgColor = colors7.bgrey300;
  let pinColor = colors7.primary;
  let iconSrc = null;
  if (isCorrect !== void 0) {
    borderColor = isCorrect ? colors7.success : colors7.failure;
    bgColor = isCorrect ? colors7.successBg : colors7.failureBg;
    pinColor = isCorrect ? colors7.success : colors7.failure;
    iconSrc = isCorrect ? check_default : cross_default;
  }
  const updatedSvgString = pin_default.replace(
    /fill="[^"]*"/g,
    `fill="${pinColor}"`
  );
  const updatedPinDataUrl = `data:image/svg+xml;base64,${btoa(updatedSvgString)}`;
  const pinImage = new Image();
  pinImage.src = updatedPinDataUrl;
  pinImage.onload = () => {
    ctx.drawImage(
      pinImage,
      x - PIN_ICON_WIDTH / 2,
      y - PIN_ICON_HEIGHT / 2,
      PIN_ICON_WIDTH,
      PIN_ICON_HEIGHT
    );
  };
  ctx.font = `16px Open Sauce Two`;
  const textWidth = ctx.measureText(label).width;
  const labelWidth = textWidth + 30;
  let labelX = x - labelWidth - 20;
  const labelY = y - LABEL_HEIGHT / 2;
  if (labelX < 0) {
    labelX = x + PIN_ICON_WIDTH / 2 + 10;
  }
  ctx.fillStyle = bgColor;
  ctx.beginPath();
  ctx.moveTo(labelX + 10, labelY);
  ctx.arcTo(
    labelX + labelWidth,
    labelY,
    labelX + labelWidth,
    labelY + LABEL_HEIGHT,
    4
  );
  ctx.arcTo(
    labelX + labelWidth,
    labelY + LABEL_HEIGHT,
    labelX,
    labelY + LABEL_HEIGHT,
    4
  );
  ctx.arcTo(labelX, labelY + LABEL_HEIGHT, labelX, labelY, 4);
  ctx.arcTo(labelX, labelY, labelX + labelWidth, labelY, 4);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.fillStyle = colors7.neutralBlack500;
  ctx.textAlign = "center";
  ctx.fillText(
    label,
    labelX + labelWidth / 2,
    labelY + LABEL_HEIGHT / 2 + (isCorrect === void 0 ? 6 : 0)
  );
  if (!iconSrc) {
    return;
  }
  const iconImage = new Image();
  iconImage.src = iconSrc;
  iconImage.onload = () => {
    ctx.drawImage(iconImage, labelX + labelWidth - 8, labelY + 8, 16, 16);
  };
};
var drawDot = (ctx, x, y, isCorrect) => {
  ctx.beginPath();
  ctx.arc(x, y, 5, 0, Math.PI * 2);
  ctx.fillStyle = isCorrect ? colors7.success : colors7.failure;
  ctx.fill();
};
var renderPointMarker = (ctx, scale, label, coords, isCorrect) => {
  const padding = 10;
  const boxHeight = 26;
  ctx.font = "14px Open Sauce Two";
  const textWidth = ctx.measureText(label).width;
  const boxWidth = Math.max(textWidth + padding * 2, boxHeight);
  const [x = 0, y = 0] = coords.map((coord) => coord * scale);
  const shiftedX = x;
  const shiftedY = y - boxHeight - 5;
  let labelColor = colors7.primary;
  if (isCorrect !== void 0) {
    labelColor = isCorrect ? colors7.success : colors7.failure;
  }
  ctx.shadowColor = "rgba(0, 0, 0, 0.25)";
  ctx.shadowBlur = 10;
  ctx.shadowOffsetX = 2;
  ctx.shadowOffsetY = 2;
  ctx.fillStyle = labelColor;
  ctx.beginPath();
  ctx.moveTo(shiftedX + 10, shiftedY);
  ctx.arcTo(
    shiftedX + boxWidth,
    shiftedY,
    shiftedX + boxWidth,
    shiftedY + boxHeight,
    boxHeight / 2
  );
  ctx.arcTo(
    shiftedX + boxWidth,
    shiftedY + boxHeight,
    shiftedX,
    shiftedY + boxHeight,
    boxHeight / 2
  );
  ctx.arcTo(shiftedX, shiftedY + boxHeight, shiftedX, shiftedY, boxHeight / 2);
  ctx.arcTo(shiftedX, shiftedY, shiftedX + boxWidth, shiftedY, boxHeight / 2);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = labelColor;
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.shadowColor = "transparent";
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  const arrowHeight = 1;
  const arrowWidth = 15;
  ctx.beginPath();
  ctx.moveTo(shiftedX, shiftedY + 30);
  ctx.lineTo(
    shiftedX - arrowWidth / 2 + 10,
    shiftedY + boxHeight / 2 - arrowHeight
  );
  ctx.lineTo(
    shiftedX + arrowWidth / 2 + 20,
    shiftedY + boxHeight / 2 - arrowHeight
  );
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = "white";
  ctx.fillText(label, shiftedX + 10, shiftedY + 18);
};

// src/multiformat/question-preview/interactions/hotspot-interaction-preview/hooks.ts
var useHotspotPositionObjectInteractionPreview = (fields, snapshot, promptImage, mode, outcomes) => {
  const highlightCorrect = useMemo4(
    () => ["marked", "feedback", "response"].includes(mode ?? ""),
    [mode]
  );
  const [canvasRendering, setCanvasRendering] = useState3(true);
  const renderCorrectAreas = (ctx, scale) => {
    if (highlightCorrect) {
      fields.forEach((field, index) => {
        field.response?.correct_areas?.forEach((polygon) => {
          const outcome = outcomes?.find(
            (outcome2) => outcome2.identifier === field.identifier
          );
          const isCorrect = outcome && outcome.automark_score && outcome.automark_score > 0;
          const points2 = [];
          const coords = polygon.coords.map((coord) => coord * scale);
          for (let i = 0; i < polygon.coords.length; i += 2) {
            points2.push(coords.slice(i, i + 2));
          }
          drawPolygon(ctx, points2, index + 1, !!isCorrect);
        });
      });
    }
  };
  const renderLabels = (ctx, scale) => {
    fields.map((field, index) => {
      const position = snapshot?.answer_fields?.[field.identifier];
      if (position) {
        const label = `${index + 1}. ` + ("label" in field.interaction && field.interaction.label || "Option");
        const scaledX = parseFloat(position[0] ?? "") * scale;
        const scaledY = parseFloat(position[1] ?? "") * scale;
        if (highlightCorrect) {
          const outcome = outcomes?.find(
            (outcome2) => outcome2.identifier === field.identifier
          );
          const isCorrect = !!outcome?.automark_score && outcome.automark_score > 0;
          drawLabel(ctx, scaledX, scaledY, label, isCorrect);
        } else if (mode !== "question") {
          drawLabel(ctx, scaledX, scaledY, label);
        }
      }
    });
  };
  const { canvasRef, wrapperRef } = useHotspotCanvas(
    promptImage,
    (ctx, scale) => {
      renderCorrectAreas(ctx, scale);
      renderLabels(ctx, scale);
      setCanvasRendering(false);
    }
  );
  return {
    canvasRef,
    wrapperRef,
    highlightCorrect,
    canvasRendering
  };
};
var useHotspotTextChoiceInteractionPreview = (promptImage, fields, snapshot, outcomes, mode) => {
  const [canvasRendering, setCanvasRendering] = useState3(true);
  const highlightCorrect = useMemo4(
    () => ["marked", "feedback"].includes(mode ?? ""),
    [mode]
  );
  const savedAnswerMap = useMemo4(() => {
    return fields.reduce(
      (acc, field) => {
        if (field) {
          if (field.identifier) {
            const answer2 = getAnswerForBlank(snapshot, field.identifier);
            if (field.interaction && field.interaction.kind === "choice_interaction") {
              const choice = field.interaction.choices?.find(
                (choice2) => choice2?.identifier === answer2
              );
              acc[field.identifier] = choice ? choice.content ?? "" : "";
            } else {
              acc[field.identifier] = answer2 ?? "";
            }
          }
        }
        return acc;
      },
      {}
    );
  }, [fields]);
  const renderPoints = (context, scale) => {
    if (context) {
      promptImage?.layers?.forEach((layer, index) => {
        const [x = 0, y = 0] = layer.coords;
        const outcome = outcomes?.find(
          (outcome2) => outcome2.identifier === layer.identifier
        );
        const isCorrect = outcome && outcome.automark_score && outcome.automark_score > 0;
        const savedAnswer = ["answer", "response"].includes(mode ?? "") ? savedAnswerMap?.[layer.identifier] ? `. ${savedAnswerMap[layer.identifier]}` : "" : "";
        renderPointMarker(
          context,
          scale,
          `${index + 1}${savedAnswer}`,
          [x, y],
          highlightCorrect ? !!isCorrect : void 0
        );
      });
    }
  };
  const { canvasRef, wrapperRef } = useHotspotCanvas(
    promptImage,
    (ctx, scale) => {
      renderPoints(ctx, scale);
      setCanvasRendering(false);
    }
  );
  return {
    canvasRef,
    wrapperRef,
    canvasRendering
  };
};

// src/multiformat/question-preview/interactions/hotspot-interaction-preview/hotspot-interaction-preview.css.ts
import { createRuntimeFn as _7a4682 } from "@vanilla-extract/recipes/createRuntimeFn";
var CanvasWrapper = _7a4682({ defaultClassName: "fs67xm1", variantClassNames: { hasWiderImage: { true: "fs67xm2" } }, defaultVariants: {}, compoundVariants: [] });
var ChoicesList = "fs67xm6";
var ChoicesListAlertWrapper = "fs67xm4";
var ChoicesListWrapper = "fs67xm5";
var ChoicesWrapper = "fs67xm3";
var PreviewWrapper = "fs67xm0";

// src/multiformat/question-preview/interactions/hotspot-interaction-preview/position-object-interaction-preview.tsx
import { CheckRoundIcon as CheckRoundIcon6, CloseRoundIcon as CloseRoundIcon6 } from "@vericus/cadmus-icons";
import { colors as colors8, Text as Text17 } from "@vericus/cadmus-ui";
import { jsx as jsx26 } from "react/jsx-runtime";
var PositionObjectInteractionPreview = (props) => {
  const { field, mode, outcome, answer: answer2, fieldLabel, highlightCorrect } = props;
  const withLabel = fieldLabel !== null;
  let itemProps = {
    id: field.identifier,
    text: answer2,
    withLabel
  };
  if (highlightCorrect) {
    {
      const automarkScore = outcome?.automark_score ?? null;
      const markedCorrect = automarkScore !== null ? automarkScore > 0 : null;
      let colorVariant = "grey300";
      let icon3 = void 0;
      if (markedCorrect === true) {
        icon3 = /* @__PURE__ */ jsx26(
          CheckRoundIcon6,
          {
            color: colors8.success,
            label: "Marked correct",
            size: 16
          }
        );
        colorVariant = "functionalSuccess";
      } else if (markedCorrect === false) {
        icon3 = /* @__PURE__ */ jsx26(CloseRoundIcon6, { label: "Marked incorrect", size: 16 });
        colorVariant = "functionalFail";
      }
      const rightAction = automarkScore !== null && mode === "marked" && /* @__PURE__ */ jsx26("div", { className: awardedPoints2, children: /* @__PURE__ */ jsx26(Text17, { kind: "systemMd", color: "navy500", children: formatPoints(automarkScore, true) }) });
      itemProps = {
        ...itemProps,
        colorVariant,
        icon: icon3,
        rightAction,
        dataAttributes: {
          "data-testid": `field-${field.identifier}`,
          "data-answered-correctly": markedCorrect
        }
      };
    }
  }
  return /* @__PURE__ */ jsx26(OptionList.Item, { ...itemProps, children: /* @__PURE__ */ jsx26(Text17, { kind: "bodyMd", color: "navy700", children: answer2 }) }, field.identifier);
};

// src/multiformat/question-preview/interactions/hotspot-interaction-preview/hotspot-interaction-preview.tsx
import { jsx as jsx27, jsxs as jsxs16 } from "react/jsx-runtime";
var HotspotPositionObjectInteractionPreview = ({ fields, snapshot, promptImage, mode, outcomes, printView }) => {
  const { canvasRef, wrapperRef, highlightCorrect, canvasRendering } = useHotspotPositionObjectInteractionPreview(
    fields,
    snapshot,
    promptImage,
    mode,
    outcomes
  );
  const interactions = fields.map((field, index) => {
    const outcome = outcomes.find(
      (outcome2) => outcome2.identifier === field.identifier
    );
    return /* @__PURE__ */ jsx27(
      PositionObjectInteractionPreview,
      {
        mode,
        field,
        outcome: outcome ?? null,
        answer: "label" in field.interaction ? field.interaction.label ?? "" : "",
        fieldLabel: index + 1,
        highlightCorrect
      },
      field.identifier
    );
  });
  let choices = [];
  if (printView) {
    const uniqueContents = Array.from(
      new Set(
        fields.filter(
          (field) => field.interaction.kind === "position_object_interaction" && "label" in field.interaction
        ).map((field) => field.interaction.label)
      )
    );
    choices = shuffleArray(uniqueContents);
  }
  return /* @__PURE__ */ jsxs16(Fragment8, { children: [
    /* @__PURE__ */ jsx27("div", { className: PreviewWrapper, children: /* @__PURE__ */ jsxs16(
      "div",
      {
        className: CanvasWrapper({
          hasWiderImage: !!promptImage && (promptImage.width || 0) > (promptImage.height || 0)
        }),
        ref: wrapperRef,
        children: [
          /* @__PURE__ */ jsx27(
            "canvas",
            {
              style: { display: !canvasRendering ? "block" : "none" },
              ref: canvasRef
            }
          ),
          canvasRendering && /* @__PURE__ */ jsx27(
            "img",
            {
              src: promptImage?.url,
              alt: "Hotspot Preview",
              style: {
                width: "100%",
                height: "100%"
              }
            }
          )
        ]
      }
    ) }),
    printView ? /* @__PURE__ */ jsxs16("div", { className: ChoicesListAlertWrapper, children: [
      /* @__PURE__ */ jsx27(Alert, { severity: "info", size: "sm", children: "Use the label number while marking on the image." }),
      /* @__PURE__ */ jsx27(Spacer3, { spacing: 12 }),
      /* @__PURE__ */ jsx27("div", { className: ChoicesList, children: choices.map((choice, index) => /* @__PURE__ */ jsxs16(Pill2, { color: colors9.bgrey300, basic: true, lowerCase: true, children: [
        index + 1,
        ". ",
        choice
      ] }, choice)) })
    ] }) : /* @__PURE__ */ jsx27(OptionList.List, { children: interactions })
  ] });
};
var HotspotTextChoiceInteractionPreview = ({ fields, mode, outcomes, promptImage, snapshot, printView }) => {
  const { canvasRef, wrapperRef, canvasRendering } = useHotspotTextChoiceInteractionPreview(
    promptImage,
    fields,
    snapshot,
    outcomes,
    mode
  );
  const interactions = fields.map((field, index) => {
    const answer2 = getAnswerForBlank(snapshot, field.identifier);
    const outcome = outcomes.find(
      (outcome2) => outcome2.identifier === field.identifier
    );
    if (field.interaction.kind === "choice_interaction") {
      return /* @__PURE__ */ jsx27(
        InlineChoiceInteractionPreview,
        {
          mode,
          field,
          outcome: outcome ?? null,
          answerChoiceId: answer2 ?? null,
          fieldLabel: index + 1,
          printView,
          questionType: "hotspot"
        },
        field.identifier
      );
    } else {
      return /* @__PURE__ */ jsx27(
        TextEntryInteractionPreview,
        {
          mode,
          field,
          outcome: outcome ?? null,
          answer: answer2,
          fieldLabel: index + 1,
          printView
        },
        field.identifier
      );
    }
  });
  let choices = [];
  if (printView) {
    const uniqueContents = Array.from(
      new Set(
        fields.filter(
          (field) => field.interaction.kind === "choice_interaction" && "choices" in field.interaction
        ).flatMap(
          (field) => field.interaction.choices.map(
            (choice) => choice.content
          )
        )
      )
    );
    choices = shuffleArray(uniqueContents);
  }
  return /* @__PURE__ */ jsxs16(Fragment8, { children: [
    /* @__PURE__ */ jsx27("div", { className: PreviewWrapper, children: /* @__PURE__ */ jsxs16(
      "div",
      {
        className: CanvasWrapper({
          hasWiderImage: !!promptImage && (promptImage.width || 0) > (promptImage.height || 0)
        }),
        ref: wrapperRef,
        children: [
          /* @__PURE__ */ jsx27(
            "canvas",
            {
              style: { display: !canvasRendering ? "block" : "none" },
              ref: canvasRef
            }
          ),
          canvasRendering && /* @__PURE__ */ jsx27(
            "img",
            {
              src: promptImage?.url,
              alt: "Hotspot Preview",
              style: {
                width: "97%",
                height: "100%"
              }
            }
          )
        ]
      }
    ) }),
    /* @__PURE__ */ jsxs16("div", { className: ChoicesWrapper, children: [
      printView && choices && choices.length > 0 && /* @__PURE__ */ jsxs16("div", { className: ChoicesListWrapper, children: [
        /* @__PURE__ */ jsx27(Text18, { kind: "bodyMd", color: "navy700", children: "Select from:" }),
        /* @__PURE__ */ jsx27("div", { className: ChoicesList, children: choices.map((choice) => /* @__PURE__ */ jsx27(Pill2, { color: colors9.bgrey300, basic: true, lowerCase: true, children: choice }, choice)) })
      ] }),
      /* @__PURE__ */ jsx27(OptionList.List, { children: interactions })
    ] })
  ] });
};
var HotspotInteractionPreview = (props) => {
  const interactionKind = props.fields[0]?.interaction.kind;
  return interactionKind === "position_object_interaction" ? /* @__PURE__ */ jsx27(HotspotPositionObjectInteractionPreview, { ...props }) : /* @__PURE__ */ jsx27(HotspotTextChoiceInteractionPreview, { ...props });
};

// src/multiformat/question-preview/interaction-preview/choice-interaction-previews/choice-interaction-preview.tsx
import {
  CheckRoundIcon as CheckRoundIcon7,
  CloseRoundIcon as CloseRoundIcon7,
  ErrorIcon as ErrorIcon2
} from "@vericus/cadmus-icons";
import { colors as colors10, Text as Text19 } from "@vericus/cadmus-ui";
import { jsx as jsx28, jsxs as jsxs17 } from "react/jsx-runtime";
function ChoiceInteractionPreview(props) {
  const { mode, field, outcome, answerChoiceIds, shuffleSeed } = props;
  const passed = typeof outcome?.automark_score === "number" && outcome.automark_score > 0;
  const choices = field.interaction.kind === "choice_interaction" ? field.interaction.choices : [];
  const shuffledChoices = shuffleChoices(choices, shuffleSeed);
  const correctChoicesCount = field.interaction.kind === "choice_interaction" ? field.response.correct_values.length : null;
  const scorePerChoice = props.maxScore !== null && typeof correctChoicesCount === "number" && correctChoicesCount > 0 ? props.maxScore / correctChoicesCount : 0;
  const datas = shuffledChoices.map((choice) => {
    const state = makeChoiceState(
      choice.identifier,
      field.response,
      mode === "answer" ? answerChoiceIds : outcome?.answers ?? [],
      mode,
      passed
    );
    const optionColor = stateBackground(state);
    const checked = stateIsChecked(state);
    const isCorrect = field.response.correct_values.includes(choice.identifier);
    let icon3 = void 0;
    if (state === 3 /* MarkedCorrect */ || state === 6 /* NotPassedAndIsCorrect */ || state === 1 /* SelectedByTeacher */) {
      icon3 = /* @__PURE__ */ jsx28(
        CheckRoundIcon7,
        {
          label: "Marked correct",
          size: 16,
          color: colors10.success
        }
      );
    } else if (state === 4 /* MarkedIncorrect */) {
      icon3 = /* @__PURE__ */ jsx28(CloseRoundIcon7, { label: "Marked incorrect", size: 16 });
    }
    return {
      id: choice.identifier,
      text: choice.content,
      colorVariant: optionColor,
      selected: checked,
      rightAction: mode !== "feedback" ? stateFeedbackNode(state, scorePerChoice) : void 0,
      showToggle: true,
      icon: icon3,
      dataAttributes: {
        "data-testid": `choice-${choice.identifier}`,
        "data-answered-correctly": state === 3 /* MarkedCorrect */ ? true : state === 4 /* MarkedIncorrect */ ? false : void 0,
        "data-correct-answer": mode === "marked" || mode === "feedback" ? isCorrect : void 0
      }
    };
  });
  const partialScoringCallout = field.response.partial_scoring === false && (mode === "marked" || mode === "feedback") && passed === false && /* @__PURE__ */ jsxs17("div", { className: callout2, children: [
    /* @__PURE__ */ jsx28(ErrorIcon2, { label: "", size: 16 }),
    /* @__PURE__ */ jsx28(
      Text19,
      {
        kind: "bodySm",
        color: "functionalFail",
        "aria-live": "assertive",
        as: "span",
        children: "Note: All correct answers are required."
      }
    )
  ] });
  return /* @__PURE__ */ jsx28(OptionList.List, { topAction: partialScoringCallout, children: datas.map((data) => /* @__PURE__ */ jsx28(OptionList.Item, { ...data }, data.id)) });
}
function makeChoiceState(identifier, response2, answerIdentifiers, mode, passed) {
  const isCorrect = response2.correct_values.includes(identifier);
  if (mode === "response") {
    if (isCorrect === true) {
      return 1 /* SelectedByTeacher */;
    }
  }
  if (mode === "answer") {
    if (answerIdentifiers.includes(identifier)) {
      return 2 /* SelectedByStudent */;
    }
  }
  if (mode === "marked" || mode === "feedback") {
    if (isCorrect === true) {
      if (answerIdentifiers.includes(identifier)) {
        if (response2.partial_scoring === false && passed === false) {
          return 6 /* NotPassedAndIsCorrect */;
        }
        return 3 /* MarkedCorrect */;
      } else {
        return 5 /* NotSelectedAndIsCorrect */;
      }
    } else if (isCorrect === false) {
      if (answerIdentifiers.includes(identifier)) {
        return 4 /* MarkedIncorrect */;
      } else {
        return 0 /* Unselected */;
      }
    }
  }
  return 0 /* Unselected */;
}
function stateFeedbackNode(state, scorePerChoice) {
  if (state === 1 /* SelectedByTeacher */) {
    return /* @__PURE__ */ jsx28(
      Text19,
      {
        kind: "bodySm",
        color: "navy500",
        className: correctAnswerFeedback2,
        "data-testid": "correct-answer-feedback",
        as: "span",
        children: "Correct answer"
      }
    );
  }
  if (state === 6 /* NotPassedAndIsCorrect */) {
    return /* @__PURE__ */ jsx28(
      Text19,
      {
        kind: "systemMd",
        className: pointsFeedback2,
        "data-testid": "not-passed-is-correct-choice-score",
        children: formatPoints(0, true)
      }
    );
  } else if (state === 3 /* MarkedCorrect */ && scorePerChoice !== null) {
    return /* @__PURE__ */ jsx28(
      Text19,
      {
        kind: "systemMd",
        className: pointsFeedback2,
        "data-testid": "marked-correct-choice-score",
        children: formatPoints(scorePerChoice, true)
      }
    );
  }
  if (state === 4 /* MarkedIncorrect */ && scorePerChoice !== null) {
    return /* @__PURE__ */ jsx28(
      Text19,
      {
        kind: "systemMd",
        className: pointsFeedback2,
        "data-testid": "marked-incorrect-choice-score",
        children: formatPoints(0, true)
      }
    );
  }
  if (state === 5 /* NotSelectedAndIsCorrect */) {
    return /* @__PURE__ */ jsx28(
      Text19,
      {
        kind: "bodySm",
        color: "navy500",
        className: correctAnswerFeedback2,
        "data-testid": "correct-answer-feedback",
        as: "span",
        children: "Correct answer"
      }
    );
  }
  return null;
}
function stateIsChecked(state) {
  if (state === 1 /* SelectedByTeacher */) return true;
  if (state === 2 /* SelectedByStudent */) return true;
  if (state === 3 /* MarkedCorrect */) return true;
  if (state === 4 /* MarkedIncorrect */) return true;
  if (state === 6 /* NotPassedAndIsCorrect */) return true;
  return false;
}
function stateBackground(state) {
  if (state === 1 /* SelectedByTeacher */) return "functionalSuccess";
  if (state === 2 /* SelectedByStudent */) return "primary";
  if (state === 3 /* MarkedCorrect */) return "functionalSuccess";
  if (state === 4 /* MarkedIncorrect */) return "functionalFail";
  if (state === 5 /* NotSelectedAndIsCorrect */) return "functionalSuccess";
  if (state === 6 /* NotPassedAndIsCorrect */) return "functionalSuccess";
  return "grey300";
}

// src/multiformat/question-preview/interaction-preview/extended-interaction-preview/extended-interaction-preview.tsx
import { useMemo as useMemo5 } from "react";
import {
  listFootnotes
} from "@vericus/cadmus-editor-prosemirror";
import { CadmusKitServer } from "@vericus/cadmus-editor-prosemirror/server";
import { EditorContentSkeleton as EditorContentSkeleton2, Text as Text20 } from "@vericus/cadmus-ui";
import { getSchema } from "@tiptap/core";

// src/multiformat/question-preview/interaction-preview/extended-interaction-preview/footnote-content.css.ts
var container2 = "gp7l7z0";
var footnote = "gp7l7z1";

// src/multiformat/question-preview/interaction-preview/extended-interaction-preview/extended-interaction-preview.tsx
import { Fragment as Fragment9, jsx as jsx29, jsxs as jsxs18 } from "react/jsx-runtime";
var schema = getSchema([CadmusKitServer.configure()]);
function ExtendedInteractionPreview(props) {
  const footnotes = useMemo5(() => {
    if (!props.answerDoc) return [];
    const doc = schema.nodeFromJSON(props.answerDoc);
    return listFootnotes(doc);
  }, [props.answerDoc]);
  if (props.answerDoc !== null) {
    return /* @__PURE__ */ jsxs18("div", { className: answerWrapper2, children: [
      props.wordLimit !== null && /* @__PURE__ */ jsx29("div", { className: wordLimit2, children: /* @__PURE__ */ jsxs18(Text20, { kind: "bodySm", bold: true, children: [
        "Word Limit: ",
        props.wordLimit
      ] }) }),
      /* @__PURE__ */ jsx29("div", { className: divider2 }),
      /* @__PURE__ */ jsx29(
        EditorPreviewer,
        {
          id: `${props.id}-answer`,
          promptDoc: props.answerDoc,
          extensions: props.extensions,
          enableFootnoting: true
        }
      ),
      props.referencesDoc && /* @__PURE__ */ jsx29(
        EditorPreviewer,
        {
          id: `${props.id}-references`,
          promptDoc: props.referencesDoc,
          extensions: props.extensions
        }
      ),
      footnotes.length > 0 && /* @__PURE__ */ jsx29(FootnoteContent, { footnotes })
    ] });
  } else {
    return /* @__PURE__ */ jsxs18(Fragment9, { children: [
      props.wordLimit !== null && /* @__PURE__ */ jsx29("div", { className: wordLimit2, style: { marginBottom: 24 }, children: /* @__PURE__ */ jsxs18(Text20, { kind: "bodySm", bold: true, children: [
        "Word Limit: ",
        props.wordLimit
      ] }) }),
      /* @__PURE__ */ jsx29("div", { className: editorSkeletonWrapper2, children: /* @__PURE__ */ jsx29(EditorContentSkeleton2, {}) })
    ] });
  }
}
function FootnoteContent(props) {
  const parser = new DOMParser();
  return /* @__PURE__ */ jsx29("div", { className: container2, children: props.footnotes.map((footnote2) => {
    const content4 = parser.parseFromString(
      footnote2.content ?? "",
      "text/html"
    );
    return /* @__PURE__ */ jsx29(
      "div",
      {
        dangerouslySetInnerHTML: { __html: content4.body.innerHTML },
        className: footnote
      },
      footnote2.id
    );
  }) });
}

// src/multiformat/question-preview/interaction-preview/matching-interaction-preview.tsx
import {
  CheckRoundIcon as CheckRoundIcon8,
  ChevronDownIcon as ChevronDownIcon2,
  CloseRoundIcon as CloseRoundIcon8,
  ErrorIcon as ErrorIcon3
} from "@vericus/cadmus-icons";
import { Alert as Alert2, colors as colors11, Text as Text22 } from "@vericus/cadmus-ui";

// src/multiformat/question-preview/matching-response-preview/matching-response-preview.tsx
import { Text as Text21 } from "@vericus/cadmus-ui";

// src/multiformat/question-preview/matching-response-preview/matching-response-preview.css.ts
var answer = "_1n6hmkg6";
var arrowHead = "_1n6hmkg5";
var content2 = "_1n6hmkg0";
var flexContainer = "_1n6hmkg2";
var header2 = "_1n6hmkg1";
var prompt = "_1n6hmkg4";
var row = "_1n6hmkg3";

// src/multiformat/question-preview/matching-response-preview/matching-response-preview.tsx
import { jsx as jsx30, jsxs as jsxs19 } from "react/jsx-runtime";
var MatchingResponsePreview = ({
  targetSet,
  sourceSet,
  answerMap
}) => {
  const distractors = sourceSet && sourceSet.filter(
    (target) => !answerMap.some((val) => val.targetIdentifier === target.identifier)
  );
  const answerSetContent = /* @__PURE__ */ new Set();
  const distractorSetContent = /* @__PURE__ */ new Set();
  return /* @__PURE__ */ jsxs19("div", { className: content2, children: [
    /* @__PURE__ */ jsx30("div", { className: header2, children: /* @__PURE__ */ jsx30(Text21, { color: "navy400", kind: "systemMd", children: "PROMPT / ANSWER" }) }),
    /* @__PURE__ */ jsx30("div", { className: flexContainer, children: targetSet.map((target) => {
      const sourceItem = sourceSet.find(
        (sourceItem2) => sourceItem2.identifier === answerMap.find(
          (val) => val.targetIdentifier === target.identifier
        )?.sourceIdentifier
      );
      if (sourceItem?.content) {
        answerSetContent.add(sourceItem.content);
      }
      return /* @__PURE__ */ jsxs19("div", { className: row, children: [
        /* @__PURE__ */ jsx30("div", { className: prompt, children: /* @__PURE__ */ jsx30(Text21, { kind: "bodyLine", children: target.content }) }),
        /* @__PURE__ */ jsx30("span", { className: arrowHead, children: "\u2192" }),
        /* @__PURE__ */ jsx30("div", { className: answer, children: /* @__PURE__ */ jsx30(Text21, { kind: "bodyLine", children: sourceItem?.content }) })
      ] }, target.identifier);
    }) }),
    distractors.filter((dItem) => !answerSetContent.has(dItem.content)).map((dupDistractor) => {
      distractorSetContent.add(dupDistractor.content);
      return null;
    }),
    distractorSetContent.size > 0 && /* @__PURE__ */ jsxs19("div", { children: [
      /* @__PURE__ */ jsx30("div", { className: header2, children: /* @__PURE__ */ jsx30(Text21, { color: "navy400", kind: "systemMd", children: "DISTRACTORS" }) }),
      /* @__PURE__ */ jsx30("div", { className: flexContainer, children: Array.from(distractorSetContent).map((item) => {
        return /* @__PURE__ */ jsx30("div", { className: row, children: /* @__PURE__ */ jsx30(Text21, { kind: "bodyLine", color: "navy400", children: item }) }, item);
      }) })
    ] })
  ] });
};

// src/multiformat/question-preview/interaction-preview/matching-interaction-preview.tsx
import { Fragment as Fragment10, jsx as jsx31, jsxs as jsxs20 } from "react/jsx-runtime";
function MatchingInteractionPreview(props) {
  const { mode, field, outcome, answer: answer2, printView } = props;
  const targetSet = field.interaction.kind === "match_interaction" ? field.interaction.target_set : [];
  const sourceSet = field.interaction.kind === "match_interaction" ? field.interaction.source_set : [];
  const answerMap = answer2.map((pair) => {
    const [targetIdentifier, sourceIdentifier] = pair.split(" ");
    return { targetIdentifier, sourceIdentifier };
  });
  const correctValueMaps = field.response.correct_values.map((pair) => {
    const [targetIdentifier, sourceIdentifier] = pair.split(" ");
    return { targetIdentifier, sourceIdentifier };
  });
  const scorePerTarget = props.maxScore !== null && targetSet.length > 0 ? props.maxScore / targetSet.length : 0;
  const passed = outcome?.automark_score ? outcome?.automark_score > 0 : false;
  const partialScoringCallout = field.response.partial_scoring === false && (mode === "marked" || mode === "feedback") && passed === false && /* @__PURE__ */ jsxs20("div", { className: callout2, children: [
    /* @__PURE__ */ jsx31(ErrorIcon3, { label: "", size: 16 }),
    /* @__PURE__ */ jsx31(
      Text22,
      {
        kind: "bodySm",
        color: "functionalFail",
        "aria-live": "assertive",
        as: "span",
        children: "Note: All correct answers are required."
      }
    )
  ] });
  if (printView && mode === "question") {
    const choicesToDisplay = shuffleArray(sourceSet);
    return /* @__PURE__ */ jsx31("div", { className: root9, children: /* @__PURE__ */ jsx31(
      MatchingPrintPreview,
      {
        targetSet,
        sourceSet: choicesToDisplay
      }
    ) });
  }
  if (mode === "response") {
    return /* @__PURE__ */ jsx31("div", { className: root9, children: /* @__PURE__ */ jsx31("div", { className: blankRoot2, children: /* @__PURE__ */ jsx31(
      MatchingResponsePreview,
      {
        targetSet,
        sourceSet,
        answerMap: correctValueMaps
      }
    ) }) });
  }
  const matchingPreview = targetSet.map((target) => {
    const answerSourceIdentifier = answerMap.find(
      (map) => map.targetIdentifier === target.identifier
    )?.sourceIdentifier;
    let itemProps;
    switch (mode) {
      case "question": {
        itemProps = {
          id: target.identifier,
          leftAction: /* @__PURE__ */ jsx31("div", { className: leftActionPrompt, children: `${target.content}` }),
          text: "Select an answer",
          withLabel: true
        };
        break;
      }
      case "answer": {
        const selectedAnswerContent = sourceSet.find(
          (source) => source.identifier === answerSourceIdentifier
        )?.content;
        itemProps = {
          id: target.identifier,
          text: selectedAnswerContent ?? "Select an answer",
          withLabel: true,
          leftAction: /* @__PURE__ */ jsx31("div", { className: leftActionPrompt, children: `${target.content}` }),
          dataAttributes: {
            "data-testid": `field-${field.identifier}-${target.identifier}`
          }
        };
        break;
      }
      case "marked":
      case "feedback": {
        const markedSourceId = getMarkedAnswer3(target.identifier, outcome);
        const selectedSourceContent = sourceSet.find(
          (source) => source.identifier === markedSourceId
        )?.content;
        const correctSourceId = field.response.correct_values.find((pair) => pair.startsWith(target.identifier))?.split(" ")[1];
        const markedCorrect = correctSourceId === markedSourceId;
        const scoreForTarget = passed ? scorePerTarget : 0;
        const correctSources = sourceSet.filter((source) => source.identifier === correctSourceId).map((source) => source.content);
        let colorVariant = "grey300";
        let icon3 = void 0;
        if (markedCorrect === true) {
          icon3 = /* @__PURE__ */ jsx31(CheckRoundIcon8, { label: "Marked correct", color: colors11.success });
          colorVariant = "functionalSuccess";
        } else if (markedCorrect === false) {
          icon3 = /* @__PURE__ */ jsx31(CloseRoundIcon8, { label: "Marked incorrect" });
          colorVariant = "functionalFail";
        }
        const leftAction = /* @__PURE__ */ jsx31("div", { className: leftActionPrompt, children: `${target.content}` });
        const rightAction = markedCorrect && mode === "marked" && /* @__PURE__ */ jsx31("div", { className: awardedPoints2, children: /* @__PURE__ */ jsx31(Text22, { kind: "systemMd", color: "navy500", children: formatPoints(scoreForTarget, true) }) });
        const bottomAction2 = markedCorrect === false && /* @__PURE__ */ jsx31("div", { children: /* @__PURE__ */ jsxs20(
          Text22,
          {
            kind: "bodySm",
            color: "neutralgrey500",
            "data-testid": "correct-answer-display",
            children: [
              "Correct Answer:",
              " ",
              /* @__PURE__ */ jsx31("span", { className: acceptedPhrases2, children: correctSources.join(" | ") })
            ]
          }
        ) });
        itemProps = {
          id: field.identifier,
          text: selectedSourceContent ?? void 0,
          colorVariant,
          icon: icon3,
          leftAction,
          rightAction,
          withLabel: true,
          bottomAction: bottomAction2,
          dataAttributes: {
            "data-testid": `field-${field.identifier}`,
            "data-answered-correctly": markedCorrect
          }
        };
        break;
      }
      default:
        return null;
    }
    return /* @__PURE__ */ jsx31(OptionList.Item, { ...itemProps, children: /* @__PURE__ */ jsxs20("div", { className: matchingDropdownItem, children: [
      /* @__PURE__ */ jsx31(Text22, { kind: "bodyMd", color: "navy700", children: itemProps.text }),
      /* @__PURE__ */ jsx31("div", { className: dropdownIcon, children: /* @__PURE__ */ jsx31(ChevronDownIcon2, { label: "", size: 24 }) })
    ] }) }, target.identifier);
  });
  return /* @__PURE__ */ jsx31(OptionList.List, { topAction: partialScoringCallout, children: matchingPreview });
}
function getMarkedAnswer3(targetIdentifier, outcome) {
  if (outcome?.answers && outcome.answers.length > 0) {
    const answer2 = outcome.answers.find(
      (answer3) => answer3.startsWith(targetIdentifier)
    );
    if (answer2) {
      return answer2.split(" ")[1] ?? null;
    }
  }
  return null;
}
function MatchingPrintPreview(props) {
  const { targetSet, sourceSet } = props;
  const leftList = targetSet.map((target, index) => {
    return /* @__PURE__ */ jsxs20("div", { children: [
      /* @__PURE__ */ jsxs20(Text22, { kind: "bodyLine", children: [
        " ",
        index + 1,
        ". ",
        target.content
      ] }),
      /* @__PURE__ */ jsx31(OptionList.Item, {}, target.identifier)
    ] }, target.identifier);
  });
  const rightList = sourceSet.map((source, index) => {
    const letter = String.fromCharCode(65 + index);
    return /* @__PURE__ */ jsx31("div", { className: matchingPrintRow, children: /* @__PURE__ */ jsxs20(Text22, { kind: "bodyLine", children: [
      letter,
      ". ",
      source.content
    ] }) }, source.identifier);
  });
  return /* @__PURE__ */ jsxs20(Fragment10, { children: [
    /* @__PURE__ */ jsx31("div", { className: matchingPrintAlert, children: /* @__PURE__ */ jsx31(Alert2, { severity: "info", size: "sm", children: 'Write the corresponding label like "A" in the box below items in the left column.' }) }),
    /* @__PURE__ */ jsxs20("div", { className: matchingPrintContainer, children: [
      /* @__PURE__ */ jsx31("div", { className: matchingPrintLeft, children: leftList }),
      /* @__PURE__ */ jsx31("div", { className: matchingPrintRight, children: rightList })
    ] })
  ] });
}

// src/multiformat/question-preview/interaction-preview/text-interaction-previews/numeric-text-entry-interaction-preview.tsx
import { CheckRoundIcon as CheckRoundIcon9, CloseRoundIcon as CloseRoundIcon9 } from "@vericus/cadmus-icons";
import { colors as colors12, Text as Text24 } from "@vericus/cadmus-ui";

// src/multiformat/question-preview/accepted-numbers-preview/accepted-numbers-preview.tsx
import { Text as Text23 } from "@vericus/cadmus-ui";

// src/multiformat/question-preview/accepted-numbers-preview/accepted-numbers-preview.css.ts
var number = "_1jzmcef1";
var root10 = "_1jzmcef0";

// src/multiformat/question-preview/accepted-numbers-preview/accepted-numbers-preview.tsx
import { jsx as jsx32 } from "react/jsx-runtime";
var AcceptedNumbersPreview = ({ numbers }) => /* @__PURE__ */ jsx32("div", { className: root10, "data-testid": "accepted-numbers", children: numbers.map((number2) => /* @__PURE__ */ jsx32("div", { className: number, children: /* @__PURE__ */ jsx32(Text23, { kind: "bodyMd", color: "functionalSuccess", children: formatNumberString(number2) }) }, number2)) });

// src/multiformat/question-preview/interaction-preview/text-interaction-previews/numeric-text-entry-interaction-preview.tsx
import { jsx as jsx33, jsxs as jsxs21 } from "react/jsx-runtime";
function NumericTextEntryInteractionPreview(props) {
  const { field, mode, outcome, answer: answer2, fieldLabel, printView } = props;
  const withLabel = fieldLabel !== null;
  if (mode === "response") {
    return /* @__PURE__ */ jsx33("div", { className: root9, children: /* @__PURE__ */ jsxs21("div", { className: blankRoot2, children: [
      withLabel && /* @__PURE__ */ jsx33("div", { className: labelWrapper2, children: /* @__PURE__ */ jsxs21(
        Text24,
        {
          className: blankLabel2,
          kind: "bodyMd",
          color: "neutralBlack300",
          children: [
            `${fieldLabel}.`,
            " "
          ]
        }
      ) }),
      /* @__PURE__ */ jsx33(AcceptedNumbersPreview, { numbers: field.response.correct_values })
    ] }, field.identifier) });
  }
  let itemProps;
  switch (mode) {
    case "question":
      itemProps = {
        id: field.identifier,
        text: !printView ? "Add your answer" : "",
        withLabel
      };
      break;
    case "answer": {
      itemProps = {
        id: field.identifier,
        text: answer2 ? formatNumberString(answer2) : "Add your answer",
        withLabel,
        dataAttributes: {
          "data-testid": `field-${field.identifier}`
        }
      };
      break;
    }
    case "marked":
    case "feedback": {
      const automarkScore = outcome?.automark_score ?? null;
      const markedCorrect = automarkScore !== null ? automarkScore > 0 : null;
      const markedAnswer = getMarkedAnswer4(outcome);
      let colorVariant = "grey300";
      let icon3 = void 0;
      if (markedCorrect === true) {
        icon3 = /* @__PURE__ */ jsx33(
          CheckRoundIcon9,
          {
            label: "Marked correct",
            size: 16,
            color: colors12.success
          }
        );
        colorVariant = "functionalSuccess";
      } else if (markedCorrect === false) {
        icon3 = /* @__PURE__ */ jsx33(CloseRoundIcon9, { label: "Marked incorrect", size: 16 });
        colorVariant = "functionalFail";
      }
      const bottomAction2 = markedCorrect === false && /* @__PURE__ */ jsxs21(
        Text24,
        {
          kind: "bodySm",
          color: "neutralgrey500",
          "data-testid": "correct-answer-display",
          children: [
            "Accepted Answer:",
            " ",
            /* @__PURE__ */ jsx33("span", { className: acceptedPhrases2, children: field.response.correct_values.map((val) => formatNumberString(val)).join(" | ") })
          ]
        }
      );
      const rightAction = automarkScore !== null && mode === "marked" && /* @__PURE__ */ jsx33("div", { className: awardedPoints2, children: /* @__PURE__ */ jsx33(Text24, { kind: "systemMd", color: "navy500", children: formatPoints(automarkScore, true) }) });
      itemProps = {
        id: field.identifier,
        text: markedAnswer ? formatNumberString(markedAnswer) : void 0,
        colorVariant,
        bottomAction: bottomAction2,
        icon: icon3,
        rightAction,
        withLabel,
        dataAttributes: {
          "data-testid": `field-${field.identifier}`,
          "data-answered-correctly": markedCorrect
        }
      };
      break;
    }
  }
  return /* @__PURE__ */ jsx33(OptionList.Item, { ...itemProps, children: /* @__PURE__ */ jsx33(Text24, { kind: "bodyMd", color: "navy700", children: itemProps.text }) }, field.identifier);
}
function getMarkedAnswer4(outcome) {
  if (outcome?.answers && outcome.answers.length > 0 && outcome.answers[0]) {
    return outcome.answers[0];
  }
  if (outcome?.answer) {
    return outcome.answer;
  }
  return null;
}

// src/multiformat/question-preview/interaction-preview/text-interaction-previews/range-text-entry-interaction-preview.tsx
import { CheckRoundIcon as CheckRoundIcon10, CloseRoundIcon as CloseRoundIcon10 } from "@vericus/cadmus-icons";
import { colors as colors13, Text as Text26 } from "@vericus/cadmus-ui";

// src/multiformat/question-preview/accepted-ranges-preview/accepted-ranges-preview.tsx
import { Text as Text25 } from "@vericus/cadmus-ui";

// src/multiformat/question-preview/accepted-ranges-preview/accepted-ranges-preview.css.ts
var range = "_12fevyp2";
var rangeRow = "_12fevyp1";
var root11 = "_12fevyp0";

// src/multiformat/question-preview/accepted-ranges-preview/accepted-ranges-preview.tsx
import { jsx as jsx34, jsxs as jsxs22 } from "react/jsx-runtime";
var AcceptedRangesPreview = ({ ranges }) => /* @__PURE__ */ jsx34("div", { className: root11, children: ranges.map((range2) => /* @__PURE__ */ jsxs22("div", { className: rangeRow, children: [
  "From",
  /* @__PURE__ */ jsx34("div", { className: range, children: /* @__PURE__ */ jsx34(Text25, { kind: "bodyMd", color: "functionalSuccess", children: formatNumber(range2.start) }) }),
  "To",
  /* @__PURE__ */ jsx34("div", { className: range, children: /* @__PURE__ */ jsx34(Text25, { kind: "bodyMd", color: "functionalSuccess", children: formatNumber(range2.end) }) })
] }, range2.identifier)) });

// src/multiformat/question-preview/interaction-preview/text-interaction-previews/range-text-entry-interaction-preview.tsx
import { Fragment as Fragment11, jsx as jsx35, jsxs as jsxs23 } from "react/jsx-runtime";
function RangeTextEntryInteractionPreview(props) {
  const { field, mode, outcome, rangeAnswer, fieldLabel, printView } = props;
  const withLabel = fieldLabel !== null;
  if (mode === "response") {
    return /* @__PURE__ */ jsx35("div", { className: root9, children: /* @__PURE__ */ jsxs23("div", { className: blankRoot2, children: [
      withLabel && /* @__PURE__ */ jsx35("div", { className: labelWrapper2, children: /* @__PURE__ */ jsxs23(
        Text26,
        {
          className: blankLabel2,
          kind: "bodyMd",
          color: "neutralBlack300",
          children: [
            `${fieldLabel}.`,
            " "
          ]
        }
      ) }),
      /* @__PURE__ */ jsx35(AcceptedRangesPreview, { ranges: field.response.correct_ranges ?? [] })
    ] }, field.identifier) });
  }
  switch (mode) {
    case "question":
      return /* @__PURE__ */ jsxs23(Fragment11, { children: [
        /* @__PURE__ */ jsx35(OptionList.Item, { children: /* @__PURE__ */ jsx35(Text26, { kind: "bodyMd", color: "navy700", children: !printView ? "Add your answer" : "" }) }, `${field.identifier}-start`),
        /* @__PURE__ */ jsx35(Text26, { kind: "bodyMd", style: { margin: "0 5px" }, children: "to" }),
        /* @__PURE__ */ jsx35(OptionList.Item, { children: /* @__PURE__ */ jsx35(Text26, { kind: "bodyMd", color: "navy700", children: !printView ? "Add your answer" : "" }) }, `${field.identifier}-end`)
      ] });
    case "answer": {
      return /* @__PURE__ */ jsxs23(Fragment11, { children: [
        /* @__PURE__ */ jsx35(OptionList.Item, { children: /* @__PURE__ */ jsx35(Text26, { kind: "bodyMd", color: "navy700", children: rangeAnswer?.[0] ? formatNumberString(rangeAnswer[0]) : "" }) }, `${field.identifier}-start`),
        /* @__PURE__ */ jsx35(Text26, { kind: "bodyMd", style: { margin: "0 5px" }, children: "to" }),
        /* @__PURE__ */ jsx35(OptionList.Item, { children: /* @__PURE__ */ jsx35(Text26, { kind: "bodyMd", color: "navy700", children: rangeAnswer?.[1] ? formatNumberString(rangeAnswer[1]) : "" }) }, `${field.identifier}-end`)
      ] });
    }
    case "marked":
    case "feedback": {
      const automarkScore = outcome?.automark_score ?? null;
      const markedCorrect = automarkScore !== null ? automarkScore > 0 : null;
      let colorVariant = "grey300";
      let icon3 = void 0;
      if (markedCorrect === true) {
        icon3 = /* @__PURE__ */ jsx35(
          CheckRoundIcon10,
          {
            color: colors13.success,
            label: "Marked correct",
            size: 16
          }
        );
        colorVariant = "functionalSuccess";
      } else if (markedCorrect === false) {
        icon3 = /* @__PURE__ */ jsx35(CloseRoundIcon10, { label: "Marked incorrect", size: 16 });
        colorVariant = "functionalFail";
      }
      const bottomAction2 = markedCorrect === false && /* @__PURE__ */ jsxs23(Text26, { kind: "bodySm", color: "neutralgrey500", children: [
        "Accepted Answer:",
        " ",
        /* @__PURE__ */ jsx35("span", { className: acceptedPhrases2, children: field.response.correct_ranges?.map(
          (range2) => `${formatNumber(range2.start)} to ${formatNumber(range2.end)}`
        ).join(" | ") })
      ] });
      return /* @__PURE__ */ jsxs23(Fragment11, { children: [
        /* @__PURE__ */ jsx35(Text26, { kind: "bodyMd", style: { margin: "0 5px" }, children: "From" }),
        /* @__PURE__ */ jsx35(
          OptionList.Item,
          {
            colorVariant,
            icon: icon3,
            withLabel,
            children: /* @__PURE__ */ jsx35(Text26, { kind: "bodyMd", color: "navy700", children: rangeAnswer?.[0] ? formatNumberString(rangeAnswer[0]) : "" })
          },
          `${field.identifier}-start`
        ),
        /* @__PURE__ */ jsx35(Text26, { kind: "bodyMd", style: { margin: "0 5px" }, children: "to" }),
        /* @__PURE__ */ jsx35(
          OptionList.Item,
          {
            colorVariant,
            icon: icon3,
            bottomAction: bottomAction2,
            children: /* @__PURE__ */ jsx35(Text26, { kind: "bodyMd", color: "navy700", children: rangeAnswer?.[1] ? formatNumberString(rangeAnswer[1]) : "" })
          },
          `${field.identifier}-end`
        )
      ] });
    }
  }
}

// src/multiformat/question-preview/interaction-preview/video-interaction-preview/video-interaction-preview.tsx
import { PlayIcon } from "@vericus/cadmus-icons";
import { Text as Text27 } from "@vericus/cadmus-ui";

// src/multiformat/question-preview/interaction-preview/video-interaction-preview/video-interaction-preview.css.ts
var description = "maef165";
var play = "maef164";
var requirement = "maef160";
var requirements = "maef161";
var response = "maef162";
var thumbnail = "maef163";
var transcript = "maef167";
var transcriptHeading = "maef166";
var transcriptSegment = "maef168";
var transcriptSegmentTimestamp = "maef169";
var unavailableNotice = "maef16a";

// src/multiformat/question-preview/interaction-preview/video-interaction-preview/video-interaction-preview.tsx
import { jsx as jsx36, jsxs as jsxs24 } from "react/jsx-runtime";
function formatTime(seconds) {
  if (seconds === null || seconds === void 0) return "Not set";
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
}
function VideoInteractionPreview(props) {
  const { field, answerVideo, dialogueId } = props;
  if (field.interaction.kind !== "socratic_interaction") {
    return null;
  }
  const { response_time, reading_time, max_attempts } = field.interaction;
  const showRequirements = props.mode === "question" || props.mode === "response";
  return /* @__PURE__ */ jsxs24("div", { style: { padding: "16px 45px" }, children: [
    showRequirements && /* @__PURE__ */ jsxs24("div", { className: requirements, children: [
      /* @__PURE__ */ jsxs24("div", { className: requirement, children: [
        /* @__PURE__ */ jsx36(
          Text27,
          {
            kind: "bodyMd",
            color: "navy700",
            as: "span",
            style: { minWidth: "140px" },
            children: "Response time:"
          }
        ),
        /* @__PURE__ */ jsx36(Text27, { kind: "bodyMd", color: "navy500", as: "span", children: formatTime(response_time) })
      ] }),
      /* @__PURE__ */ jsxs24("div", { className: requirement, children: [
        /* @__PURE__ */ jsx36(
          Text27,
          {
            kind: "bodyMd",
            color: "navy700",
            as: "span",
            style: { minWidth: "140px" },
            children: "Preparation time:"
          }
        ),
        /* @__PURE__ */ jsx36(Text27, { kind: "bodyMd", color: "navy500", as: "span", children: formatTime(reading_time) })
      ] }),
      /* @__PURE__ */ jsxs24("div", { className: requirement, children: [
        /* @__PURE__ */ jsx36(
          Text27,
          {
            kind: "bodyMd",
            color: "navy700",
            as: "span",
            style: { minWidth: "140px" },
            children: "Attempts allowed:"
          }
        ),
        /* @__PURE__ */ jsx36(Text27, { kind: "bodyMd", color: "navy500", as: "span", children: max_attempts ?? "Not set" })
      ] })
    ] }),
    answerVideo != null && answerVideo.url != null && /* @__PURE__ */ jsx36(VideoResponse, { url: answerVideo.url, duration: answerVideo.duration }),
    answerVideo?.url != null && answerVideo.transcript_segments != null && answerVideo.transcript_segments.length > 0 && /* @__PURE__ */ jsx36(Transcript, { segments: answerVideo.transcript_segments }),
    dialogueId != null && (answerVideo == null || answerVideo.url == null) && /* @__PURE__ */ jsx36("div", { className: unavailableNotice, children: /* @__PURE__ */ jsx36(Text27, { kind: "bodyMd", color: "navy500", children: "The student has submitted a response, but the video is not available." }) })
  ] });
}
function VideoResponse(props) {
  return /* @__PURE__ */ jsxs24("div", { className: response, children: [
    /* @__PURE__ */ jsx36("div", { className: thumbnail, children: /* @__PURE__ */ jsx36(
      "a",
      {
        href: props.url,
        target: "_blank",
        rel: "noopener noreferrer",
        className: play,
        children: /* @__PURE__ */ jsx36(PlayIcon, { size: 36, label: "Submitted video response" })
      }
    ) }),
    /* @__PURE__ */ jsxs24("div", { className: description, children: [
      /* @__PURE__ */ jsx36(Text27, { kind: "headingSix", children: "Video response" }),
      props.duration != null && /* @__PURE__ */ jsxs24(Text27, { kind: "label", children: [
        "DURATION ",
        formatSecondsAsTimer(props.duration)
      ] }),
      /* @__PURE__ */ jsx36("div", { children: /* @__PURE__ */ jsx36(
        "a",
        {
          href: props.url,
          target: "_blank",
          rel: "noopener noreferrer",
          style: { textDecoration: "none" },
          children: /* @__PURE__ */ jsx36(
            Text27,
            {
              kind: "bodyMd",
              color: "navy500",
              style: {
                textDecoration: "underline dashed",
                textUnderlineOffset: "4px"
              },
              children: "Watch video"
            }
          )
        }
      ) })
    ] })
  ] });
}
function Transcript(props) {
  return /* @__PURE__ */ jsxs24("div", { className: transcript, children: [
    /* @__PURE__ */ jsx36(
      Text27,
      {
        kind: "headingSix",
        color: "navy700",
        as: "h6",
        className: transcriptHeading,
        children: "Transcript"
      }
    ),
    props.segments.map((segment, index) => /* @__PURE__ */ jsxs24("div", { className: transcriptSegment, children: [
      /* @__PURE__ */ jsxs24(
        Text27,
        {
          kind: "bodyMd",
          color: "navy700",
          as: "span",
          className: transcriptSegmentTimestamp,
          children: [
            "[",
            formatSecondsAsTimer(segment.start_time ?? 0),
            "]"
          ]
        }
      ),
      /* @__PURE__ */ jsx36(Text27, { kind: "bodyMd", color: "navy500", as: "span", children: segment.text })
    ] }, index))
  ] });
}

// src/multiformat/question-preview/interaction-preview/interaction-preview.tsx
import { jsx as jsx37 } from "react/jsx-runtime";
function InteractionPreview(props) {
  const fields = props.data.fields ?? [];
  const outcomes = props.data.field_outcomes ?? [];
  const firstField = fields[0];
  const questionType = props.data.question_type;
  if (firstField === void 0) {
    return null;
  }
  switch (questionType) {
    case "extended": {
      const word_limit = firstField.interaction.kind === "extended_interaction" ? firstField.interaction.word_limit : null;
      const printLabel = word_limit ? `extended answer - ${word_limit} words` : "extended answer - separate answer sheet";
      if (props.printView) {
        return /* @__PURE__ */ jsx37(Text28, { kind: "label", style: { paddingLeft: 45 }, children: printLabel });
      }
      return /* @__PURE__ */ jsx37(
        ExtendedInteractionPreview,
        {
          id: firstField.identifier,
          wordLimit: word_limit,
          answerDoc: props.data.answer?.answer_doc ?? null,
          referencesDoc: props.data.references_answer?.answer_doc ?? null,
          extensions: props.extensions
        }
      );
    }
    case "matching": {
      const answer2 = getAnswerChoiceIds(
        questionType,
        props.data.answer,
        firstField.identifier
      );
      const outcome = outcomes.find(
        (outcome2) => outcome2.identifier === firstField.identifier
      );
      return /* @__PURE__ */ jsx37(
        MatchingInteractionPreview,
        {
          mode: props.mode,
          field: firstField,
          outcome: outcome ?? null,
          answer: answer2 ?? null,
          maxScore: props.data.max_score,
          printView: props.printView
        },
        firstField.identifier
      );
    }
    case "truefalse":
    case "mcq": {
      const answerChoiceIds = getAnswerChoiceIds(
        questionType,
        props.data.answer,
        firstField.identifier
      );
      return /* @__PURE__ */ jsx37(
        ChoiceInteractionPreview,
        {
          mode: props.mode,
          shuffleSeed: choiceShuffleSeed(props.data),
          field: firstField,
          outcome: outcomes.find(
            (outcome) => outcome.identifier === firstField.identifier
          ) ?? null,
          answerChoiceIds,
          maxScore: props.data.max_score
        }
      );
    }
    case "numerical": {
      const answer2 = getShortAnswer(props.data.answer, firstField.identifier);
      const rangeAnswer = getRangeAnswer(
        props.data.answer,
        firstField.identifier
      );
      const outcome = outcomes.find(
        (outcome2) => outcome2.identifier === firstField.identifier
      );
      const baseType = firstField.interaction.kind === "text_entry_interaction" && firstField.interaction.base_type || firstField.response.base_type || null;
      if (baseType === "range") {
        return /* @__PURE__ */ jsx37(OptionList.List, { children: /* @__PURE__ */ jsx37(
          RangeTextEntryInteractionPreview,
          {
            mode: props.mode,
            field: firstField,
            outcome: outcome ?? null,
            rangeAnswer,
            fieldLabel: null,
            printView: props.printView
          }
        ) });
      }
      return /* @__PURE__ */ jsx37(OptionList.List, { children: /* @__PURE__ */ jsx37(
        NumericTextEntryInteractionPreview,
        {
          mode: props.mode,
          field: firstField,
          outcome: outcome ?? null,
          answer: answer2,
          fieldLabel: null,
          printView: props.printView
        }
      ) });
    }
    case "short": {
      const answer2 = getShortAnswer(props.data.answer, firstField.identifier);
      const outcome = outcomes.find(
        (outcome2) => outcome2.identifier === firstField.identifier
      );
      return /* @__PURE__ */ jsx37(OptionList.List, { children: /* @__PURE__ */ jsx37(
        TextEntryInteractionPreview,
        {
          mode: props.mode,
          field: firstField,
          outcome: outcome ?? null,
          answer: answer2,
          fieldLabel: null,
          printView: props.printView
        }
      ) });
    }
    case "blanks": {
      const interactions = fields.map((field, index) => {
        const answer2 = getAnswerForBlank(props.data.answer, field.identifier);
        const outcome = outcomes.find(
          (outcome2) => outcome2.identifier === field.identifier
        );
        if (field.interaction.kind === "choice_interaction") {
          return /* @__PURE__ */ jsx37(
            InlineChoiceInteractionPreview,
            {
              mode: props.mode,
              shuffleSeed: choiceShuffleSeed(props.data),
              questionType,
              field,
              printView: props.printView,
              outcome: outcome ?? null,
              answerChoiceId: answer2 ?? null,
              fieldLabel: index + 1
            },
            field.identifier
          );
        } else {
          return /* @__PURE__ */ jsx37(
            TextEntryInteractionPreview,
            {
              mode: props.mode,
              field,
              outcome: outcome ?? null,
              answer: answer2,
              fieldLabel: index + 1,
              printView: props.printView
            },
            field.identifier
          );
        }
      });
      return /* @__PURE__ */ jsx37(OptionList.List, { children: interactions });
    }
    case "hotspot": {
      return /* @__PURE__ */ jsx37(
        HotspotInteractionPreview,
        {
          promptImage: props.data.prompt_image ?? null,
          snapshot: props.data.answer,
          fields,
          mode: props.mode,
          outcomes,
          printView: props.printView
        }
      );
    }
    case "video": {
      return /* @__PURE__ */ jsx37(
        VideoInteractionPreview,
        {
          field: firstField,
          dialogueId: props.data.answer?.answer_dialogue_id ?? null,
          answerVideo: props.data.answer?.answer_video ?? null,
          mode: props.mode
        }
      );
    }
    default:
      return null;
  }
}
function getAnswerForBlank(snapshot, identifier) {
  if (snapshot?.answer_fields) {
    const answer2 = snapshot.answer_fields[identifier];
    return answer2 && typeof answer2[0] === "string" ? answer2[0] : null;
  }
  return snapshot?.answer_blanks ? snapshot.answer_blanks[identifier] ?? null : null;
}
function getShortAnswer(snapshot, identifier) {
  if (snapshot?.answer_fields) {
    const answer2 = snapshot.answer_fields[identifier];
    return answer2 && typeof answer2[0] === "string" ? answer2[0] : null;
  }
  return snapshot?.answer_short ?? null;
}
function getRangeAnswer(snapshot, identifier) {
  if (snapshot?.answer_fields) {
    const answer2 = snapshot.answer_fields[identifier];
    const rangeStart = answer2 && typeof answer2[0] === "string" ? answer2[0] : null;
    const rangeEnd = answer2 && typeof answer2[1] === "string" ? answer2[1] : null;
    return [rangeStart, rangeEnd];
  }
  return [null, null];
}
function getAnswerChoiceIds(questionType, snapshot, identifier) {
  if (snapshot?.answer_fields) {
    const values = snapshot.answer_fields[identifier];
    return values ?? [];
  }
  switch (questionType) {
    case "truefalse": {
      const answer2 = snapshot?.answer_boolean;
      return typeof answer2 === "boolean" ? [answer2.toString()] : [];
    }
    case "mcq":
      return snapshot?.answer_choice_ids ?? [];
    case "matching":
      return [];
  }
}
function choiceShuffleSeed(data) {
  if (data.question_type === "truefalse") return null;
  return data.shuffle_seed ?? null;
}

// src/multiformat/question-preview/question-preview-next.tsx
import { Fragment as Fragment12, jsx as jsx38, jsxs as jsxs25 } from "react/jsx-runtime";
function QuestionPreviewNext(props) {
  const {
    questionId,
    promptDoc,
    questionTitle,
    bookmarked = false,
    feedback: feedback2,
    withHeader = true,
    withPromptDoc = true,
    promptEditor,
    isSubQuestion,
    data,
    mode = "question",
    withBookmark = false,
    onUpdateBookmark,
    extensions,
    withFeedbackComments = false,
    automarkDisplay,
    feedbackComments,
    citerionOutcomes,
    onExpandFeedback,
    blockAttachments,
    attachmentEnabled,
    onRubricClick,
    ...divProps
  } = props;
  const fields = data?.fields ?? [];
  const matchSimilarity = fields.find(
    (field) => field.response.match_similarity !== null
  )?.response.match_similarity;
  const errorMargin = fields.find(
    (field) => field.response.error_margin !== null
  )?.response.error_margin;
  const partialScoring = fields.find(
    (field) => field.response.partial_scoring !== null
  )?.response.partial_scoring;
  const caseSensitive = fields.find((field) => field.response.case_sensitive)?.response.case_sensitive;
  const maxScore = data?.max_score ?? void 0;
  const score = data?.question_outcome?.score ?? void 0;
  const autoMarked = typeof data?.question_outcome?.automark_score === "number";
  const manualMarked = score !== void 0 && autoMarked === false;
  return /* @__PURE__ */ jsxs25("div", { id: divProps?.id, className: clsx5(root6, divProps.className), children: [
    withHeader && /* @__PURE__ */ jsx38("div", { className: content, children: /* @__PURE__ */ jsx38(
      QuestionHeaderPreview,
      {
        questionTitle,
        bookmarked,
        points: maxScore,
        markedPoints: score,
        withMarking: mode === "marked",
        showPoints: mode !== "feedback",
        isSubQuestion,
        automarked: autoMarked,
        manualMarked,
        withBookmark,
        onRubricClick,
        onToggleBookmark: () => {
          if (!onUpdateBookmark) return;
          onUpdateBookmark(!bookmarked);
        }
      }
    ) }),
    withPromptDoc && /* @__PURE__ */ jsxs25(Fragment12, { children: [
      /* @__PURE__ */ jsx38(Spacer4, { spacing: 8 }),
      /* @__PURE__ */ jsx38(
        EditorPreviewer,
        {
          id: questionId,
          promptDoc,
          promptEditor,
          enableBlanks: true,
          enableFootnoting: true,
          extensions
        }
      )
    ] }),
    data !== null && fields.length > 0 && /* @__PURE__ */ jsxs25(Fragment12, { children: [
      /* @__PURE__ */ jsx38(Spacer4, { spacing: 8 }),
      /* @__PURE__ */ jsxs25("div", { children: [
        mode === "marked" && requiresManualMarking(data.question_type) && citerionOutcomes && citerionOutcomes.length > 0 && typeof score === "number" && typeof maxScore === "number" && /* @__PURE__ */ jsx38(
          MarkerRubric,
          {
            citerionOutcomes,
            markedScore: { score, maxScore }
          }
        ),
        /* @__PURE__ */ jsx38(
          InteractionPreview,
          {
            mode,
            data,
            extensions
          }
        )
      ] })
    ] }),
    (mode === "marked" || mode === "feedback") && feedback2 && autoMarked && /* @__PURE__ */ jsxs25(Fragment12, { children: [
      /* @__PURE__ */ jsx38(Spacer4, { spacing: 16 }),
      /* @__PURE__ */ jsx38("div", { className: content, children: feedback2 && /* @__PURE__ */ jsx38(Feedback, { feedback: feedback2 }) })
    ] }),
    automarkDisplay === "inline" && /* @__PURE__ */ jsx38(
      AutomarkConditionsInline,
      {
        matchSimilarity,
        errorMargin
      }
    ),
    (data?.question_type === "extended" || data?.question_type === "short") && attachmentEnabled === true && blockAttachments !== void 0 && blockAttachments.length > 0 && /* @__PURE__ */ jsxs25(Fragment12, { children: [
      /* @__PURE__ */ jsx38(Spacer4, { spacing: 16 }),
      /* @__PURE__ */ jsx38(BlockAttachments, { attachments: blockAttachments })
    ] }),
    automarkDisplay === "badge" && /* @__PURE__ */ jsxs25(Fragment12, { children: [
      /* @__PURE__ */ jsx38(Spacer4, { spacing: 16 }),
      /* @__PURE__ */ jsx38(
        AutomarkBadge,
        {
          matchSimilarity,
          errorMargin,
          partialScoring,
          caseSensitive
        }
      )
    ] }),
    withFeedbackComments && feedbackComments && feedbackComments.length > 0 && !onExpandFeedback && /* @__PURE__ */ jsxs25(Fragment12, { children: [
      /* @__PURE__ */ jsx38(Spacer4, { spacing: 16 }),
      /* @__PURE__ */ jsx38("div", { className: feedbackContent, children: feedbackComments.map((comment, index) => /* @__PURE__ */ jsx38(
        CommentBox,
        {
          ...comment,
          index: index + 1,
          criterion_outcomes: citerionOutcomes
        },
        comment.id
      )) })
    ] }),
    onExpandFeedback && data && requiresManualMarking(data.question_type) && /* @__PURE__ */ jsxs25(Fragment12, { children: [
      /* @__PURE__ */ jsx38(Spacer4, { spacing: 16 }),
      /* @__PURE__ */ jsx38("div", { className: content, children: /* @__PURE__ */ jsx38(
        FeedbackViewerButton,
        {
          commentCount: feedbackComments ? feedbackComments.length : null,
          onExpandFeedback
        }
      ) })
    ] })
  ] });
}
var AutomarkBadge = (props) => {
  const { matchSimilarity, errorMargin, caseSensitive, partialScoring } = props;
  if (!matchSimilarity && !errorMargin && !caseSensitive && (partialScoring === null || partialScoring === void 0))
    return null;
  const getSubstring = () => {
    const parts = [];
    if (matchSimilarity) {
      parts.push(matchSimilarity === 1 ? "Exact Match" : "Close enough");
    }
    if (errorMargin) {
      parts.push("Error Margin");
    }
    if (caseSensitive) {
      parts.push("Case Sensitive");
    }
    if (partialScoring !== null && partialScoring !== void 0) {
      parts.push(partialScoring ? "Partial Marks" : "All or nothing");
    }
    return parts.length ? ` - ${parts.join(" | ")}` : "";
  };
  return /* @__PURE__ */ jsxs25("div", { className: accordion, children: [
    /* @__PURE__ */ jsxs25("div", { className: accordionTitle, children: [
      /* @__PURE__ */ jsx38(Cog6ToothIcon, { label: "Auto-marking conditions", size: 24 }),
      /* @__PURE__ */ jsxs25(Text29, { kind: "headingSix", style: { margin: 0 }, inline: true, as: "div", children: [
        "Auto-marking conditions ",
        getSubstring()
      ] })
    ] }),
    /* @__PURE__ */ jsx38(Pill3, { basic: true, color: "#F6E4ED", textColor: "#604", children: "Not visible to students" })
  ] });
};
var AutomarkConditionsInline = (props) => {
  const { matchSimilarity, errorMargin } = props;
  if (!matchSimilarity && !errorMargin) return null;
  return /* @__PURE__ */ jsxs25(Fragment12, { children: [
    /* @__PURE__ */ jsx38(Spacer4, { spacing: 36 }),
    /* @__PURE__ */ jsxs25("div", { style: { textAlign: "right", marginRight: 45 }, children: [
      /* @__PURE__ */ jsxs25(Text29, { kind: "bodySm", color: "neutralgrey500", as: "span", children: [
        "Automark Conditions:",
        " "
      ] }),
      matchSimilarity ? /* @__PURE__ */ jsxs25(Text29, { kind: "bodySm", color: "neutralBlack500", as: "span", children: [
        Math.round(matchSimilarity * 100),
        "% Similarity"
      ] }) : errorMargin ? /* @__PURE__ */ jsxs25(Text29, { kind: "bodySm", color: "neutralBlack500", as: "span", children: [
        Math.round(errorMargin),
        "% Margin of Error"
      ] }) : null
    ] })
  ] });
};

// src/multiformat/question-preview/question-print-preview.tsx
import { Spacer as Spacer5 } from "@vericus/cadmus-ui";
import clsx6 from "clsx";
import { Fragment as Fragment13, jsx as jsx39, jsxs as jsxs26 } from "react/jsx-runtime";
function QuestionPrintPreview(props) {
  const {
    questionId,
    promptDoc,
    questionTitle,
    bookmarked = false,
    withHeader = true,
    withPromptDoc = true,
    promptEditor,
    isSubQuestion,
    data,
    mode = "question",
    withBookmark = false,
    onUpdateBookmark,
    extensions,
    ...divProps
  } = props;
  const fields = data?.fields ?? [];
  const maxScore = data?.max_score ?? void 0;
  const score = data?.question_outcome?.score ?? void 0;
  const autoMarked = typeof data?.question_outcome?.automark_score === "number";
  const manualMarked = score !== void 0 && autoMarked === false;
  return /* @__PURE__ */ jsxs26("div", { id: divProps?.id, className: clsx6(root6, divProps.className), children: [
    withHeader && /* @__PURE__ */ jsx39("div", { className: content, children: /* @__PURE__ */ jsx39(
      QuestionHeaderPreview,
      {
        questionTitle,
        bookmarked,
        points: maxScore,
        markedPoints: score,
        withMarking: mode === "marked",
        showPoints: mode !== "feedback",
        isSubQuestion,
        automarked: autoMarked,
        manualMarked,
        withBookmark,
        onToggleBookmark: () => {
          if (!onUpdateBookmark) return;
          onUpdateBookmark(!bookmarked);
        }
      }
    ) }),
    withPromptDoc && /* @__PURE__ */ jsxs26(Fragment13, { children: [
      /* @__PURE__ */ jsx39(Spacer5, { spacing: 8 }),
      /* @__PURE__ */ jsx39(
        EditorPreviewer,
        {
          id: questionId,
          promptDoc,
          promptEditor,
          enableBlanks: true,
          extensions
        }
      )
    ] }),
    data !== null && fields.length > 0 && /* @__PURE__ */ jsxs26(Fragment13, { children: [
      /* @__PURE__ */ jsx39(Spacer5, { spacing: 8 }),
      /* @__PURE__ */ jsx39("div", { children: /* @__PURE__ */ jsx39(
        InteractionPreview,
        {
          mode,
          data,
          extensions,
          printView: true
        }
      ) })
    ] })
  ] });
}

// src/multiformat/question-preview/static-preview-extensions.ts
import { CadmusKit } from "@vericus/cadmus-editor-prosemirror";
var STATIC_PREVIEW_EXTENSIONS = [
  // Read-only surface, no paste risk: always render saved font marks.
  CadmusKit.configure({
    editable: false,
    enableBlanks: true,
    enableFootnoting: true,
    enableFontControls: true
  })
];

// src/multiformat/work-manifest-preview/work-manifest-mfa-preview.tsx
import { Spacer as Spacer7 } from "@vericus/cadmus-ui";

// src/components/cover-page-header/cover-page-header.tsx
import { QuillPenIcon } from "@vericus/cadmus-icons";
import { Divider as Divider2, Text as Text30 } from "@vericus/cadmus-ui";

// src/components/cover-page-header/cover-page-header.css.ts
var container3 = "jmto4g0";
var detailRow = "jmto4g3";
var icon2 = "jmto4g1";
var rowLabel = "jmto4g4";
var rowValue = "jmto4g5";
var submissionDetails = "jmto4g2";

// src/components/cover-page-header/cover-page-header.tsx
import { jsx as jsx40, jsxs as jsxs27 } from "react/jsx-runtime";
function CoverPageHeader(props) {
  const { assessmentName, subject, feedbackTitle, details } = props;
  return /* @__PURE__ */ jsxs27("div", { className: container3, children: [
    /* @__PURE__ */ jsx40(QuillPenIcon, { label: "", size: 48, className: icon2 }),
    /* @__PURE__ */ jsx40(Text30, { kind: "displayThree", textAlign: "left", children: assessmentName }),
    subject && /* @__PURE__ */ jsx40(Text30, { kind: "label", textAlign: "left", children: subject }),
    /* @__PURE__ */ jsx40(Text30, { kind: "headingFour", textAlign: "left", children: feedbackTitle }),
    /* @__PURE__ */ jsx40(Divider2, {}),
    /* @__PURE__ */ jsx40("div", { className: submissionDetails, children: details.filter((d) => d.show).map((d) => /* @__PURE__ */ jsxs27("div", { className: detailRow, children: [
      /* @__PURE__ */ jsx40(Text30, { kind: "label", textAlign: "right", className: rowLabel, children: d.label }),
      /* @__PURE__ */ jsx40(Text30, { kind: "label", color: "black", className: rowValue, children: d.value })
    ] }, d.label)) })
  ] });
}

// src/components/question-feedback-summary/question-feedback-summary.tsx
import { styled } from "styled-components";
import { InfoUnfilledIcon } from "@vericus/cadmus-icons";
import { colors as colors14, Divider as Divider3, Pill as Pill4, Spacer as Spacer6, Text as Text31 } from "@vericus/cadmus-ui";

// src/components/question-feedback-summary/question-feedback-summary.css.ts
var container4 = "vim1ji0";
var containerRow = "vim1ji1";

// src/components/question-feedback-summary/question-feedback-summary.tsx
import { Fragment as Fragment14, jsx as jsx41, jsxs as jsxs28 } from "react/jsx-runtime";
function QuestionFeedbackSummary(props) {
  const { score, finalScore, maxScore, sections, penalty, moderatorModifier } = props;
  const hasModifier = moderatorModifier !== 0;
  const markerScore = hasModifier ? score - moderatorModifier : score;
  const topLabel = hasModifier ? "Marker score" : "Total points";
  return /* @__PURE__ */ jsxs28("div", { className: container4, children: [
    /* @__PURE__ */ jsxs28("div", { className: containerRow, children: [
      /* @__PURE__ */ jsx41(Text31, { kind: "headingFive", children: topLabel }),
      /* @__PURE__ */ jsxs28(Text31, { kind: "headingFive", as: "span", bold: true, children: [
        formatDecimalNumber(markerScore),
        "/",
        maxScore,
        " pts"
      ] })
    ] }),
    sections && sections.map((part) => /* @__PURE__ */ jsx41(
      PartRow,
      {
        sectionLabel: part.sectionLabel,
        minQuestion: part.minQuestion,
        maxQuestion: part.maxQuestion,
        score: part.score,
        maxScore: part.maxScore
      },
      part.sectionLabel
    )),
    /* @__PURE__ */ jsx41(Spacer6, { spacing: 16 }),
    hasModifier && /* @__PURE__ */ jsx41(ModeratorAdjustmentRow, { adjustment: moderatorModifier }),
    /* @__PURE__ */ jsx41(PenaltiesRow, { penalties: penalty }),
    /* @__PURE__ */ jsx41(TotalScoreDivider, {}),
    /* @__PURE__ */ jsxs28("div", { className: containerRow, children: [
      /* @__PURE__ */ jsx41(Text31, { kind: "headingThree", children: "Total score" }),
      /* @__PURE__ */ jsxs28(Text31, { kind: "headingThree", as: "span", bold: true, children: [
        formatDecimalNumber(finalScore ?? score - penalty),
        "/",
        maxScore,
        " pts"
      ] })
    ] })
  ] });
}
function PartRow(props) {
  return /* @__PURE__ */ jsxs28(Fragment14, { children: [
    /* @__PURE__ */ jsx41(Spacer6, { spacing: 16 }),
    /* @__PURE__ */ jsxs28("div", { className: containerRow, children: [
      /* @__PURE__ */ jsx41(Pill4, { color: colors14.lilac200, basic: true, children: /* @__PURE__ */ jsx41(Text31, { kind: "label", as: "div", bold: true, children: props.sectionLabel }) }),
      /* @__PURE__ */ jsxs28(Text31, { kind: "bodySm", as: "div", children: [
        "Q",
        props.minQuestion,
        " to Q",
        props.maxQuestion
      ] }),
      /* @__PURE__ */ jsx41(StyledDivider, {}),
      /* @__PURE__ */ jsxs28(Text31, { kind: "bodySm", as: "div", children: [
        formatDecimalNumber(props.score),
        "/",
        props.maxScore,
        " pts"
      ] })
    ] })
  ] });
}
function ModeratorAdjustmentRow({ adjustment }) {
  return /* @__PURE__ */ jsxs28("div", { className: containerRow, children: [
    /* @__PURE__ */ jsx41(StyledText, { kind: "bodySm", color: "navy400", children: "Adjusted points" }),
    /* @__PURE__ */ jsx41(Pill4, { color: colors14.lilac200, basic: true, children: /* @__PURE__ */ jsxs28(Text31, { kind: "bodySm", as: "span", bold: true, children: [
      adjustment > 0 ? "+" : "",
      formatDecimalNumber(adjustment),
      " points"
    ] }) })
  ] });
}
function PenaltiesRow({ penalties }) {
  return /* @__PURE__ */ jsxs28("div", { className: containerRow, children: [
    /* @__PURE__ */ jsxs28(StyledText, { kind: "bodySm", color: "navy400", children: [
      "Penalties applied",
      /* @__PURE__ */ jsx41(InfoUnfilledIcon, { size: 20, label: "" })
    ] }),
    /* @__PURE__ */ jsx41(Pill4, { color: colors14.poppy200, basic: true, children: /* @__PURE__ */ jsxs28(Text31, { kind: "bodySm", as: "span", bold: true, children: [
      penalties,
      " points"
    ] }) })
  ] });
}
var StyledDivider = styled(Divider3)`
  max-width: 70%;
`;
var TotalScoreDivider = styled(Divider3)`
  margin-top: 16px;
  background-color: ${colors14.black100};
`;
var StyledText = styled(Text31)`
  display: flex;
  align-items: center;
`;

// src/components/question-outcome-summary/question-outcome-summary.tsx
import { styled as styled2 } from "styled-components";
import { colors as colors15, Divider as Divider4, Text as Text32 } from "@vericus/cadmus-ui";

// src/components/question-outcome-summary/question-outcome-summary.css.ts
var autoMarkedContainer = "_1wavoi22";
var container5 = "_1wavoi20";
var containerRow2 = "_1wavoi21";
var row2 = "_1wavoi23";
var totalScoreContainer = "_1wavoi24";

// src/components/question-outcome-summary/question-outcome-summary.tsx
import { Fragment as Fragment15, jsx as jsx42, jsxs as jsxs29 } from "react/jsx-runtime";
function QuestionOutcomeSummary(props) {
  return /* @__PURE__ */ jsxs29("div", { className: container5, children: [
    /* @__PURE__ */ jsxs29("div", { className: containerRow2, children: [
      /* @__PURE__ */ jsx42(
        AutoMarkedSummary,
        {
          autoMarkedTotalQuestionsCount: props.autoMarkedTotalQuestionsCount,
          autoMarkedMaxScore: props.autoMarkedMaxScore,
          automarkedScore: props.automarkedScore
        }
      ),
      /* @__PURE__ */ jsx42(
        ManualMarkedSummary,
        {
          totalManuallyMarkedQuestionsCount: props.totalManuallyMarkedQuestionsCount,
          manuallyMarkedMaxScore: props.manuallyMarkedMaxScore,
          manuallyYetToBeMarkedQuestions: props.manuallyYetToBeMarkedQuestions,
          manuallyMarkedScore: props.manuallyMarkedScore
        }
      )
    ] }),
    /* @__PURE__ */ jsx42("div", { children: /* @__PURE__ */ jsx42(
      TotalScoreSummary,
      {
        automarkedScore: props.automarkedScore,
        maxScore: props.maxScore,
        totalQuestionsCount: props.totalQuestionsCount
      }
    ) })
  ] });
}
function AutoMarkedSummary(props) {
  return /* @__PURE__ */ jsxs29("div", { className: autoMarkedContainer, children: [
    /* @__PURE__ */ jsxs29("div", { children: [
      /* @__PURE__ */ jsx42(Text32, { kind: "headingFive", color: "navy400", children: "Auto-marked questions" }),
      /* @__PURE__ */ jsx42(Divider4, {}),
      /* @__PURE__ */ jsx42(
        Row,
        {
          label: "No. of questions",
          value: props.autoMarkedTotalQuestionsCount
        }
      ),
      /* @__PURE__ */ jsx42(Row, { label: "Maximum total points", value: props.autoMarkedMaxScore })
    ] }),
    /* @__PURE__ */ jsxs29("div", { children: [
      /* @__PURE__ */ jsx42(Row, { label: "Adjusted points", value: "_____" }),
      /* @__PURE__ */ jsx42(
        SummaryScoreRow,
        {
          scoreTitle: "Score",
          totalPts: `${formatDecimalNumber(props.automarkedScore)}/${props.autoMarkedMaxScore}`
        }
      )
    ] })
  ] });
}
function ManualMarkedSummary(props) {
  return /* @__PURE__ */ jsxs29("div", { className: autoMarkedContainer, children: [
    /* @__PURE__ */ jsxs29("div", { children: [
      /* @__PURE__ */ jsx42(Text32, { kind: "headingFive", color: "navy400", children: "Manually marked questions" }),
      /* @__PURE__ */ jsx42(Divider4, {}),
      /* @__PURE__ */ jsx42(
        Row,
        {
          label: "No. of questions",
          value: props.totalManuallyMarkedQuestionsCount
        }
      ),
      /* @__PURE__ */ jsx42(
        Row,
        {
          label: "Maximum total points",
          value: props.manuallyMarkedMaxScore
        }
      )
    ] }),
    /* @__PURE__ */ jsxs29("div", { children: [
      /* @__PURE__ */ jsx42(
        Row,
        {
          label: "Yet to be marked",
          value: props.manuallyYetToBeMarkedQuestions
        }
      ),
      /* @__PURE__ */ jsx42(
        SummaryScoreRow,
        {
          scoreTitle: "Score",
          totalPts: `____/${props.manuallyMarkedMaxScore}`
        }
      )
    ] })
  ] });
}
function TotalScoreSummary(props) {
  const { automarkedScore, maxScore, totalQuestionsCount } = props;
  return /* @__PURE__ */ jsxs29("div", { className: totalScoreContainer, children: [
    /* @__PURE__ */ jsx42("div", { children: /* @__PURE__ */ jsxs29(StyledHeading, { kind: "headingThree", children: [
      "Total Score ",
      /* @__PURE__ */ jsx42("br", {}),
      /* @__PURE__ */ jsxs29(Text32, { kind: "bodyMd", color: "navy400", as: "span", children: [
        "from all ",
        totalQuestionsCount,
        " questions"
      ] })
    ] }) }),
    /* @__PURE__ */ jsxs29("div", { children: [
      /* @__PURE__ */ jsx42(
        Row,
        {
          label: "Auto-marked points",
          value: formatDecimalNumber(automarkedScore)
        }
      ),
      /* @__PURE__ */ jsx42(Row, { label: "Adjusted points", value: `+ ______` }),
      /* @__PURE__ */ jsx42(Row, { label: "Manually marked", value: `+ ______` }),
      /* @__PURE__ */ jsx42(StyledDivider2, {}),
      /* @__PURE__ */ jsxs29("div", { style: { display: "flex", justifyContent: "space-between" }, children: [
        /* @__PURE__ */ jsx42(Text32, { kind: "headingThree", children: "Score" }),
        /* @__PURE__ */ jsxs29(Text32, { kind: "headingThree", bold: true, children: [
          `___ pts`,
          /* @__PURE__ */ jsxs29(Text32, { kind: "headingFive", color: "navy300", as: "span", children: [
            "/",
            maxScore
          ] })
        ] })
      ] })
    ] })
  ] });
}
function SummaryScoreRow({ scoreTitle, totalPts }) {
  return /* @__PURE__ */ jsxs29(Fragment15, { children: [
    /* @__PURE__ */ jsx42(StyledDivider2, {}),
    /* @__PURE__ */ jsxs29("div", { className: row2, children: [
      /* @__PURE__ */ jsx42(Text32, { kind: "headingFive", children: scoreTitle }),
      /* @__PURE__ */ jsxs29(Text32, { kind: "headingFive", as: "span", bold: true, children: [
        totalPts,
        " pts"
      ] })
    ] })
  ] });
}
function Row(props) {
  return /* @__PURE__ */ jsxs29(RowText, { kind: "bodySm", as: "div", children: [
    props.label,
    /* @__PURE__ */ jsx42(Text32, { kind: "bodySm", as: "span", bold: true, children: props.value })
  ] });
}
var RowText = styled2(Text32)`
  display: flex;
  justify-content: space-between;
  margin: 2px 0;
  color: ${colors15.navy400};
`;
var StyledDivider2 = styled2(Divider4)`
  background-color: ${colors15.navy400};
  margin-top: 8px;
`;
var StyledHeading = styled2(Text32)`
  line-height: 1;
`;

// src/multiformat/work-manifest-preview/utils.ts
function isNewManifest(manifest) {
  const markableQuestion = manifest.question_blocks.find(
    (qb) => typeof qb.points === "number"
  );
  return !markableQuestion || markableQuestion.fields !== void 0;
}
function mapManifestToQuestionPreviewNextProps(manifest, workOutcome, options, filteredQuestions) {
  const questionBlocks = filteredQuestions ? filteredQuestions : manifest.question_blocks;
  return questionBlocks.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((question) => {
    const answerBlocks = manifest.answer_blocks.filter(
      (answerBlock) => answerBlock.question_id === question.question_id
    );
    const bodyAnswerBlock = answerBlocks.find(
      (answerBlock) => !answerBlock.block_name || answerBlock.block_name === "body"
    );
    const referencesAnswerBlock = answerBlocks.find(
      (answerBlock) => answerBlock.block_name === "references"
    );
    const questionOutcome = workOutcome.question_outcomes.find(
      (qo) => qo.question_id === question.question_id
    );
    const answerBlockAttachments = bodyAnswerBlock?.block_attachments || [];
    return buildQuestionPreviewNextProps(
      question,
      workOutcome.assessment_id,
      options?.previewMode,
      {
        body: bodyAnswerBlock?.snapshot,
        references: referencesAnswerBlock?.snapshot
      },
      questionOutcome,
      answerBlockAttachments,
      choiceShuffleSeed2(bodyAnswerBlock)
    );
  });
}
function choiceShuffleSeed2(answerBlock) {
  if (answerBlock?.shuffle !== true) return null;
  return answerBlock.answer_id;
}
function mapManifestToQuestionPreviewProps(manifest, workOutcome, options, filteredQuestions) {
  const questionBlocks = filteredQuestions ? filteredQuestions : manifest.question_blocks;
  return questionBlocks.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((question) => {
    const answerBlock = manifest.answer_blocks.find(
      (answer2) => answer2.question_id === question.question_id
    );
    const questionOutcome = workOutcome.question_outcomes.find(
      (o) => o.question_id === question.question_id
    );
    const answerBlockAttachments = answerBlock?.block_attachments || [];
    return buildQuestionPreviewProps(
      question,
      options?.previewMode,
      answerBlock?.snapshot,
      questionOutcome,
      answerBlockAttachments
    );
  });
}
function questionOutcomesSummary(manifest, workOutcome) {
  const manuallyMarkedCount = manifest.question_blocks.reduce((acc, curr) => {
    if (requiresManualMarking(curr.question_type)) return acc + 1;
    return acc;
  }, 0);
  const manuallyMarkedTotalPoints = manifest.question_blocks.reduce(
    (acc, curr) => {
      if (requiresManualMarking(curr.question_type))
        return acc + (curr.points ?? 0);
      return acc;
    },
    0
  );
  const markableQuestions = manifest.question_blocks.filter(
    (question) => question.question_type !== "overview" && question.question_type !== "section" && question.question_type !== "sub"
  );
  const totalQuestionCount = markableQuestions.length || 0;
  const autoMarkedquestionOutcomes = workOutcome.question_outcomes.filter(
    (a) => a.automark_score !== null
  );
  return {
    autoMarkedTotalQuestionsCount: autoMarkedquestionOutcomes.length,
    autoMarkedMaxScore: autoMarkedquestionOutcomes.reduce(
      (acc, outcome) => acc + (outcome?.max_score || 0),
      0
    ),
    automarkedScore: workOutcome.automark_score ?? 0,
    totalManuallyMarkedQuestionsCount: manuallyMarkedCount,
    manuallyMarkedMaxScore: manuallyMarkedTotalPoints,
    manuallyMarkedScore: 0,
    manuallyYetToBeMarkedQuestions: manuallyMarkedCount,
    totalQuestionsCount: totalQuestionCount,
    maxScore: workOutcome.max_score
  };
}
function reactiveQuestionOutcomesSummary(workOutcome) {
  const totalQuestionsCount = workOutcome.question_outcomes.length || 0;
  const automarkedOutcomes = workOutcome.question_outcomes.filter(
    (a) => a.field_outcomes.some((fo) => fo.field_rubric.automarked)
  );
  const manualOutcomes = workOutcome.question_outcomes.filter(
    (a) => a.field_outcomes.some((fo) => !fo.field_rubric.automarked)
  );
  const manuallyMarkedCount = manualOutcomes.length;
  return {
    autoMarkedTotalQuestionsCount: automarkedOutcomes.length,
    autoMarkedMaxScore: automarkedOutcomes.reduce(
      (acc, outcome) => (outcome.max_score || 0) + acc,
      0
    ),
    automarkedScore: workOutcome.automark_score ?? 0,
    totalManuallyMarkedQuestionsCount: manuallyMarkedCount,
    manuallyMarkedMaxScore: manualOutcomes.reduce(
      (acc, outcome) => (outcome.max_score || 0) + acc,
      0
    ),
    manuallyMarkedScore: workOutcome.marker_score ?? 0,
    manuallyYetToBeMarkedQuestions: manualOutcomes.filter(
      (outcome) => typeof outcome.score !== "number"
    ).length,
    totalQuestionsCount,
    maxScore: workOutcome.max_score
  };
}
function getSectionisedQuestions(questionItems) {
  const unprocessed = questionItems.filter(
    (q) => q.question_type !== "overview"
  );
  const sectionisedQuestions = [];
  let currentSectionIndex = 0;
  while (unprocessed.length > 0) {
    const newSectionIndex = unprocessed.findIndex(
      (q, index) => index !== 0 && q.question_type === "section"
    );
    sectionisedQuestions[currentSectionIndex] = unprocessed.splice(
      0,
      newSectionIndex > 0 ? newSectionIndex : unprocessed.length
    );
    currentSectionIndex++;
  }
  return sectionisedQuestions;
}
function sectionMaxScore(sectionQuestions) {
  const sectionBlock = sectionQuestions[0]?.question_type === "section" ? sectionQuestions[0] : void 0;
  if (sectionBlock?.points != null) {
    return sectionBlock.points;
  }
  return sectionQuestions.reduce(
    (acc, item) => item.question_type === "section" || item.question_type === "sub" ? acc : acc + (item.points ?? 0),
    0
  );
}
function sectionScore(sectionQuestions, questionOutcomes) {
  return sectionQuestions.reduce((acc, item) => {
    if (item.question_type === "section" || item.question_type === "sub") {
      return acc;
    }
    const score = questionOutcomes.find(
      (qo) => qo.question_id === item.question_id
    )?.score;
    return acc + (score ?? 0);
  }, 0);
}
function isTeacherWorkOutcome(workOutcome) {
  return workOutcome.final_score_penalty !== void 0;
}
function mapManifestToFeedbackSummary(manifest, workOutcome) {
  const sections = getSectionisedQuestions(manifest.question_blocks).map(
    (sectionQuestions) => {
      const sectionQuestion = sectionQuestions[0]?.question_type === "section" ? sectionQuestions[0] : void 0;
      const minQuestion = sectionQuestion ? sectionQuestions[1]?.order_label ?? "" : sectionQuestions[0]?.order_label ?? "";
      let maxQuestion = sectionQuestions[sectionQuestions.length - 1]?.order_label ?? "";
      if (maxQuestion.includes(".")) {
        maxQuestion = maxQuestion.split(".")[0] ?? "";
      }
      return {
        score: sectionScore(sectionQuestions, workOutcome.question_outcomes),
        maxScore: sectionMaxScore(sectionQuestions),
        minQuestion,
        maxQuestion,
        sectionLabel: sectionQuestion ? `Part ${sectionQuestion.order_label}` : ""
      };
    }
  );
  const isTeacher = isTeacherWorkOutcome(workOutcome);
  const moderatorModifier = isTeacher ? workOutcome.moderator_modifier ?? 0 : 0;
  const penalty = isTeacher ? workOutcome.final_score_penalty ?? 0 : workOutcome.penalty_score ?? 0;
  const score = (workOutcome.score ?? 0) + (isTeacher ? 0 : penalty);
  return {
    score,
    finalScore: workOutcome.final_score ?? null,
    maxScore: workOutcome.max_score,
    sections,
    penalty,
    moderatorModifier
  };
}
function buildQuestionPreviewNextProps(question, assessmentId, previewMode, snapshots, questionOutcome, blockAttachments, shuffleSeed) {
  const feedbackComments = questionOutcome?.field_outcomes[0]?.feedback_comments ?? [];
  const citerionOutcomes = questionOutcome?.field_outcomes[0]?.criterion_outcomes ?? [];
  const questionTitle = question.question_type === "section" ? `Part ${question.order_label}` : question.question_type === "overview" ? "Assessment Overview" : `Question ${question.order_label}`;
  const data = buildQuestionPreviewData(
    question,
    assessmentId,
    snapshots,
    questionOutcome,
    shuffleSeed
  );
  const isAttachmentEnabled = question.fields?.some((field) => {
    const interaction = field.interaction;
    if (interaction.kind === "extended_interaction" || interaction.kind === "text_entry_interaction") {
      return !!interaction.attachment_enabled;
    }
    return false;
  });
  return {
    questionId: question.question_id,
    questionTitle,
    data,
    promptDoc: question.prompt_doc ? JSON.stringify(question.prompt_doc) : null,
    isSubQuestion: question.question_type === "sub",
    mode: previewMode ?? "marked",
    feedback: snapshots?.body === void 0 ? void 0 : question.feedback ?? void 0,
    feedbackComments,
    citerionOutcomes,
    blockAttachments,
    attachmentEnabled: isAttachmentEnabled
  };
}
function buildQuestionPreviewData(question, assessmentId, snapshots, questionOutcome, shuffleSeed) {
  return {
    question_type: question.question_type,
    // For Support request.
    // Question.points is the max score of the question at the time of the Student's submission (answer_block.points).
    // QuestionOutcome.maxScore (QuestionRubric.max_score) is the current max score of the Question.
    // The existence of the QuestionOutcome means that Student had the opportunity to answer this question during
    // their work. When Question.points !== QuestionOutcome.maxScore, there has been a new instruction sheet
    // release since the time of submission and the instruction sheet has updated the points of the question.
    // a support requests is asking to use updated scores.
    max_score: assessmentId == "229657d0-f0f2-4c19-91ac-f765e3bd1437" && questionOutcome && typeof questionOutcome.max_score == "number" ? questionOutcome.max_score : question.points ?? null,
    fields: question.fields ?? null,
    shuffle_seed: shuffleSeed,
    answer: snapshots?.body ?? null,
    references_answer: snapshots?.references ?? null,
    field_outcomes: questionOutcome?.field_outcomes ?? [],
    prompt_image: question.prompt_image ?? null,
    question_outcome: questionOutcome ? {
      score: questionOutcome.score,
      automark_score: questionOutcome.automark_score
    } : null
  };
}
function buildQuestionPreviewProps(question, previewMode, snapshot, questionOutcome, blockAttachments) {
  const feedbackComments = questionOutcome?.field_outcomes[0]?.feedback_comments ?? [];
  const score = (questionOutcome?.automark_score ?? 0) + (questionOutcome?.marker_score ?? 0);
  const response2 = buildQuestionPreviewResponse(
    question,
    snapshot,
    questionOutcome
  );
  const questionTitle = question.question_type === "section" ? `Part ${question.order_label}` : question.question_type === "overview" ? "Assessment Overview" : `Question ${question.order_label}`;
  const isAttachmentEnabled = question.fields?.some((field) => {
    const interaction = field.interaction;
    if (interaction.kind === "extended_interaction" || interaction.kind === "text_entry_interaction") {
      return !!interaction.attachment_enabled;
    }
    return false;
  });
  return {
    questionId: question.question_id,
    questionTitle,
    points: question.points ?? void 0,
    response: response2 ?? void 0,
    markedPoints: score ?? 0,
    automarked: questionOutcome?.automark_score !== null,
    manualMarked: questionOutcome?.marker_score !== null,
    promptDoc: question.prompt_doc ? JSON.stringify(question.prompt_doc) : null,
    isSubQuestion: question.question_type === "sub",
    mode: previewMode ?? "marked",
    passed: score > 0,
    feedback: snapshot === void 0 ? void 0 : question.feedback ?? void 0,
    feedbackComments,
    blockAttachments,
    attachmentEnabled: isAttachmentEnabled
  };
}
function buildQuestionPreviewResponse(question, snapshot, questionOutcome) {
  if (!question.response) {
    return null;
  }
  switch (question.response.kind) {
    case "multi_response":
      return mapMultiResponse(question.response, questionOutcome);
    case "boolean_response":
      return mapBooleanResponse(questionOutcome);
    case "editor_response":
      return mapEditorResponse(question.response, snapshot, questionOutcome);
    case "short_response":
      return mapShortResponse(question.response, snapshot, questionOutcome);
    case "blanks_response":
      return mapBlanksResponse(question.response, questionOutcome);
  }
}
function mapMultiResponse(response2, questionOutcome) {
  const fieldOutcome = questionOutcome?.field_outcomes[0];
  const fieldRubric = fieldOutcome?.field_rubric;
  const sortedResponseChoice = fieldRubric?.values?.sort() ?? [];
  const sortedStudentChoice = fieldOutcome?.answers?.sort() ?? [];
  const partialScoring = fieldOutcome?.field_rubric?.partial_scoring ?? true;
  const isAllCorrect = sortedResponseChoice.length > 0 && sortedResponseChoice.length === sortedStudentChoice.length && sortedResponseChoice.every(
    (value, index) => value === sortedStudentChoice[index]
  );
  const isAllCorrectSelected = sortedStudentChoice.length > 0 && sortedStudentChoice.every((x) => sortedResponseChoice.includes(x));
  const scorePerChoice = fieldRubric?.values && fieldRubric?.values?.length > 0 ? (questionOutcome?.max_score ?? 0) / fieldRubric?.values?.length : 0;
  return {
    type: "MultiResponse",
    choices: response2.choices.map((choice) => {
      const score = (partialScoring && isAllCorrectSelected || isAllCorrect) && fieldRubric?.values?.includes(choice.id) ? scorePerChoice : 0;
      return {
        id: choice.id,
        text: choice.text,
        isCorrect: choice.is_correct ?? false,
        markedPoints: score
      };
    }),
    partialScoring: fieldRubric?.partial_scoring ?? true,
    answerIds: sortedStudentChoice
  };
}
function mapBooleanResponse(questionOutcome) {
  const fieldOutcome = questionOutcome?.field_outcomes[0];
  const fieldRubric = fieldOutcome?.field_rubric;
  const isTrue = (fieldRubric?.values && fieldRubric?.values[0] === "true") ?? void 0;
  const answer2 = fieldOutcome?.answers && fieldOutcome?.answers?.length > 0 ? fieldOutcome?.answers[0] === "true" : void 0;
  return {
    type: "BooleanResponse",
    isTrue,
    answerTrue: answer2
  };
}
function mapEditorResponse(response2, snapshot, questionOutcome) {
  const answerDoc = snapshot?.answer_doc ? JSON.stringify(snapshot.answer_doc) : void 0;
  const fieldOutcome = questionOutcome?.field_outcomes[0];
  if (fieldOutcome?.doc_content) {
    const doc = JSON.stringify(fieldOutcome.doc_content);
    return {
      type: "EditorResponse",
      answerDoc: doc,
      wordLimit: response2.word_limit ?? void 0
    };
  }
  return {
    type: "EditorResponse",
    answerDoc,
    wordLimit: response2.word_limit ?? void 0
  };
}
function mapShortResponse(response2, snapshot, questionOutcome) {
  const fieldOutcome = questionOutcome?.field_outcomes[0];
  return {
    type: "ShortResponse",
    matchPhrases: response2.match_phrases,
    matchSimilarity: response2.match_similarity ?? 0,
    answerShort: snapshot?.answer_short,
    markedPoints: fieldOutcome?.score ?? 0,
    markedCorrect: (fieldOutcome?.score ?? 0) > 0
  };
}
function mapBlanksResponse(response2, questionOutcome) {
  const fieldOutcomes = questionOutcome?.field_outcomes ?? [];
  return {
    type: "BlanksResponse",
    blanks: response2.match_blanks.map((blank) => {
      const fieldOutcome = fieldOutcomes.find(
        (field) => field.identifier === blank.id
      );
      return {
        id: blank.id,
        matchPhrases: fieldOutcome?.field_rubric?.values ?? []
      };
    }),
    matchSimilarity: response2.match_similarity ?? 0,
    answerPhrases: fieldOutcomes.map((blank) => {
      return {
        blankId: blank.identifier,
        phrase: blank.answer ?? "",
        markedPoints: blank.score ?? 0,
        markedCorrect: (blank.score ?? 0) > 0
      };
    })
  };
}

// src/multiformat/work-manifest-preview/work-manifest-preview.css.ts
var content3 = { base: "_6mcs5h4", header: "_6mcs5h6 _6mcs5h4", details: "_6mcs5h7 _6mcs5h4", summary: "_6mcs5h8 _6mcs5h4" };
var coverPage = "_6mcs5h1";
var feedbackContent2 = "_6mcs5hf";
var questions = "_6mcs5he";
var root12 = "_6mcs5h0";
var summary = "_6mcs5h2";
var title = "_6mcs5h9";

// src/multiformat/work-manifest-preview/work-manifest-mfa-preview.tsx
import { jsx as jsx43, jsxs as jsxs30 } from "react/jsx-runtime";
function WorkManifestMFAPreview(props) {
  const {
    manifest,
    workOutcome,
    previewMode,
    assessment: { name: assessmentName, subject, settings },
    withPromptDoc = true,
    withExtendedAnswersOnly = false,
    withMarkingSummary = false,
    withAutoMarkConditions = false,
    withOverviewBlock = false,
    withFeedbackSummary = false,
    withTitle = false,
    withFeedbackComments = false,
    filteredQuestions,
    extensions,
    onExpandQuestionFeedback
  } = props;
  const isNew = isNewManifest(manifest);
  const studentName = manifest.student?.name;
  function renderDeprecatedQuestions() {
    let previewItems = mapManifestToQuestionPreviewProps(
      manifest,
      workOutcome,
      { previewMode },
      filteredQuestions
    );
    previewItems = previewItems.filter((item) => {
      if (withExtendedAnswersOnly) {
        return item?.response?.type === "EditorResponse";
      }
      if (!withOverviewBlock) {
        return item.questionTitle !== "Assessment Overview";
      }
      return true;
    });
    return previewItems.map((item) => /* @__PURE__ */ jsx43(
      QuestionPreview,
      {
        id: "question-" + item.questionId,
        ...item,
        extensions,
        withPromptDoc,
        showAutomarkConditions: withAutoMarkConditions,
        showFeedbackComments: withFeedbackComments,
        blockAttachments: item.blockAttachments,
        attachmentEnabled: item.attachmentEnabled
      },
      item.questionId
    ));
  }
  function renderNewQuestions() {
    let previewItems = mapManifestToQuestionPreviewNextProps(
      manifest,
      workOutcome,
      { previewMode },
      filteredQuestions
    );
    previewItems = previewItems.filter((item) => {
      if (withExtendedAnswersOnly) {
        return item?.data?.question_type === "extended";
      }
      if (!withOverviewBlock) {
        return item.questionTitle !== "Assessment Overview";
      }
      return true;
    });
    return previewItems.map((item) => /* @__PURE__ */ jsx43(
      QuestionPreviewNext,
      {
        id: "question-" + item.questionId,
        ...item,
        extensions,
        withPromptDoc,
        automarkDisplay: withAutoMarkConditions ? "inline" : void 0,
        withFeedbackComments,
        blockAttachments: item.blockAttachments,
        attachmentEnabled: item.attachmentEnabled,
        onExpandFeedback: onExpandQuestionFeedback && (() => onExpandQuestionFeedback?.(item.questionId))
      },
      item.questionId
    ));
  }
  function renderSummary() {
    if (withMarkingSummary && workOutcome.assessment_id === "229657d0-f0f2-4c19-91ac-f765e3bd1437") {
      const autoMarkSummaryProps = reactiveQuestionOutcomesSummary(workOutcome);
      return /* @__PURE__ */ jsx43(QuestionOutcomeSummary, { ...autoMarkSummaryProps });
    }
    if (withMarkingSummary) {
      const autoMarkSummaryProps = questionOutcomesSummary(
        manifest,
        workOutcome
      );
      return /* @__PURE__ */ jsx43(QuestionOutcomeSummary, { ...autoMarkSummaryProps });
    }
    if (withFeedbackSummary && QuestionFeedbackSummary) {
      const feedbackSummaryProps = mapManifestToFeedbackSummary(
        manifest,
        workOutcome
      );
      return /* @__PURE__ */ jsx43(QuestionFeedbackSummary, { ...feedbackSummaryProps });
    }
    return null;
  }
  const summary2 = renderSummary();
  const questions2 = isNew ? renderNewQuestions() : renderDeprecatedQuestions();
  const details = [
    {
      label: "Student Name",
      value: studentName ?? "",
      show: !!studentName
    },
    {
      label: "Subject",
      value: subject?.name ?? "",
      show: !!subject
    },
    {
      label: "Assessment",
      value: assessmentName ?? "",
      show: !!assessmentName
    },
    {
      label: "Date",
      value: settings?.due_date ?? "",
      show: !!settings?.due_date
    }
  ];
  return /* @__PURE__ */ jsxs30("div", { className: root12, children: [
    withTitle && /* @__PURE__ */ jsx43("div", { className: title, children: /* @__PURE__ */ jsx43(
      CoverPageHeader,
      {
        details,
        assessmentName,
        subject: subject?.name ?? null,
        feedbackTitle: "Feedback"
      }
    ) }),
    summary2 && /* @__PURE__ */ jsx43("div", { className: summary, id: "summary", children: summary2 }),
    /* @__PURE__ */ jsx43(Spacer7, { spacing: 32 }),
    /* @__PURE__ */ jsx43("div", { className: questions, children: questions2 })
  ] });
}

// src/multiformat/work-manifest-preview/work-manifest-classic-preview.tsx
import { Spacer as Spacer8 } from "@vericus/cadmus-ui";
import { format } from "date-fns";

// src/printing/utils.ts
function classicEditorContents(manifest, assessmentSettings, withNotes = false) {
  const maybeBody = answerBlockEditorContent(
    manifest.answer_blocks.find((ab) => ab.block_name === "body")
  );
  const maybeReferences = answerBlockEditorContent(
    manifest.answer_blocks.find((ab) => ab.block_name === "references")
  );
  const maybeNotes = answerBlockEditorContent(
    manifest.answer_blocks.find((ab) => ab.block_name === "notes")
  );
  const hideReferences = assessmentSettings.assessment_type === "exam" ? !assessmentSettings.referencing_style_enabled : false;
  const contents = [
    maybeBody,
    hideReferences ? void 0 : maybeReferences,
    withNotes ? maybeNotes : void 0
  ].filter((x) => !!x);
  return contents;
}
function answerBlockEditorContent(ab) {
  if (!ab || !ab.snapshot || !ab.snapshot.answer_doc) {
    return null;
  }
  return {
    id: ab.block_name ?? ab.answer_id,
    content: ab.snapshot.answer_doc
  };
}
function wordCountContent(referencingStyle, hasReferences, bodyWordCount, referencesWordCount, footnotesWordCount) {
  if (!bodyWordCount) return "";
  const bodyWordCountContent = `Body: ${bodyWordCount} words`;
  const referencesWordCountContent = hasReferences && referencesWordCount ? `${referencesTitle(referencingStyle)}: ${referencesWordCount} words` : void 0;
  const footnotesWordCountContent = footnotesWordCount !== null ? `Footnotes: ${footnotesWordCount} words` : void 0;
  return [
    bodyWordCountContent,
    referencesWordCountContent,
    footnotesWordCountContent
  ].filter(Boolean).join(" \u2022 ");
}
function isPageNumberOnTopRight(referencingStyle) {
  return referencingStyle === "ChicagoA" || referencingStyle === "AGLC";
}
function pageNumber(referencingStyle) {
  if (referencingStyle === "OSCOLA" || referencingStyle === "ChicagoA" || referencingStyle === "AGLC") {
    return `counter(page)`;
  } else {
    return `counter(page) " of " counter(pages)`;
  }
}
var fontStyle = `
<link rel="stylesheet" href="https://use.typekit.net/ice5gsa.css" />
<link rel="stylesheet" href="https://unpkg.com/mathlive@0.83.0/dist/mathlive-static.css" />
<link rel="stylesheet" href="https://cdn.cadmus.io/fonts/fonts-960c08fcf21499bc976e314921ddb536.css" />
`;

// src/multiformat/work-manifest-preview/editor-content-preview.tsx
import { useMemo as useMemo6 } from "react";
import { styled as styled4 } from "styled-components";
import {
  CadmusKitServer as CadmusKitServer3
} from "@vericus/cadmus-editor-prosemirror/server";
import { Divider as Divider5, SectionLabel as SectionLabel2, typography } from "@vericus/cadmus-ui";

// src/components/prosemirror-html-preview.tsx
import { styled as styled3 } from "styled-components";
import { CadmusEditorWrapper as CadmusEditorWrapper2 } from "@vericus/cadmus-editor-prosemirror/server";
import { generateHTML as generateHTML4 } from "@tiptap/html";
import { jsx as jsx44, jsxs as jsxs31 } from "react/jsx-runtime";
function ProseMirrorHTMLPreview(props) {
  const { content: content4, extensions, style, footnotes } = props;
  const html = generateHTML4(content4, extensions);
  return /* @__PURE__ */ jsx44("div", { style, children: /* @__PURE__ */ jsxs31(Wrapper, { children: [
    /* @__PURE__ */ jsx44(
      "div",
      {
        className: "ProseMirror",
        contentEditable: "false",
        dangerouslySetInnerHTML: { __html: html }
      }
    ),
    footnotes && /* @__PURE__ */ jsx44("div", { className: "ProseMirror", children: footnotes })
  ] }) });
}
var Wrapper = styled3(CadmusEditorWrapper2)`
  &::before {
    display: none;
  }
`;

// src/multiformat/work-manifest-preview/footnotes-utils.ts
import {
  listFootnotes as listFootnotes2,
  Node
} from "@vericus/cadmus-editor-prosemirror";
import {
  CadmusKitServer as CadmusKitServer2,
  getSchema as getSchema2
} from "@vericus/cadmus-editor-prosemirror/server";
var schema2 = getSchema2([CadmusKitServer2.configure()]);
function extractFootnotes(content4) {
  const doc = Node.fromJSON(schema2, content4);
  return listFootnotes2(doc).map((node) => ({
    ...node,
    content: node.content ? removeParagraphTag(node.content) : void 0
  }));
}
function removeParagraphTag(html) {
  if (typeof DOMParser !== "undefined") {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    const p = doc.querySelector("p");
    return p ? p.innerHTML : html;
  }
  return html.replace(/<\/?p>/g, "");
}

// src/multiformat/work-manifest-preview/editor-content-preview.tsx
import { Fragment as Fragment16, jsx as jsx45, jsxs as jsxs32 } from "react/jsx-runtime";
function EditorContentPreview(props) {
  const { editors, withEditorSectionLabel, hasHangingIndent = false } = props;
  return /* @__PURE__ */ jsxs32(Fragment16, { children: [
    withEditorSectionLabel && /* @__PURE__ */ jsx45("div", { style: { margin: 0, padding: "0 88px" }, children: /* @__PURE__ */ jsx45(SectionLabel2, { title: "Written Submission" }) }),
    editors.map(({ id, content: content4 }) => /* @__PURE__ */ jsx45(
      EditorWithFootnotes,
      {
        id,
        content: content4,
        hasHangingIndent: id === "references" ? hasHangingIndent : void 0
      },
      id
    ))
  ] });
}
function EditorWithFootnotes(props) {
  const { content: content4, hasHangingIndent } = props;
  const footnotes = useMemo6(() => extractFootnotes(content4), [content4]);
  return /* @__PURE__ */ jsx45(EditorContainer, { children: /* @__PURE__ */ jsx45(
    ProseMirrorHTMLPreview,
    {
      content: content4,
      extensions: [
        // Read-only surface, no paste risk: always render saved font marks.
        CadmusKitServer3.configure({
          enableHangingIndent: hasHangingIndent,
          enableFontControls: true
        })
      ],
      footnotes: footnotes.length > 0 ? /* @__PURE__ */ jsxs32(Fragment16, { children: [
        /* @__PURE__ */ jsx45(Divider5, { className: "footnote-bottom-divider" }),
        footnotes.map((footnote2, index) => /* @__PURE__ */ jsx45(FootnoteBottom, { className: "footnote-bottom", children: /* @__PURE__ */ jsxs32(FootnoteBottomContent, { href: `#footnote-${footnote2.id}`, children: [
          /* @__PURE__ */ jsxs32(FootnoteBottomCount, { children: [
            index + 1,
            "\xA0"
          ] }),
          /* @__PURE__ */ jsx45(
            "span",
            {
              dangerouslySetInnerHTML: {
                __html: footnote2.content ?? ""
              }
            }
          )
        ] }) }, footnote2.id))
      ] }) : void 0
    }
  ) });
}
var EditorContainer = styled4.div`
  counter-reset: footnote;

  @media vivliostyle {
    footnote {
      font-size: ${typography.systemSm.fontSize} !important;
      font-weight: ${typography.systemSm.fontWeight} !important;
      font-family: ${typography.systemSm.fontFamily};
      letter-spacing: ${typography.systemSm.letterSpacing};
      line-height: 1;
    }
    .footnote-content {
      display: inline !important;
    }
    .footnote-bottom,
    .footnote-bottom-divider {
      display: none !important;
    }
  }

  @media print {
    widows: 4;
    orphans: 3;
    page-break-after: always;

    footnote {
      float: footnote;
      footnote-policy: line;
      counter-increment: footnote;

      text-align: left;
      text-indent: 0px;
      background: transparent !important;

      &:before {
        vertical-align: super;
        margin-left: 0.1em;
      }
    }

    footnote::footnote-marker {
      margin-left: 0.1em;
    }

    footnote::footnote-call {
      content: counter(footnote);
      font-size: 80%;
      vertical-align: super;
      display: inline;
      line-height: 1;
      margin-left: 0.1em;
      margin-right: 0.1em;
    }

    @-adapt-footnote-area {
      margin-top: 0;
      word-break: break-all;
      padding: 0 80px; // to align with the rest of the content
      max-height: 100%; // to allow footnotes area to expand
    }

    @-adapt-footnote-area ::before {
      margin-inline-end: 60%;
      border-top: 0.8px solid black;
    }

    // Hide comment threads
    thread,
    thread.thread-active {
      background: none !important;
      border-bottom: none !important;
    }
  }

  /* Sets displayTwo styles first child titles */
  .ProseMirror > h1:first-child,
  h1:first-child {
    font-family: ${typography["displayTwo"].fontFamily};
    font-size: ${typography["displayTwo"].fontSize};
    font-weight: ${typography["displayTwo"].fontWeight};
    padding-top: ${typography["displayTwo"].paddingTop};
    padding-bottom: ${typography["displayTwo"].paddingBottom};
    line-height: ${typography["displayTwo"].lineHeight};
    letter-spacing: ${typography["displayTwo"].letterSpacing};
  }

  [data-cadmus-pasted] {
    display: none !important;
  }
`;
var FootnoteBottom = styled4.p`
  padding-bottom: 2px !important;
  font-size: 80% !important;
`;
var FootnoteBottomContent = styled4.a`
  color: inherit !important;
  text-decoration: none;
`;
var FootnoteBottomCount = styled4.span`
  vertical-align: super;
`;

// src/multiformat/work-manifest-preview/work-manifest-classic-preview.tsx
import { Fragment as Fragment17, jsx as jsx46, jsxs as jsxs33 } from "react/jsx-runtime";
function WorkManifestClassicPreview(props) {
  const {
    manifest,
    workOutcome,
    assessment,
    withCriterionOutcomes = false,
    withNotes = false,
    withFeedbackComments = false
  } = props;
  const contents = classicEditorContents(
    manifest,
    assessment.settings,
    withNotes
  );
  const referencingStyle = assessment.settings?.referencing_style;
  const hasHangingIndent = assessment.settings?.referencing_style_enabled === false && referencingStyle ? isHangingIndent(referencingStyle) : false;
  function renderSummary() {
    if (withCriterionOutcomes) {
      const outcomes = criterionOutcomesSummary(workOutcome);
      const isTeacher = isTeacherWorkOutcome(workOutcome);
      const finalScorePenalty = isTeacher ? workOutcome.final_score_penalty ?? 0 : workOutcome.penalty_score ?? 0;
      const markedScore = {
        score: workOutcome.score == null ? null : workOutcome.score + (isTeacher ? 0 : finalScorePenalty),
        maxScore: workOutcome.max_score
      };
      const reportWeight = assessment.settings.group_assessment_enabled ? workOutcome?.group_id ? assessment.settings.group_report_weight : assessment.settings.individual_report_weight : null;
      return /* @__PURE__ */ jsx46("div", { className: content3.summary, id: "submissionOutcome", children: /* @__PURE__ */ jsx46(
        SubmissionOutcomeSummary,
        {
          criterionOutcomes: outcomes,
          markedScore,
          reportWeight,
          moderatorModifier: isTeacher ? workOutcome.moderator_modifier ?? 0 : 0,
          finalScorePenalty
        }
      ) });
    }
    return null;
  }
  const summary2 = renderSummary();
  const isCadmusMarker = assessment.settings.marking_tool === "cadmusgrader";
  const showCoverPage = isCadmusMarker && withCriterionOutcomes;
  const feedbackComments = workOutcome.submission_outcome?.feedback_comments;
  const criterionOutcomes = workOutcome.submission_outcome?.criterion_outcomes;
  return /* @__PURE__ */ jsxs33(Fragment17, { children: [
    showCoverPage && /* @__PURE__ */ jsxs33("div", { className: coverPage, children: [
      /* @__PURE__ */ jsx46("div", { className: content3.details, id: "submissionDetails", children: /* @__PURE__ */ jsx46(SubmissionDetails, { ...props }) }),
      summary2
    ] }),
    withCriterionOutcomes && /* @__PURE__ */ jsxs33(Fragment17, { children: [
      /* @__PURE__ */ jsx46(
        BlockAttachments,
        {
          attachments: manifest.answer_blocks.find(
            (block) => block.block_name === "body"
          )?.block_attachments || []
        }
      ),
      /* @__PURE__ */ jsx46(Spacer8, { spacing: 16 })
    ] }),
    /* @__PURE__ */ jsx46(
      EditorContentPreview,
      {
        editors: contents,
        hasHangingIndent,
        withEditorSectionLabel: withCriterionOutcomes
      }
    ),
    !withCriterionOutcomes && /* @__PURE__ */ jsxs33(Fragment17, { children: [
      /* @__PURE__ */ jsx46(Spacer8, { spacing: 16 }),
      /* @__PURE__ */ jsx46(
        BlockAttachments,
        {
          attachments: manifest.answer_blocks.find(
            (block) => block.block_name === "body"
          )?.block_attachments || []
        }
      )
    ] }),
    withFeedbackComments && !!feedbackComments?.length && /* @__PURE__ */ jsxs33(Fragment17, { children: [
      /* @__PURE__ */ jsx46(Spacer8, { spacing: 16 }),
      /* @__PURE__ */ jsx46("div", { className: feedbackContent2, children: feedbackComments.map((comment, index) => /* @__PURE__ */ jsx46(
        CommentBox,
        {
          ...comment,
          index: index + 1,
          criterion_outcomes: criterionOutcomes
        },
        comment.id
      )) })
    ] })
  ] });
}
function SubmissionDetails(props) {
  const {
    assessment: { subject, settings, name },
    manifest,
    workOutcome
  } = props;
  const isGroupReport = !!workOutcome.group_id;
  const assessmentType = settings.group_assessment_enabled ? isGroupReport ? "Group report" : "Individual report" : settings.assessment_type;
  const feedbackTitle = settings.group_assessment_enabled && isGroupReport ? "Group report Feedback" : "Individual report Feedback";
  const dueDate = settings.due_date ? format(new Date(settings.due_date), "d MMM yyyy") : null;
  const details = [
    {
      label: "Group Name",
      value: manifest.group?.name,
      show: !!manifest.group?.name
    },
    {
      label: "Student Members",
      value: manifest.group?.members?.flatMap(({ name: name2 }) => name2 ? [name2] : []).join(", "),
      show: isGroupReport && !!manifest.group?.members && settings.group_hide_members_name == false && settings.anonymous_marking == false
    },
    {
      label: "Student Name",
      value: manifest.student?.name,
      show: !isGroupReport && !!manifest.student?.name && settings.anonymous_marking == false
    },
    {
      label: "Subject",
      value: subject?.name,
      show: !!subject
    },
    {
      label: "Assessment Type",
      value: assessmentType,
      show: true
    },
    {
      label: "Date",
      value: dueDate,
      show: !!dueDate
    }
  ];
  return /* @__PURE__ */ jsx46(
    CoverPageHeader,
    {
      details,
      assessmentName: name,
      subject: subject?.name ?? null,
      feedbackTitle
    }
  );
}

// src/multiformat/auto-mark-summary/auto-mark-summary.tsx
import { Text as Text33, vars as vars3 } from "@vericus/cadmus-ui";

// src/multiformat/auto-mark-summary/auto-mark-summary.css.ts
var correctAnswerLabel = { correctAnswer: "tx9un7a", incorrectAnswer: "tx9un7b" };
var footer = "tx9un71";
var left = "tx9un73";
var main = "tx9un72";
var right = "tx9un74";
var root13 = "tx9un70";
var tab = { correctAnswer: "tx9un76 tx9un75", incorrectAnswer: "tx9un77 tx9un75" };
var tabBase = "tx9un75";
var total = "tx9un78 tx9un75";

// src/multiformat/auto-mark-summary/auto-mark-summary.tsx
import { jsx as jsx47, jsxs as jsxs34 } from "react/jsx-runtime";
var AutoMarkSummary = (props) => {
  return /* @__PURE__ */ jsxs34("div", { className: root13, "data-testid": "automark-summary", children: [
    /* @__PURE__ */ jsxs34("div", { className: main, children: [
      /* @__PURE__ */ jsxs34("div", { className: left, children: [
        /* @__PURE__ */ jsx47(
          AutoMarkedCount,
          {
            marked: props.autoMarkedCount,
            total: props.totalQuestionCount
          }
        ),
        /* @__PURE__ */ jsx47(
          SummaryPoint,
          {
            variant: "correctAnswer",
            count: props.correctAnswerCount
          }
        ),
        /* @__PURE__ */ jsx47(
          SummaryPoint,
          {
            variant: "incorrectAnswer",
            count: props.incorrectAnswerCount
          }
        )
      ] }),
      /* @__PURE__ */ jsx47("div", { className: right, children: /* @__PURE__ */ jsx47(TotalPoints, { points: props.pointsAwarded }) })
    ] }),
    /* @__PURE__ */ jsx47("div", { className: footer, children: /* @__PURE__ */ jsxs34("ul", { children: [
      /* @__PURE__ */ jsx47(Text33, { kind: "bodySm", as: "li", color: "blue800", children: "This is a summary of the performance on auto-marked question types only. These score are provisional and may be revised." }),
      props.extendedResponseCount ? /* @__PURE__ */ jsxs34(Text33, { kind: "bodySm", as: "li", color: "blue800", children: [
        "Extended responses are not auto-marked. This assessment has",
        " ",
        /* @__PURE__ */ jsx47("strong", { children: props.extendedResponseCount }),
        " of these questions"
      ] }) : "",
      props.extendedResponseCount ? /* @__PURE__ */ jsxs34(Text33, { kind: "bodySm", as: "li", color: "blue800", children: [
        "They are included below for you to mark manually out of",
        " ",
        /* @__PURE__ */ jsx47("strong", { children: props.extendedResponseTotalPoints }),
        " points"
      ] }) : ""
    ] }) })
  ] });
};
var SummaryPoint = (props) => {
  let label;
  switch (props.variant) {
    case "correctAnswer":
      label = "Correct Answers";
      break;
    case "incorrectAnswer":
      label = "Incorrect Answers";
      break;
  }
  return /* @__PURE__ */ jsxs34("div", { className: tab[props.variant], children: [
    /* @__PURE__ */ jsx47("div", { children: /* @__PURE__ */ jsx47(
      Text33,
      {
        kind: "systemSm",
        className: correctAnswerLabel[props.variant],
        children: label
      }
    ) }),
    /* @__PURE__ */ jsx47(
      Text33,
      {
        kind: "headingTwo",
        className: correctAnswerLabel[props.variant],
        children: props.count
      }
    )
  ] });
};
var AutoMarkedCount = (props) => {
  return /* @__PURE__ */ jsxs34("div", { className: tabBase, children: [
    /* @__PURE__ */ jsx47(Text33, { color: "navy500", kind: "systemSm", children: "Auto-marked" }),
    /* @__PURE__ */ jsxs34(Text33, { kind: "headingTwo", children: [
      /* @__PURE__ */ jsx47("span", { children: props.marked }),
      /* @__PURE__ */ jsx47("span", { children: "/" }),
      /* @__PURE__ */ jsx47(
        "span",
        {
          style: {
            color: vars3.colors.poppy700
          },
          children: props.total
        }
      )
    ] })
  ] });
};
var TotalPoints = (props) => {
  return /* @__PURE__ */ jsxs34("div", { className: total, children: [
    /* @__PURE__ */ jsx47(Text33, { kind: "systemSm", children: "Total Score" }),
    /* @__PURE__ */ jsx47("span", { style: { fontSize: "10px" }, children: "of auto-marked questions" }),
    /* @__PURE__ */ jsx47(Text33, { kind: "headingTwo", inline: true, children: formatPoints2(props.points) })
  ] });
};
function formatPoints2(points2) {
  const truncatedPoints = Number.parseFloat(points2.toFixed(2));
  return `${truncatedPoints}`;
}

// src/multiformat/print-instruction-preview/print-instruction-preview.tsx
import { CadmusKitServer as CadmusKitServer4 } from "@vericus/cadmus-editor-prosemirror/server";

// src/components/print-instruction/pdf-header.tsx
import { Text as Text34 } from "@vericus/cadmus-ui";

// src/components/print-instruction/pdf-header.css.ts
var left2 = "_1bxy7l31";
var previewHeader = "_1bxy7l30";

// src/components/print-instruction/assets/cadmus-logo.ts
var cadmus_logo_default = `<svg xmlns="http://www.w3.org/2000/svg" width="340" height="120" viewBox="0 0 340 120" fill="none">
  <path fill-rule="evenodd" clip-rule="evenodd" d="M37.1754 77.2433C43.9338 38.5772 78.7527 18.0532 110.651 21.1286C100.649 22.8389 93.6588 27.3644 89.6792 34.7049C87.2899 38.4098 84.0871 40.8066 80.071 41.8953L86.8084 41.0658C85.9593 42.8671 85.0486 44.5874 84.0832 46.2299C79.4266 53.0182 74.2615 56.9657 68.5879 58.0724L68.3576 58.1157L74.9884 58.1159C73.6896 59.4396 72.3496 60.685 70.9768 61.8563C65.903 65.4607 60.7082 67.5306 55.3925 68.066L62.09 68.2194C59.6813 69.6597 57.2306 70.9242 54.7742 72.0304L54.2073 72.2829C45.3343 76.1905 39.4856 83.6028 36.661 94.52L36.5763 94.8519L30.1775 100.165C30.4757 99.7666 30.7703 99.3132 31.0615 98.8052L31.2199 98.5227C31.2462 98.4747 31.2726 98.4263 31.2989 98.3774L31.4562 98.0786L31.6129 97.769C31.639 97.7165 31.665 97.6636 31.691 97.6102L31.8466 97.2843L32.0016 96.9477C32.0273 96.8906 32.053 96.8332 32.0787 96.7753L32.2326 96.4223C32.2582 96.3626 32.2838 96.3024 32.3093 96.2418L32.4621 95.8726L32.6142 95.4926L32.7656 95.1017L32.9163 94.7C32.9414 94.6322 32.9664 94.5639 32.9914 94.4951L33.1411 94.0772L33.29 93.6484C33.3148 93.576 33.3395 93.5032 33.3642 93.4299L33.5121 92.9849L33.6593 92.529L33.8058 92.0623L33.9516 91.5848C33.9759 91.5043 34.0001 91.4233 34.0243 91.342L34.169 90.8482L34.313 90.3435L34.4564 89.8281L34.599 89.3018L34.7409 88.7646L34.8821 88.2167L35.0226 87.6579L35.1625 87.0882L35.3016 86.5077L35.5089 85.6167L35.6463 85.0091L35.7829 84.3907L35.9189 83.7615L36.1215 82.7973L36.2557 82.141L36.3892 81.4738L36.522 80.7958L36.7198 79.7585L36.9161 78.6968L37.1108 77.6108C37.1324 77.4887 37.1539 77.3663 37.1754 77.2433Z" fill="#0F3063"/>
  <path d="M111.125 58.5167C117.892 58.6703 123.748 62.9501 125.45 69.0397L125.676 69.8478H118.507L118.33 69.4841C116.744 66.2124 114.297 64.5914 110.903 64.5914C105.76 64.5914 102.203 68.3975 102.203 73.859C102.203 79.1797 105.872 83.1771 110.8 83.1771L111.123 83.1717C114.329 83.0635 116.919 81.339 118.274 78.3969L118.446 78.0214H125.74L125.436 78.8726C123.137 85.2995 117.551 89.2562 110.8 89.2562C102.327 89.2562 95.5621 82.5532 95.5621 74.1623C95.5621 65.2917 102.142 58.5124 110.749 58.5124L111.125 58.5167Z" fill="#0F3063"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M129.763 74.3644C129.763 65.2593 136.53 58.5124 145.649 58.5124C148.805 58.5124 151.535 59.316 153.685 60.8675L153.994 61.0992L155.131 62.0169L155.13 59.2698H161.953V88.5478H155.13L155.131 85.5361L154.111 86.4442L153.801 86.6983C151.91 88.189 149.486 89.0675 146.776 89.229L146.335 89.2489L145.849 89.2562L145.476 89.2524C136.663 89.0732 129.763 82.5685 129.763 74.3644ZM155.236 73.9601C155.236 68.6021 151.206 64.5914 145.806 64.5914C140.735 64.5914 136.586 68.804 136.586 73.9601C136.586 78.9473 140.818 83.1771 145.806 83.1771C150.98 83.1771 155.236 79.0203 155.236 73.9601Z" fill="#0F3063"/>
  <path fill-rule="evenodd" clip-rule="evenodd" d="M191.276 44.6281H198.166V88.5449H191.7L191.7 85.6807L190.474 86.6208L190.124 86.8706C188.111 88.2528 185.615 89.0707 182.889 89.2281L182.421 89.2486L181.904 89.2562L181.53 89.2524C172.716 89.0731 165.976 82.5621 165.976 74.1494C165.976 65.1325 172.721 58.3828 181.754 58.3828C184.893 58.3828 187.765 59.1945 190.028 60.7125L191.276 61.5808L191.276 44.6281ZM191.7 74.0986C191.7 68.4788 187.754 64.4875 182.177 64.4875C177.002 64.4875 172.866 68.6658 172.866 73.8956C172.866 78.9039 177.14 83.1515 182.177 83.1515C187.375 83.1515 191.7 79.0531 191.7 74.0986Z" fill="#0F3063"/>
  <path d="M236.954 59.5041L237.328 59.5078C244.887 59.6552 249.467 64.2843 249.467 71.8021L249.467 89.2562H242.66L242.66 73.1038C242.66 68.115 240.574 65.5258 236.475 65.5258L236.198 65.5288C232 65.6221 230.238 67.8688 230.238 73.0036L230.238 89.2562H223.43L223.431 72.503C223.431 67.8432 221.473 65.5258 217.508 65.5258L217.223 65.5292C212.988 65.6321 211.009 68.0533 211.009 73.0537L211.009 89.2562H204.201V60.2546H210.589L210.591 62.2794L211.707 61.4264C213.433 60.1595 215.326 59.5715 217.886 59.5096L218.354 59.5041C221.668 59.5042 224.826 60.7577 226.752 62.8018L227.012 63.086L227.45 63.5204L227.979 62.9217C230.026 60.6903 232.724 59.5982 236.444 59.5101L236.954 59.5041Z" fill="#0F3063"/>
  <path d="M282.663 59.5041V88.5534H276.058L276.061 86.7793L274.727 87.6088C273.022 88.6026 270.779 89.1802 268.297 89.2491L267.783 89.2562L267.396 89.2527C259.195 89.1044 254.498 84.2201 254.498 75.6342L254.497 59.5041H261.592L261.593 75.233C261.593 80.7502 263.676 83.2246 268.39 83.2246L268.684 83.2207C273.141 83.1009 275.568 80.2321 275.568 75.1327L275.567 59.5041H282.663Z" fill="#0F3063"/>
  <path d="M299.006 58.5163C304.308 58.6454 308.452 61.9817 309.042 66.4957L309.069 66.7413L301.843 66.9042L301.818 66.8147C301.378 65.4239 300.119 64.5914 298.412 64.5914C296.683 64.5914 295.207 65.6211 295.207 66.9347C295.207 68.2024 296.106 68.9405 298.477 69.761L299.136 69.9795L299.883 70.2104L301.253 70.6106C307.112 72.3884 309.822 75.2462 309.822 79.6714C309.822 85.2332 305.091 89.2562 298.521 89.2562L298.171 89.2522C291.896 89.1097 287.19 85.1395 286.712 79.6412L286.686 79.2776L293.895 79.1996C294.296 81.7443 295.993 83.1771 298.631 83.1771C300.932 83.1771 302.659 81.8423 302.659 79.9747C302.659 78.391 300.966 77.0488 297.964 76.0997L297.089 75.8358C290.649 73.8693 288.043 71.3899 288.043 67.3896C288.043 62.4728 292.794 58.5124 298.686 58.5124L299.006 58.5163Z" fill="#0F3063"/>
</svg>
`;

// src/components/print-instruction/pdf-header.tsx
import { jsx as jsx48, jsxs as jsxs35 } from "react/jsx-runtime";
function PdfHeader(props) {
  const { date } = props;
  return /* @__PURE__ */ jsxs35("div", { className: previewHeader, children: [
    /* @__PURE__ */ jsx48("div", { className: left2, children: /* @__PURE__ */ jsx48(
      "img",
      {
        src: `data:image/svg+xml;utf8,${encodeURIComponent(cadmus_logo_default)}`,
        alt: "Cadmus logo",
        "aria-label": "Cadmus home",
        height: 40,
        style: { marginBottom: "2px" }
      }
    ) }),
    date && /* @__PURE__ */ jsx48(Text34, { kind: "label", children: date.replaceAll("-", "/") })
  ] });
}

// src/components/print-instruction/pdf-label.tsx
import { Text as Text35 } from "@vericus/cadmus-ui";

// src/components/print-instruction/pdf-label.css.ts
var dottedLine = "xb90sz3";
var labelContainer = "xb90sz0";
var labelRow = "xb90sz1";
var labelText = "xb90sz2";

// src/components/print-instruction/pdf-label.tsx
import { jsx as jsx49, jsxs as jsxs36 } from "react/jsx-runtime";
function PdfLabel(props) {
  const { assessmentName, labels, totalPoints } = props;
  return /* @__PURE__ */ jsxs36("div", { className: labelContainer, children: [
    /* @__PURE__ */ jsx49(Text35, { kind: "displayThree", textTransform: "capitalize", children: assessmentName }),
    /* @__PURE__ */ jsxs36(Text35, { kind: "systemMd", children: [
      totalPoints,
      " TOTAL POINTS"
    ] }),
    /* @__PURE__ */ jsx49("div", { style: { margin: "12px 0" }, children: labels.map((label, index) => /* @__PURE__ */ jsxs36("div", { className: labelRow, children: [
      /* @__PURE__ */ jsx49(
        Text35,
        {
          kind: "systemMd",
          className: labelText,
          style: { minWidth: "60px" },
          children: label
        }
      ),
      /* @__PURE__ */ jsx49("div", { className: dottedLine })
    ] }, index)) })
  ] });
}

// src/multiformat/print-instruction-preview/print-instruction-preview.css.ts
var previewContainer = "rne50e0";

// src/multiformat/print-instruction-preview/utils.ts
function convertToWorkManifestFields(fields) {
  if (!fields) return null;
  const converted = fields.filter(
    (field) => field.identifier !== void 0 && field.response !== void 0 && field.interaction !== void 0
  ).map(
    (field) => ({
      identifier: field.identifier,
      response: field.response,
      interaction: field.interaction
    })
  );
  return converted.length > 0 ? converted : null;
}
function buildQuestionPrintPreview(blocks) {
  const sortedBlocks = blocks.filter((block) => block.question_type !== "overview").sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  return sortedBlocks.map((question) => {
    const questionTitle = question.question_type === "section" ? `Part ${question.order_label}` : question.question_type === "overview" ? "Assessment Overview" : `Question ${question.order_label}`;
    return {
      questionId: question.question_id,
      questionTitle,
      points: question.points ?? void 0,
      data: {
        question_type: question.question_type,
        max_score: question.points ?? null,
        fields: convertToWorkManifestFields(question.fields),
        answer: null,
        references_answer: null,
        field_outcomes: null,
        question_outcome: null,
        prompt_image: question.prompt_image ?? null
      },
      mode: "question",
      promptDoc: question.prompt_doc ? JSON.stringify(question.prompt_doc) : null
    };
  });
}

// src/multiformat/print-instruction-preview/print-instruction-preview.tsx
import { jsx as jsx50, jsxs as jsxs37 } from "react/jsx-runtime";
function PrintInstructionPreview(props) {
  const { instruction } = props;
  const { assessmentName, labels, date } = instruction;
  const questionPreviewProps = buildQuestionPrintPreview(instruction.blocks);
  const totalPoints = instruction.blocks.reduce(
    (acc, block) => acc + (block.points ?? 0),
    0
  );
  return /* @__PURE__ */ jsxs37("div", { className: previewContainer, children: [
    /* @__PURE__ */ jsx50(PdfHeader, { date: date ?? null }),
    assessmentName && /* @__PURE__ */ jsx50(
      PdfLabel,
      {
        assessmentName,
        labels: labels ?? [],
        totalPoints
      }
    ),
    questionPreviewProps.map((props2) => /* @__PURE__ */ jsx50(
      QuestionPrintPreview,
      {
        ...props2,
        extensions: [
          // Read-only surface, no paste risk: always render saved font marks.
          CadmusKitServer4.configure({
            enableBlanks: true,
            enableFontControls: true
          })
        ]
      },
      props2.questionId
    ))
  ] });
}

// src/printing/render-classic-work.tsx
import { ServerStyleSheet } from "styled-components";
import { renderToString } from "react-dom/server";

// src/printing/components/print-content.tsx
import { styled as styled5 } from "styled-components";
import { colors as colors16, typography as typography2 } from "@vericus/cadmus-ui";
import { jsx as jsx51 } from "react/jsx-runtime";
function PrintContent(props) {
  const workOwner = props.studentId ? `Student ID: ${props.studentId}` : props.groupName ? `${props.groupName}` : "";
  const wordCount = wordCountContent(
    props.referencingStyle,
    props.hasReferences,
    props.bodyWordCount,
    props.referencesWordCount,
    props.footnotesWordCount
  );
  const topRightContent = isPageNumberOnTopRight(props.referencingStyle) ? "content: " + pageNumber(props.referencingStyle) : `content: "${workOwner}${workOwner && wordCount ? " \u2022 " : ""}${wordCount}"`;
  const topLeftContent = isPageNumberOnTopRight(props.referencingStyle) ? `content: "${workOwner} ${wordCount}"` : `content: ""`;
  const bottomRightContent = isPageNumberOnTopRight(props.referencingStyle) ? `content: ""` : "content: " + pageNumber(props.referencingStyle);
  return /* @__PURE__ */ jsx51(
    StyledPrintContent,
    {
      topLeftContent,
      topRightContent,
      bottomRightContent,
      children: props.children
    }
  );
}
var StyledPrintContent = styled5.div`
  @media print {
    zoom: 0.9;
  }
  @page {
    size: A4;
    margin-top: 0.5in;
    margin-bottom: 0.5in;
    margin-left: 0;
    margin-right: 0;

    @top-right {
      ${(props) => props.topRightContent};
      font-family: ${typography2.bodyMd.fontFamily} sans-serif;
      font-size: 10px;
      color: ${colors16.black100};
      margin-right: 1.5cm;
      margin-top: 0.5cm;
    }
    @top-left {
      ${(props) => props.topLeftContent};
      font-family: ${typography2.bodyMd.fontFamily} sans-serif;
      font-size: 10px;
      color: ${colors16.black100};
      margin-left: 1.5cm;
      margin-top: 0.5cm;
    }
    @bottom-right {
      ${(props) => props.bottomRightContent};
      font-family: ${typography2.bodyMd.fontFamily} sans-serif;
      font-size: 10px;
      color: ${colors16.black100};
      margin-right: 1.5cm;
      margin-bottom: 0.5cm;
    }
  }
`;

// src/printing/components/print-container.tsx
import {
  CadmusKitServer as CadmusKitServer5,
  Editor as Editor2,
  editorFootnoteWordCount,
  editorWordCount
} from "@vericus/cadmus-editor-prosemirror/server";
import { CssBaseline, CUIProvider, defaultTheme } from "@vericus/cadmus-ui";
import { jsx as jsx52, jsxs as jsxs38 } from "react/jsx-runtime";
function PrintContainer(props) {
  const {
    children,
    pdfMeta,
    referencingStyle,
    classicEditorContents: classicEditorContents2 = [],
    displayWordCount = false
  } = props;
  const nf = new Intl.NumberFormat("en-us");
  const validReferencingStyle = validateReferencingStyle(referencingStyle);
  const hasReferences = classicEditorContents2.some(
    ({ id }) => id === "references"
  );
  let bodyWordCount = 0;
  let referencesWordCount = 0;
  let footnotesWordCount = 0;
  if (displayWordCount) {
    classicEditorContents2.forEach(({ id, content: content4 }) => {
      if (id === "body") {
        const bodyEditor = new Editor2({
          extensions: [CadmusKitServer5.configure({ editorId: "body" })],
          content: content4
        });
        bodyWordCount = editorWordCount(bodyEditor)?.total ?? 0;
        footnotesWordCount = editorFootnoteWordCount(bodyEditor.state);
        bodyEditor.destroy();
      } else if (id === "references") {
        const referencesEditor = new Editor2({
          extensions: [CadmusKitServer5.configure({ editorId: "references" })],
          content: content4
        });
        referencesWordCount = editorWordCount(referencesEditor)?.total ?? 0;
        referencesEditor.destroy();
      }
    });
  }
  const showFootnotesWordCount = isFootnotingEnabled(validReferencingStyle) || footnotesWordCount > 0;
  return /* @__PURE__ */ jsxs38(CUIProvider, { theme: defaultTheme, children: [
    /* @__PURE__ */ jsx52(CssBaseline, {}),
    /* @__PURE__ */ jsx52(
      PrintContent,
      {
        referencingStyle: validReferencingStyle,
        hasReferences,
        bodyWordCount: displayWordCount ? nf.format(bodyWordCount) : null,
        referencesWordCount: displayWordCount ? nf.format(referencesWordCount) : null,
        footnotesWordCount: displayWordCount && showFootnotesWordCount ? nf.format(footnotesWordCount) : null,
        ...pdfMeta,
        children
      }
    )
  ] });
}

// src/printing/render-classic-work.tsx
import { jsx as jsx53 } from "react/jsx-runtime";
function renderClassicWork(props) {
  const sheet = new ServerStyleSheet();
  let html = renderToString(
    sheet.collectStyles(
      /* @__PURE__ */ jsx53(
        PrintContainer,
        {
          classicEditorContents: props.contents,
          pdfMeta: props.pdfMeta,
          referencingStyle: props.referencingStyle,
          displayWordCount: true,
          children: /* @__PURE__ */ jsx53(
            EditorContentPreview,
            {
              editors: props.contents,
              hasHangingIndent: props.hasHangingIndent
            }
          )
        }
      )
    )
  );
  const styles = sheet.getStyleTags();
  html = html.replace(
    // eslint-disable-next-line no-irregular-whitespace
    /[\u00A0\u1680​\u180e\u2000-\u2009\u200a​\u200b​\u202f\u205f​\u3000]/g,
    " "
  ).replace(/&nbsp;/g, " ");
  const studentId = props.pdfMeta?.studentId ?? "";
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Cadmus Submission ${studentId}</title>${styles}${fontStyle}</head><body>${html}</body></html>`;
}

// src/printing/render-work-manifest.tsx
import { ServerStyleSheet as ServerStyleSheet2 } from "styled-components";
import { CadmusKitServer as CadmusKitServer6 } from "@vericus/cadmus-editor-prosemirror/server";
import {
  buildRootTheme,
  createCuiTheme,
  defaultTheme as defaultTheme2
} from "@vericus/cadmus-ui";
import * as ReactDOMServer from "react-dom/server";
import { jsx as jsx54 } from "react/jsx-runtime";
function renderWorkManifest(manifest, workOutcome, assessment, options, extraStyles) {
  const mode = workDocumentPreviewMode(options);
  const referencingStyle = assessment.settings.referencing_style ?? null;
  const contents = classicEditorContents(
    manifest,
    assessment.settings,
    options?.withNotes
  );
  const pdfMeta = {
    studentId: manifest.student_sis_id || manifest.student?.sis_id || null,
    groupName: manifest.group?.name || null
  };
  let preview = null;
  switch (manifest.format) {
    case "classic":
      preview = /* @__PURE__ */ jsx54(
        PrintContainer,
        {
          classicEditorContents: contents,
          pdfMeta,
          referencingStyle,
          displayWordCount: true,
          children: /* @__PURE__ */ jsx54(
            WorkManifestClassicPreview,
            {
              manifest,
              workOutcome,
              assessment,
              withCriterionOutcomes: options?.criterionOutcomes ?? false,
              withFeedbackComments: options?.feedback ?? false
            }
          )
        }
      );
      break;
    case "multiformat":
    default:
      preview = /* @__PURE__ */ jsx54(PrintContainer, { pdfMeta, children: /* @__PURE__ */ jsx54(
        WorkManifestMFAPreview,
        {
          manifest,
          assessment,
          workOutcome,
          previewMode: mode,
          extensions: [
            // Read-only surface, no paste risk: always render saved font marks.
            CadmusKitServer6.configure({
              enableBlanks: true,
              enableFontControls: true
            })
          ],
          withPromptDoc: options?.question === true,
          withMarkingSummary: options?.markingSummary ?? false,
          withAutoMarkConditions: options?.rubric ?? false,
          withExtendedAnswersOnly: options?.questionTypes === "extended",
          withOverviewBlock: options?.overview ?? false,
          withFeedbackSummary: options?.feedbackSummary ?? false,
          withTitle: options?.title ?? false,
          withFeedbackComments: options?.feedback ?? false
        }
      ) });
  }
  const sheet = new ServerStyleSheet2();
  let html = ReactDOMServer.renderToString(sheet.collectStyles(preview));
  html = html.replace(
    // eslint-disable-next-line no-irregular-whitespace
    /[\u00A0\u1680​\u180e\u2000-\u2009\u200a​\u200b​\u202f\u205f​\u3000]/g,
    " "
  ).replace(/&nbsp;/g, " ");
  const theme = createCuiTheme(defaultTheme2);
  const cadmusCommonRootStyling = `<style>${buildRootTheme(theme)}</style>`;
  const styles = cadmusCommonRootStyling + sheet.getStyleTags() + (extraStyles ? `<style>${extraStyles}</style>` : "") + fontStyle;
  const workOwnerId = manifest.student_sis_id || manifest.student?.sis_id || manifest.student?.id || manifest.group?.id || "";
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Cadmus Submission ${workOwnerId}</title>${styles}</head><body>${html}</body></html>`;
}
function workDocumentPreviewMode(options) {
  if (options?.answer === "score") {
    return "marked";
  }
  if (options?.answer === "outcome") {
    return "feedback";
  }
  if (options?.answer == "student" || options?.questionTypes === "extended") {
    return "answer";
  }
  if (options?.question === true) {
    return "question";
  }
  return "answer";
}

// src/printing/render-print-instruction.tsx
import { ServerStyleSheet as ServerStyleSheet3 } from "styled-components";
import {
  buildRootTheme as buildRootTheme2,
  createCuiTheme as createCuiTheme2,
  defaultTheme as defaultTheme3
} from "@vericus/cadmus-ui";
import * as ReactDOMServer2 from "react-dom/server";
import { jsx as jsx55 } from "react/jsx-runtime";
function renderPrintInstruction(printInstruction, extraStyles) {
  const preview = /* @__PURE__ */ jsx55(PrintContainer, { children: /* @__PURE__ */ jsx55(PrintInstructionPreview, { instruction: printInstruction }) });
  const sheet = new ServerStyleSheet3();
  let html = ReactDOMServer2.renderToString(sheet.collectStyles(preview));
  html = html.replace(
    // eslint-disable-next-line no-irregular-whitespace
    /[\u00A0\u1680​\u180e\u2000-\u2009\u200a​\u200b​\u202f\u205f​\u3000]/g,
    " "
  ).replace(/&nbsp;/g, " ");
  const theme = createCuiTheme2(defaultTheme3);
  const cadmusCommonRootStyling = `<style>${buildRootTheme2(theme)}</style>`;
  const styles = cadmusCommonRootStyling + sheet.getStyleTags() + (extraStyles ? `<style>${extraStyles}</style>` : "") + fontStyle;
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Print Instruction</title>${styles}</head><body>${html}</body></html>`;
}

// src/timeline-scrubber/timeline-scrubber.tsx
import { useCallback as useCallback2, useEffect as useEffect2, useMemo as useMemo7, useRef as useRef2 } from "react";
import {
  PauseIcon,
  PlayMiniIcon,
  RewindMiniIcon,
  SkipBackMiniIcon,
  SkipForwardMiniIcon,
  SpeedMiniIcon
} from "@vericus/cadmus-icons";
import { IconButton } from "@vericus/cadmus-ui";
import { CloseButton, Slider } from "@mantine/core";

// src/timeline-scrubber/thin-marks.ts
var MAX_SLIDER_MARKS = 49;
function thinMarks(versions, max) {
  if (versions.length <= max) return [...versions];
  if (max <= 1) {
    const first = versions[0];
    return first === void 0 ? [] : [first];
  }
  const lastIndex = versions.length - 1;
  const sampled = [];
  for (let i = 0; i < max; i++) {
    const index = Math.round(i * lastIndex / (max - 1));
    const value = versions[index];
    if (value !== void 0) sampled.push(value);
  }
  return [...new Set(sampled)];
}

// src/timeline-scrubber/timeline-scrubber.css.ts
var bar = "_1rszsma0";
var mark = "_1rszsma8";
var playButton = "_1rszsma3 _1rszsma2";
var playerButton = "_1rszsma2";
var progress = "_1rszsma6";
var sliderWrap = "_1rszsma4";
var speedActive = "_1rszsmab _1rszsmaa";
var speedBtn = "_1rszsmaa";
var speedGroup = "_1rszsma9";
var thumb = "_1rszsma7";
var track = "_1rszsma5";
var transport = "_1rszsma1";

// src/timeline-scrubber/timeline-scrubber.tsx
import { jsx as jsx56, jsxs as jsxs39 } from "react/jsx-runtime";
var SPEEDS = [0.5, 1, 2];
var ICON = 24;
var SLIDER_CLASSNAMES = {
  track,
  bar: progress,
  thumb,
  mark
};
function formatTime2(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(void 0, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}
function playheadLabel(snapshots, version, hasRealZero = false) {
  if (version === 0) {
    const zero = hasRealZero ? snapshots.find((s) => s.version === 0) : void 0;
    return zero == null ? "Start" : formatTime2(zero.timestamp);
  }
  const exact = snapshots.find((s) => s.version === version);
  if (exact != null) return formatTime2(exact.timestamp);
  return timeLabel(snapshots, version);
}
function timeLabel(snapshots, version) {
  const real = snapshots.filter((s) => s.version > 0);
  const firstReal = real[0];
  if (firstReal == null) return "";
  if (version < firstReal.version)
    return `before ${formatTime2(firstReal.timestamp)}`;
  const exact = real.find((s) => s.version === version);
  if (exact != null) return formatTime2(exact.timestamp);
  for (let i = 0; i < real.length - 1; i++) {
    const lo = real[i];
    const hi = real[i + 1];
    if (lo != null && hi != null && lo.version < version && version < hi.version) {
      return `${formatTime2(lo.timestamp)} \u2013 ${formatTime2(hi.timestamp)}`;
    }
  }
  const last = real[real.length - 1];
  return last != null ? `after ${formatTime2(last.timestamp)}` : "";
}
function prevTickVersion(ticks, current) {
  let best = 0;
  for (const v of ticks) if (v < current && v > best) best = v;
  return best;
}
function nextTickVersion(ticks, current, head) {
  let best = head;
  for (const v of ticks) if (v > current && v < best) best = v;
  return best;
}
function TimelineScrubber(props) {
  const {
    snapshots,
    headVersion,
    activeVersion,
    onVersionChange,
    onClose,
    playing,
    onTogglePlay,
    speed,
    onSpeedChange,
    previousLabel = "Previous keyframe",
    nextLabel = "Next keyframe",
    thumbLabel = "Version",
    hasRealZero = false
  } = props;
  const showPlayback = onTogglePlay != null;
  const showSpeed = onSpeedChange != null;
  const isPlaying = playing ?? false;
  const marks = useMemo7(
    () => thinMarks(
      snapshots.map((s) => s.version),
      MAX_SLIDER_MARKS
    ).map((value) => ({ value })),
    [snapshots]
  );
  const ticks = useMemo7(() => snapshots.map((s) => s.version), [snapshots]);
  const rafRef = useRef2(null);
  const pendingRef = useRef2(null);
  const onSliderChange = useCallback2(
    (v) => {
      pendingRef.current = v;
      if (rafRef.current != null) return;
      rafRef.current = window.requestAnimationFrame(() => {
        rafRef.current = null;
        if (pendingRef.current != null) onVersionChange(pendingRef.current);
      });
    },
    [onVersionChange]
  );
  useEffect2(
    () => () => {
      if (rafRef.current != null) window.cancelAnimationFrame(rafRef.current);
    },
    []
  );
  if (headVersion == null || headVersion < 0 || snapshots.length === 0) {
    return null;
  }
  const atStart = activeVersion <= 0;
  const atEnd = activeVersion >= headVersion;
  return /* @__PURE__ */ jsxs39("div", { className: bar, children: [
    /* @__PURE__ */ jsxs39("div", { className: transport, children: [
      /* @__PURE__ */ jsx56(
        IconButton,
        {
          className: playerButton,
          shape: "square",
          "aria-label": "Skip to start",
          onClick: () => onVersionChange(0),
          disabled: atStart,
          children: /* @__PURE__ */ jsx56(RewindMiniIcon, { label: "", size: ICON })
        }
      ),
      showPlayback && /* @__PURE__ */ jsx56(
        IconButton,
        {
          className: playButton,
          shape: "square",
          "aria-label": isPlaying ? "Pause" : "Play",
          onClick: onTogglePlay,
          children: isPlaying ? /* @__PURE__ */ jsx56(PauseIcon, { label: "", size: ICON }) : /* @__PURE__ */ jsx56(PlayMiniIcon, { label: "", size: ICON })
        }
      ),
      /* @__PURE__ */ jsx56(
        IconButton,
        {
          shape: "square",
          className: playerButton,
          "aria-label": previousLabel,
          onClick: () => onVersionChange(prevTickVersion(ticks, activeVersion)),
          disabled: atStart,
          children: /* @__PURE__ */ jsx56(SkipBackMiniIcon, { label: "", size: ICON })
        }
      ),
      /* @__PURE__ */ jsx56(
        IconButton,
        {
          className: playerButton,
          shape: "square",
          "aria-label": nextLabel,
          onClick: () => onVersionChange(nextTickVersion(ticks, activeVersion, headVersion)),
          disabled: atEnd,
          children: /* @__PURE__ */ jsx56(SkipForwardMiniIcon, { label: "", size: ICON })
        }
      )
    ] }),
    /* @__PURE__ */ jsx56("div", { className: sliderWrap, children: /* @__PURE__ */ jsx56(
      Slider,
      {
        min: 0,
        max: headVersion,
        step: 1,
        value: activeVersion,
        marks,
        thumbSize: 16,
        label: playheadLabel(snapshots, activeVersion, hasRealZero),
        thumbLabel,
        onChange: onSliderChange,
        classNames: SLIDER_CLASSNAMES
      }
    ) }),
    showSpeed && /* @__PURE__ */ jsx56("div", { className: speedGroup, children: SPEEDS.map((s) => /* @__PURE__ */ jsxs39(
      "button",
      {
        type: "button",
        className: s === speed ? speedActive : speedBtn,
        onClick: () => onSpeedChange?.(s),
        "aria-label": `${s}x speed`,
        "aria-pressed": s === speed,
        children: [
          s,
          "x"
        ]
      },
      s
    )) }),
    /* @__PURE__ */ jsxs39("div", { className: transport, children: [
      /* @__PURE__ */ jsx56(
        IconButton,
        {
          className: playerButton,
          shape: "square",
          "aria-label": "Skip to end",
          onClick: () => onVersionChange(headVersion),
          disabled: atEnd,
          children: /* @__PURE__ */ jsx56(SpeedMiniIcon, { label: "", size: ICON })
        }
      ),
      /* @__PURE__ */ jsx56(
        CloseButton,
        {
          "aria-label": "Hide the timeline",
          onClick: onClose,
          size: "md"
        }
      )
    ] })
  ] });
}

// src/insights/checkbox-filters.tsx
import { FilterIcon } from "@vericus/cadmus-icons";
import { Command, Divider as Divider6, LinkButton as LinkButton2, Text as Text36 } from "@vericus/cadmus-ui";

// src/insights/insights-chart.css.ts
var filterButton = "dsjaite";
var filterContainer = "dsjaitb";
var filterHeader = "dsjaith";
var filterHeaderTitle = "dsjaiti";
var filterInput = "dsjaitj";
var loadingContainer = "dsjaitf";

// src/insights/checkbox-filters.tsx
import { Fragment as Fragment18, jsx as jsx57, jsxs as jsxs40 } from "react/jsx-runtime";
function CheckBoxFilters(props) {
  const { title: title2, selectedFilters, setSelectedFilters, filters } = props;
  const onClearFilters = () => {
    setSelectedFilters([]);
  };
  const onSelectAll = () => {
    setSelectedFilters(filters.map((filter) => filter.id));
  };
  return /* @__PURE__ */ jsxs40(Fragment18, { children: [
    /* @__PURE__ */ jsxs40("div", { className: filterHeader, children: [
      /* @__PURE__ */ jsxs40("div", { className: filterHeaderTitle, children: [
        /* @__PURE__ */ jsx57(FilterIcon, { label: "Filter" }),
        /* @__PURE__ */ jsx57(Text36, { kind: "headingSix", children: "Filters" })
      ] }),
      /* @__PURE__ */ jsx57(LinkButton2, { kind: "primary", onClick: onClearFilters, children: "Reset" })
    ] }),
    /* @__PURE__ */ jsxs40("div", { className: filterContainer, children: [
      /* @__PURE__ */ jsx57(Text36, { kind: "headingSix", children: title2 }),
      /* @__PURE__ */ jsx57(Command.Root, { children: /* @__PURE__ */ jsxs40(Command.List, { children: [
        /* @__PURE__ */ jsx57(
          Command.Input,
          {
            placeholder: "Search...",
            withMargin: false,
            className: filterInput
          }
        ),
        /* @__PURE__ */ jsx57(Command.Empty, { children: /* @__PURE__ */ jsxs40(Text36, { kind: "bodyMd", textAlign: "left", color: "black100", children: [
          "No ",
          title2,
          " found"
        ] }) }),
        /* @__PURE__ */ jsx57(Command.Group, { style: { overflowY: "auto" }, children: filters.map((filter) => {
          return /* @__PURE__ */ jsx57(
            Command.Item,
            {
              checkable: true,
              "data-selected": selectedFilters.includes(filter.id) ? "true" : void 0,
              onSelect: () => {
                if (selectedFilters.includes(filter.id)) {
                  setSelectedFilters(
                    selectedFilters.filter((id) => id !== filter.id)
                  );
                } else {
                  setSelectedFilters([...selectedFilters, filter.id]);
                }
              },
              children: /* @__PURE__ */ jsx57("div", { style: { padding: "8px 0px" }, children: /* @__PURE__ */ jsx57(
                Text36,
                {
                  kind: "bodyMd",
                  textAlign: "left",
                  color: "black100",
                  style: {
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  },
                  children: filter.name
                }
              ) })
            },
            filter.id
          );
        }) })
      ] }) }),
      /* @__PURE__ */ jsx57(Divider6, {}),
      /* @__PURE__ */ jsxs40("div", { className: filterButton, children: [
        /* @__PURE__ */ jsx57(LinkButton2, { kind: "primary", onClick: onSelectAll, children: "Select all" }),
        /* @__PURE__ */ jsx57(LinkButton2, { onClick: onClearFilters, kind: "primary", children: "Clear all" })
      ] })
    ] })
  ] });
}

// src/insights/chart-loading.tsx
import { LoaderIcon } from "@vericus/cadmus-icons";
import { Text as Text37 } from "@vericus/cadmus-ui";
import { jsx as jsx58, jsxs as jsxs41 } from "react/jsx-runtime";
function ChartLoading() {
  return /* @__PURE__ */ jsxs41("div", { className: loadingContainer, children: [
    /* @__PURE__ */ jsx58(LoaderIcon, { size: 24, label: "Loading" }),
    /* @__PURE__ */ jsx58(Text37, { kind: "bodyMd", color: "grey300", children: "Preparing data..." })
  ] });
}

// src/insights/comments-chart.tsx
import { colors as colors17, Divider as Divider7, Text as Text39 } from "@vericus/cadmus-ui";
import {
  Bar,
  BarChart,
  LabelList,
  Rectangle,
  Tooltip as Tooltip2,
  XAxis
} from "recharts";

// src/insights/axis-name-tick.tsx
import { Tooltip } from "@vericus/cadmus-ui";
import { Text as Text38 } from "recharts";

// src/insights/axis-name-tick.css.ts
var axisNameTick = "bvjcgv0";

// src/insights/utils.ts
function getItemName(list, id, slice) {
  let name = list.find((listItem) => listItem.id === id)?.name ?? "";
  if (slice && name.length > slice) {
    name = name.slice(0, slice) + "...";
  }
  return name;
}
function structureTaskSummaryData(data, xAxisKey) {
  if (!data || data.length === 0) return [];
  if (xAxisKey === "user_id") {
    return data[0]?.students.map((student) => ({
      completed: student.completed,
      user_id: student.user_id,
      overdue: student.overdue,
      onTrack: student.on_track,
      total: student.total
    })) ?? [];
  }
  const groupedByGroup = data.map((item) => {
    const students = item.students.reduce(
      (acc, student) => {
        return {
          completed: acc.completed + student.completed,
          overdue: acc.overdue + student.overdue,
          onTrack: acc.onTrack + student.on_track,
          total: acc.total + student.total
        };
      },
      { overdue: 0, completed: 0, total: 0, onTrack: 0 }
    );
    return {
      group_id: item.group_id,
      ...students
    };
  });
  return Object.values(groupedByGroup);
}
function structureThreadsSummaryData(data, list, xAxisKey) {
  if (!data || data.length === 0) return [];
  if (xAxisKey === "user_id") {
    const studentData = data[0]?.threads ?? [];
    const group_id = data[0]?.group_id;
    const noDataUsers = list.filter(
      (user) => !studentData.some((t) => t.user_id === user.id)
    );
    return [
      ...studentData.map((thread) => ({
        ...thread,
        group_id
      })),
      ...noDataUsers.map((user) => ({
        user_id: user.id,
        group_id,
        resolved: 0,
        unresolved: 0,
        total: 0,
        deleted: 0
      }))
    ];
  }
  return data.map((item) => ({
    group_id: item.group_id,
    ...item.threads.reduce(
      (acc, thread) => ({
        resolved: acc.resolved + thread.resolved,
        unresolved: acc.unresolved + thread.unresolved,
        total: acc.total + thread.total,
        deleted: acc.deleted + thread.deleted
      }),
      { resolved: 0, unresolved: 0, total: 0, deleted: 0 }
    )
  }));
}
function structWordCountData(data, cumulative = true) {
  const groupedByDate = data.reduce(
    (acc, item) => {
      const date = item.date;
      if (!acc[date]) {
        acc[date] = {};
      }
      acc[date][item.user_id] = (acc[date][item.user_id] || 0) + item.word_count;
      return acc;
    },
    {}
  );
  const sorted = Object.entries(groupedByDate).sort(
    ([a], [b]) => a.localeCompare(b)
  );
  if (!cumulative) {
    const allUserIds = [...new Set(data.map((d) => d.user_id))];
    const lastKnown = {};
    return sorted.map(([date, users]) => {
      const row3 = { date };
      for (const id of allUserIds) {
        const val = users[id];
        if (val !== void 0) {
          lastKnown[id] = val;
        }
        const known = lastKnown[id];
        if (known !== void 0) {
          row3[id] = known;
        }
      }
      return row3;
    });
  }
  const runningTotals = {};
  return sorted.map(([date, users]) => {
    for (const userId of Object.keys(users)) {
      runningTotals[userId] = (runningTotals[userId] || 0) + (users[userId] ?? 0);
    }
    return { date, ...runningTotals };
  });
}
function structPasteRatioData(data) {
  return structCumulativePasteRatio(data, (item) => item.user_id);
}
function structCumulativePasteRatio(data, getKey) {
  const sorted = [...data].sort((a, b) => a.date.localeCompare(b.date));
  const cumPaste = {};
  const cumTyped = {};
  const result = [];
  let lastDate = "";
  for (const item of sorted) {
    const key = getKey(item);
    const hasDeltas = item.paste_added != null || item.type_added != null;
    if (item.date !== lastDate) {
      lastDate = item.date;
      result.push({ date: item.date });
    }
    const entry = result[result.length - 1];
    if (hasDeltas) {
      cumPaste[key] = (cumPaste[key] || 0) + (item.paste_added ?? 0);
      cumTyped[key] = (cumTyped[key] || 0) + (item.type_added ?? 0);
      const total2 = cumPaste[key] + cumTyped[key];
      entry[key] = total2 > 0 ? Number((cumPaste[key] / total2 * 100).toFixed(2)) : 0;
    } else {
      entry[key] = Number(item.paste_ratio.toFixed(2));
    }
  }
  return result;
}
function getUsers(data) {
  const group = /* @__PURE__ */ new Set();
  data.forEach((item) => {
    group.add(item.user_id);
  });
  return Array.from(group);
}
function getChartWidth(dataLength) {
  if (dataLength > 3) {
    return dataLength * 100;
  }
  if (dataLength === 3) {
    return dataLength * 125;
  }
  if (dataLength === 2) {
    return dataLength * 135;
  }
  return 200;
}
function getNiceAxisMax(maxValue, tickCount = 5) {
  if (maxValue <= 0) return 1;
  const rawInterval = maxValue / (tickCount - 1);
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawInterval)));
  const residual = rawInterval / magnitude;
  let niceInterval;
  if (residual <= 1) niceInterval = magnitude;
  else if (residual <= 2) niceInterval = 2 * magnitude;
  else if (residual <= 2.5) niceInterval = 2.5 * magnitude;
  else if (residual <= 5) niceInterval = 5 * magnitude;
  else niceInterval = 10 * magnitude;
  return niceInterval * (tickCount - 1);
}
function getClosestPayloadItem(payload, chartY, chartHeight, margin, globalMax) {
  const withValues = payload.filter(
    (p) => p.value != null
  );
  if (withValues.length === 0) return null;
  if (withValues.length === 1) return withValues[0] ?? null;
  const plotTop = margin.top;
  const plotHeight = chartHeight - margin.top - margin.bottom;
  const maxVal = getNiceAxisMax(globalMax);
  let closest = null;
  let minDist = Infinity;
  for (const item of withValues) {
    const pixelY = plotTop + plotHeight * (1 - item.value / maxVal);
    const dist = Math.abs(pixelY - chartY);
    if (dist < minDist) {
      minDist = dist;
      closest = item;
    }
  }
  return closest;
}

// src/insights/axis-name-tick.tsx
import { jsx as jsx59, jsxs as jsxs42 } from "react/jsx-runtime";
var AXIS_NAME_LENGTH = 10;
var LABEL_WIDTH = 80;
var LABEL_HEIGHT2 = 48;
function AxisNameTick(props) {
  const {
    list,
    payload,
    x = 0,
    y = 0,
    textAnchor,
    verticalAnchor,
    fill
  } = props;
  const fullName = getItemName(list, payload?.value ?? "");
  if (fullName === "") return null;
  return /* @__PURE__ */ jsx59(Tooltip, { content: fullName, children: /* @__PURE__ */ jsxs42(
    "g",
    {
      tabIndex: 0,
      role: "img",
      "aria-label": fullName,
      className: axisNameTick,
      children: [
        /* @__PURE__ */ jsx59(
          "rect",
          {
            x: x - LABEL_WIDTH / 2,
            y,
            width: LABEL_WIDTH,
            height: LABEL_HEIGHT2,
            fill: "transparent"
          }
        ),
        /* @__PURE__ */ jsx59(
          Text38,
          {
            x,
            y,
            width: LABEL_WIDTH,
            fill,
            fontSize: "18px",
            textAnchor,
            verticalAnchor,
            children: getItemName(list, payload?.value ?? "", AXIS_NAME_LENGTH)
          }
        )
      ]
    }
  ) });
}

// src/insights/comments-chart.css.ts
var circleIndicator = "_8thgo2";
var legend = "_8thgo1";
var tooltipContainer = "_8thgo3";
var tooltipItem = "_8thgo4";

// src/insights/comments-chart.tsx
import { Fragment as Fragment19, jsx as jsx60, jsxs as jsxs43 } from "react/jsx-runtime";
function CommentsChart(props) {
  const { xAxisKey, list } = props;
  const data = structureThreadsSummaryData(props.data, list, xAxisKey);
  const barchartWidth = getChartWidth(data?.length || 0) - 70;
  return /* @__PURE__ */ jsxs43(Fragment19, { children: [
    /* @__PURE__ */ jsx60(
      "div",
      {
        style: { overflowX: "scroll", maxWidth: "913px", overflowY: "hidden" },
        children: /* @__PURE__ */ jsxs43(
          BarChart,
          {
            width: barchartWidth,
            height: 320,
            data,
            margin: {
              top: 20,
              right: 20,
              bottom: 40,
              left: 20
            },
            barSize: 60,
            barGap: 8,
            children: [
              /* @__PURE__ */ jsx60(
                XAxis,
                {
                  dataKey: xAxisKey,
                  axisLine: false,
                  tickLine: false,
                  interval: 0,
                  tick: /* @__PURE__ */ jsx60(AxisNameTick, { list }),
                  textAnchor: "middle",
                  tickSize: 20
                }
              ),
              /* @__PURE__ */ jsx60(
                Tooltip2,
                {
                  content: /* @__PURE__ */ jsx60(CustomTooltip, {}),
                  cursor: false,
                  animationDuration: 100
                }
              ),
              /* @__PURE__ */ jsx60(
                Bar,
                {
                  dataKey: "resolved",
                  stackId: "a",
                  fill: colors17.lilac300,
                  activeBar: /* @__PURE__ */ jsx60(Rectangle, { fill: colors17.lilac400 }),
                  background: { fill: colors17.bgrey300 }
                }
              ),
              /* @__PURE__ */ jsx60(
                Bar,
                {
                  dataKey: "unresolved",
                  stackId: "a",
                  fill: colors17.poppy300,
                  activeBar: /* @__PURE__ */ jsx60(Rectangle, { fill: colors17.poppy400 })
                }
              ),
              /* @__PURE__ */ jsx60(
                Bar,
                {
                  dataKey: "deleted",
                  stackId: "a",
                  fill: colors17.navy200,
                  activeBar: /* @__PURE__ */ jsx60(Rectangle, { fill: colors17.navy300 }),
                  children: /* @__PURE__ */ jsx60(LabelList, { dataKey: "total", position: "top" })
                }
              )
            ]
          }
        )
      }
    ),
    /* @__PURE__ */ jsx60(Divider7, {}),
    /* @__PURE__ */ jsxs43("div", { className: legend, children: [
      /* @__PURE__ */ jsx60(
        "div",
        {
          className: circleIndicator,
          style: { backgroundColor: colors17.lilac300 }
        }
      ),
      /* @__PURE__ */ jsx60(Text39, { kind: "systemSm", color: "grey500", bold: true, children: "Resolved" }),
      /* @__PURE__ */ jsx60(
        "div",
        {
          className: circleIndicator,
          style: { backgroundColor: colors17.poppy300 }
        }
      ),
      /* @__PURE__ */ jsx60(Text39, { kind: "systemSm", color: "grey500", bold: true, children: "Unresolved" }),
      /* @__PURE__ */ jsx60(
        "div",
        {
          className: circleIndicator,
          style: { backgroundColor: colors17.navy300 }
        }
      ),
      /* @__PURE__ */ jsx60(Text39, { kind: "systemSm", color: "grey500", bold: true, children: "Deleted" })
    ] })
  ] });
}
function CustomTooltip({ active, payload }) {
  if (active && payload && payload.length && payload[0]?.payload) {
    const { resolved, unresolved, deleted, total: total2 } = payload[0].payload;
    return /* @__PURE__ */ jsxs43("div", { className: tooltipContainer, children: [
      /* @__PURE__ */ jsxs43(
        Text39,
        {
          kind: "label",
          textTransform: "capitalize",
          as: "div",
          className: tooltipItem,
          children: [
            "Total ",
            /* @__PURE__ */ jsx60(Text39, { kind: "bodyLine", children: total2 })
          ]
        }
      ),
      /* @__PURE__ */ jsx60(Divider7, {}),
      /* @__PURE__ */ jsxs43(Text39, { kind: "systemSm", as: "div", className: tooltipItem, children: [
        "Resolved",
        " ",
        /* @__PURE__ */ jsx60(Text39, { kind: "bodyLine", as: "span", children: resolved })
      ] }),
      /* @__PURE__ */ jsxs43(Text39, { kind: "systemSm", as: "div", className: tooltipItem, children: [
        "Unresolved",
        " ",
        /* @__PURE__ */ jsx60(Text39, { kind: "bodyLine", as: "span", children: unresolved })
      ] }),
      /* @__PURE__ */ jsxs43(Text39, { kind: "systemSm", as: "div", className: tooltipItem, children: [
        "Deleted",
        " ",
        /* @__PURE__ */ jsx60(Text39, { kind: "bodyLine", as: "span", children: deleted })
      ] })
    ] });
  }
  return null;
}

// src/insights/user-word-count-chart.tsx
import { useState as useState4 } from "react";
import { colors as colors18, Divider as Divider8, Text as Text40 } from "@vericus/cadmus-ui";
import {
  Dot,
  Line,
  LineChart,
  ReferenceLine,
  Tooltip as Tooltip3,
  XAxis as XAxis2,
  YAxis
} from "recharts";

// src/insights/user-word-count-chart.css.ts
var circleIndicator2 = "l8asdc1";
var container6 = "l8asdc0";
var legend2 = "l8asdc2";
var legendItem = "l8asdc3";
var tooltip = "l8asdc4";

// src/insights/user-word-count-chart.tsx
import { Fragment as Fragment20, jsx as jsx61, jsxs as jsxs44 } from "react/jsx-runtime";
var CHART_HEIGHT = 280;
var CHART_MARGIN = { top: 20, right: 20, bottom: 20, left: 20 };
function UserWordCountChart({
  data,
  users,
  cumulative = true
}) {
  const [hoveredKey, setHoveredKey] = useState4(null);
  const [selectedKey, setSelectedKey] = useState4(null);
  const activeKey = selectedKey ?? hoveredKey;
  if (!data || data.length === 0) {
    return /* @__PURE__ */ jsx61("div", { className: container6, children: /* @__PURE__ */ jsx61(Text40, { color: "grey300", kind: "bodyLg", textAlign: "center", children: "No data available" }) });
  }
  const usersIds = getUsers(data);
  const structuredData = structWordCountData(data, cumulative);
  const globalMax = Math.max(
    ...structuredData.flatMap(
      (d) => usersIds.map((id) => {
        const val = d[id];
        return typeof val === "number" ? val : 0;
      })
    )
  );
  const barchartWidth = getChartWidth(structuredData.length);
  return /* @__PURE__ */ jsxs44(Fragment20, { children: [
    /* @__PURE__ */ jsx61(
      "div",
      {
        style: { overflowX: "scroll", maxWidth: "913px", overflowY: "hidden" },
        children: /* @__PURE__ */ jsxs44(
          LineChart,
          {
            width: barchartWidth,
            height: CHART_HEIGHT,
            data: structuredData,
            margin: CHART_MARGIN,
            barGap: 10,
            onMouseMove: (state) => {
              const s = state;
              if (s?.activePayload && typeof s.chartY === "number") {
                const closest = getClosestPayloadItem(
                  s.activePayload,
                  s.chartY,
                  CHART_HEIGHT,
                  CHART_MARGIN,
                  globalMax
                );
                setHoveredKey(
                  closest?.dataKey ?? null
                );
              }
            },
            onMouseLeave: () => {
              setHoveredKey(null);
            },
            children: [
              /* @__PURE__ */ jsx61(
                XAxis2,
                {
                  dataKey: "date",
                  axisLine: false,
                  tickLine: false,
                  padding: { left: 40, right: 40 },
                  width: 100,
                  tickFormatter: (value) => {
                    return new Date(value).toLocaleDateString("en-AU", {
                      month: "short",
                      day: "numeric"
                    });
                  }
                }
              ),
              /* @__PURE__ */ jsx61(YAxis, { axisLine: false, tickLine: false }),
              /* @__PURE__ */ jsx61(
                Tooltip3,
                {
                  content: /* @__PURE__ */ jsx61(
                    CustomTooltip2,
                    {
                      users,
                      activeKey
                    }
                  ),
                  cursor: false,
                  animationDuration: 100
                }
              ),
              structuredData.map((item) => /* @__PURE__ */ jsx61(
                ReferenceLine,
                {
                  x: item.date,
                  strokeWidth: 60,
                  stroke: colors18.bgrey300
                },
                item.date
              )),
              usersIds.map((userId, index) => /* @__PURE__ */ jsx61(
                Line,
                {
                  type: "monotone",
                  animationDuration: 500,
                  dataKey: userId,
                  layout: "vertical",
                  stroke: getChartColourset(index),
                  dot: /* @__PURE__ */ jsx61(Dot, { cx: 0, cy: 0, r: 4, fill: getChartColourset(index) }),
                  strokeWidth: activeKey === userId ? 3 : 1,
                  activeDot: activeKey === userId ? { r: 8, fill: getChartColourset(index) } : false
                },
                userId
              ))
            ]
          }
        )
      }
    ),
    /* @__PURE__ */ jsx61(Divider8, {}),
    /* @__PURE__ */ jsx61("div", { className: legend2, children: usersIds.map((userId, index) => /* @__PURE__ */ jsxs44(
      "button",
      {
        className: legendItem,
        onClick: () => setSelectedKey((prev) => prev === userId ? null : userId),
        "aria-pressed": selectedKey === userId,
        children: [
          /* @__PURE__ */ jsx61(
            "div",
            {
              className: circleIndicator2,
              style: { backgroundColor: getChartColourset(index) }
            }
          ),
          /* @__PURE__ */ jsx61(Text40, { kind: "systemSm", color: "grey500", bold: selectedKey === userId, children: getItemName(users, userId) })
        ]
      },
      userId
    )) })
  ] });
}
var chartColoursets = [
  colors18.poppy500,
  colors18.nteal500,
  colors18.npink500,
  colors18.purple500,
  colors18.teal500,
  colors18.blue500,
  colors18.yellow500
];
function getChartColourset(index) {
  if (index === null) return colors18.primary;
  return chartColoursets[index % chartColoursets.length] || colors18.primary;
}
var CustomTooltip2 = ({
  active,
  payload,
  users,
  activeKey
}) => {
  if (active && payload && payload.length) {
    const item = payload.find((p) => p.dataKey === activeKey) ?? payload[0];
    if (!item) return null;
    return /* @__PURE__ */ jsx61("div", { className: tooltip, children: /* @__PURE__ */ jsx61(
      Text40,
      {
        kind: "systemSm",
        color: "grey500",
        bold: true,
        children: `${getItemName(users, item.name ?? "")}: ${item.value}`
      }
    ) });
  }
  return null;
};

// src/insights/user-paste-ratio-chart.tsx
import { useState as useState5 } from "react";
import { colors as colors19, Divider as Divider9, Text as Text41 } from "@vericus/cadmus-ui";
import {
  Dot as Dot2,
  Line as Line2,
  LineChart as LineChart2,
  ReferenceLine as ReferenceLine2,
  Tooltip as Tooltip4,
  XAxis as XAxis3,
  YAxis as YAxis2
} from "recharts";
import { Fragment as Fragment21, jsx as jsx62, jsxs as jsxs45 } from "react/jsx-runtime";
var CHART_HEIGHT2 = 280;
var CHART_MARGIN2 = { top: 20, right: 20, bottom: 20, left: 20 };
function UserPasteRatioChart({ data, users }) {
  const [hoveredKey, setHoveredKey] = useState5(null);
  const [selectedKey, setSelectedKey] = useState5(null);
  const activeKey = selectedKey ?? hoveredKey;
  if (!data || data.length === 0) {
    return /* @__PURE__ */ jsx62("div", { className: container6, children: /* @__PURE__ */ jsx62(Text41, { color: "grey300", kind: "bodyLg", textAlign: "center", children: "No data available" }) });
  }
  const usersIds = getUsers(data);
  const structuredData = structPasteRatioData(data);
  const globalMax = Math.max(
    ...structuredData.flatMap(
      (d) => usersIds.map((id) => {
        const val = d[id];
        return typeof val === "number" ? val : 0;
      })
    )
  );
  const barchartWidth = getChartWidth(structuredData.length);
  return /* @__PURE__ */ jsxs45(Fragment21, { children: [
    /* @__PURE__ */ jsx62(
      "div",
      {
        style: { overflowX: "scroll", maxWidth: "913px", overflowY: "hidden" },
        children: /* @__PURE__ */ jsxs45(
          LineChart2,
          {
            width: barchartWidth,
            height: CHART_HEIGHT2,
            data: structuredData,
            margin: CHART_MARGIN2,
            barGap: 10,
            onMouseMove: (state) => {
              const s = state;
              if (s?.activePayload && typeof s.chartY === "number") {
                const closest = getClosestPayloadItem(
                  s.activePayload,
                  s.chartY,
                  CHART_HEIGHT2,
                  CHART_MARGIN2,
                  globalMax
                );
                setHoveredKey(
                  closest?.dataKey ?? null
                );
              }
            },
            onMouseLeave: () => {
              setHoveredKey(null);
            },
            children: [
              /* @__PURE__ */ jsx62(
                XAxis3,
                {
                  dataKey: "date",
                  axisLine: false,
                  tickLine: false,
                  padding: { left: 40, right: 40 },
                  width: 100,
                  tickFormatter: (value) => {
                    return new Date(value).toLocaleDateString("en-AU", {
                      month: "short",
                      day: "numeric"
                    });
                  }
                }
              ),
              /* @__PURE__ */ jsx62(YAxis2, { axisLine: false, tickLine: false }),
              /* @__PURE__ */ jsx62(
                Tooltip4,
                {
                  content: /* @__PURE__ */ jsx62(
                    CustomTooltip3,
                    {
                      users,
                      activeKey
                    }
                  ),
                  cursor: false,
                  animationDuration: 100
                }
              ),
              structuredData.map((item) => /* @__PURE__ */ jsx62(
                ReferenceLine2,
                {
                  x: item.date,
                  strokeWidth: 60,
                  stroke: colors19.bgrey300
                },
                item.date
              )),
              usersIds.map((userId, index) => /* @__PURE__ */ jsx62(
                Line2,
                {
                  type: "monotone",
                  animationDuration: 500,
                  dataKey: userId,
                  layout: "vertical",
                  stroke: getChartColourset2(index),
                  strokeWidth: activeKey === userId ? 3 : 1,
                  dot: /* @__PURE__ */ jsx62(Dot2, { cx: 0, cy: 0, r: 4, fill: getChartColourset2(index) }),
                  activeDot: activeKey === userId ? { r: 8, fill: getChartColourset2(index) } : false
                },
                userId
              ))
            ]
          }
        )
      }
    ),
    /* @__PURE__ */ jsx62(Divider9, {}),
    /* @__PURE__ */ jsx62("div", { className: legend2, children: usersIds.map((userId, index) => /* @__PURE__ */ jsxs45(
      "button",
      {
        className: legendItem,
        onClick: () => setSelectedKey((prev) => prev === userId ? null : userId),
        "aria-pressed": selectedKey === userId,
        children: [
          /* @__PURE__ */ jsx62(
            "div",
            {
              className: circleIndicator2,
              style: { backgroundColor: getChartColourset2(index) }
            }
          ),
          /* @__PURE__ */ jsx62(Text41, { kind: "systemSm", color: "grey500", bold: selectedKey === userId, children: getItemName(users, userId) })
        ]
      },
      userId
    )) })
  ] });
}
var chartColoursets2 = [
  colors19.poppy500,
  colors19.nteal500,
  colors19.npink500,
  colors19.purple500,
  colors19.teal500,
  colors19.blue500,
  colors19.yellow500
];
function getChartColourset2(index) {
  if (index === null) return colors19.primary;
  return chartColoursets2[index % chartColoursets2.length] || colors19.primary;
}
var CustomTooltip3 = ({
  active,
  payload,
  users,
  activeKey
}) => {
  if (active && payload && payload.length) {
    const item = payload.find((p) => p.dataKey === activeKey) ?? payload[0];
    if (!item) return null;
    return /* @__PURE__ */ jsx62("div", { className: tooltip, children: /* @__PURE__ */ jsx62(
      Text41,
      {
        kind: "systemSm",
        color: "grey500",
        bold: true,
        children: `${getItemName(users, item.name ?? "")}: ${item.value}`
      }
    ) });
  }
  return null;
};

// src/insights/task-summary-chart.tsx
import { colors as colors20, Divider as Divider10, Text as Text42 } from "@vericus/cadmus-ui";
import {
  Bar as Bar2,
  BarChart as BarChart2,
  LabelList as LabelList2,
  Rectangle as Rectangle2,
  Tooltip as Tooltip5,
  XAxis as XAxis4
} from "recharts";

// src/insights/task-summary-chart.css.ts
var circleIndicator3 = "_13e6myw2";
var legend3 = "_13e6myw1";
var tooltipContainer2 = "_13e6myw3";
var tooltipItem2 = "_13e6myw4";

// src/insights/task-summary-chart.tsx
import { Fragment as Fragment22, jsx as jsx63, jsxs as jsxs46 } from "react/jsx-runtime";
function TaskSummaryChart(props) {
  const { data, xAxisKey, list } = props;
  const structuredData = structureTaskSummaryData(data, xAxisKey);
  const barchartWidth = getChartWidth(structuredData.length) - 70;
  return /* @__PURE__ */ jsxs46(Fragment22, { children: [
    /* @__PURE__ */ jsx63(
      "div",
      {
        style: { overflowX: "scroll", maxWidth: "913px", overflowY: "hidden" },
        children: /* @__PURE__ */ jsxs46(
          BarChart2,
          {
            width: barchartWidth,
            height: 320,
            data: structuredData,
            margin: {
              top: 20,
              right: 20,
              bottom: 40,
              left: 20
            },
            barSize: 60,
            barGap: 8,
            children: [
              /* @__PURE__ */ jsx63(
                XAxis4,
                {
                  dataKey: xAxisKey,
                  axisLine: false,
                  tickLine: false,
                  interval: 0,
                  tick: /* @__PURE__ */ jsx63(AxisNameTick, { list }),
                  textAnchor: "middle",
                  tickSize: 20
                }
              ),
              /* @__PURE__ */ jsx63(
                Tooltip5,
                {
                  content: /* @__PURE__ */ jsx63(CustomTooltip4, {}),
                  cursor: false,
                  animationDuration: 100
                }
              ),
              /* @__PURE__ */ jsx63(
                Bar2,
                {
                  dataKey: "completed",
                  stackId: "a",
                  fill: "#94B7B9",
                  activeBar: /* @__PURE__ */ jsx63(Rectangle2, { fill: colors20.teal700 }),
                  background: { fill: colors20.bgrey300 }
                }
              ),
              /* @__PURE__ */ jsx63(
                Bar2,
                {
                  dataKey: "onTrack",
                  stackId: "a",
                  fill: "#C0E3E4",
                  activeBar: /* @__PURE__ */ jsx63(Rectangle2, { fill: colors20.nteal400 })
                }
              ),
              /* @__PURE__ */ jsx63(
                Bar2,
                {
                  dataKey: "overdue",
                  stackId: "a",
                  fill: "#F9C9C9",
                  activeBar: /* @__PURE__ */ jsx63(Rectangle2, { fill: colors20.poppy400 }),
                  children: /* @__PURE__ */ jsx63(LabelList2, { dataKey: "total", position: "top" })
                }
              )
            ]
          }
        )
      }
    ),
    /* @__PURE__ */ jsx63(Divider10, {}),
    /* @__PURE__ */ jsxs46("div", { className: legend3, children: [
      /* @__PURE__ */ jsx63(
        "div",
        {
          className: circleIndicator3,
          style: { backgroundColor: colors20.teal700 }
        }
      ),
      /* @__PURE__ */ jsx63(Text42, { kind: "systemSm", color: "grey500", bold: true, children: "Completed" }),
      /* @__PURE__ */ jsx63(
        "div",
        {
          className: circleIndicator3,
          style: { backgroundColor: colors20.nteal400 }
        }
      ),
      /* @__PURE__ */ jsx63(Text42, { kind: "systemSm", color: "grey500", bold: true, children: "On Track" }),
      /* @__PURE__ */ jsx63(
        "div",
        {
          className: circleIndicator3,
          style: { backgroundColor: colors20.poppy400 }
        }
      ),
      /* @__PURE__ */ jsx63(Text42, { kind: "systemSm", color: "grey500", bold: true, children: "Overdue" })
    ] })
  ] });
}
function CustomTooltip4({ active, payload }) {
  if (active && payload && payload.length && payload[0]?.payload) {
    const { completed, onTrack, overdue, total: total2 } = payload[0].payload;
    return /* @__PURE__ */ jsxs46("div", { className: tooltipContainer2, children: [
      /* @__PURE__ */ jsxs46(
        Text42,
        {
          kind: "label",
          textTransform: "capitalize",
          as: "div",
          className: tooltipItem2,
          children: [
            "Total ",
            /* @__PURE__ */ jsx63(Text42, { kind: "bodyLine", children: total2 })
          ]
        }
      ),
      /* @__PURE__ */ jsx63(Divider10, {}),
      /* @__PURE__ */ jsxs46(Text42, { kind: "systemSm", as: "div", className: tooltipItem2, children: [
        "Completed",
        /* @__PURE__ */ jsx63(Text42, { kind: "bodyLine", as: "span", children: completed })
      ] }),
      /* @__PURE__ */ jsxs46(Text42, { kind: "systemSm", as: "div", className: tooltipItem2, children: [
        "On Track",
        /* @__PURE__ */ jsx63(Text42, { kind: "bodyLine", as: "span", children: onTrack })
      ] }),
      /* @__PURE__ */ jsxs46(Text42, { kind: "systemSm", as: "div", className: tooltipItem2, children: [
        "Overdue",
        /* @__PURE__ */ jsx63(Text42, { kind: "bodyLine", as: "span", children: overdue })
      ] })
    ] });
  }
  return null;
}

// src/components/print-rubric-table/print-rubric-table.tsx
import { Fragment as Fragment23 } from "react";
import { Text as Text43 } from "@vericus/cadmus-ui";

// src/components/print-rubric-table/print-rubric-table.css.ts
var criterionCell = "_1bo3q1g1";
var criterionCellContent = "_1bo3q1g2";
var scaleCell = "_1bo3q1g4";
var scaleContent = "_1bo3q1g6";
var scaleHeader = "_1bo3q1g3";
var scaleValue = "_1bo3q1g5";
var table = "_1bo3q1g0";

// src/components/print-rubric-table/print-rubric-table.tsx
import { Fragment as Fragment24, jsx as jsx64, jsxs as jsxs47 } from "react/jsx-runtime";
function PrintRubricTable(props) {
  const { criteria, format: format2, reversedScale, hideScores = false } = props;
  return /* @__PURE__ */ jsx64("table", { className: table, children: /* @__PURE__ */ jsx64("tbody", { children: criteria.map((criterion, index) => /* @__PURE__ */ jsx64(
    CriterionRow,
    {
      criterion,
      format: format2,
      reversedScale,
      hideScores
    },
    `criterion-${index}`
  )) }) });
}
function CriterionRow(props) {
  const { criterion, format: format2, reversedScale, hideScores } = props;
  const maxScore = criterion.maxScore;
  return /* @__PURE__ */ jsxs47(Fragment23, { children: [
    /* @__PURE__ */ jsxs47("tr", { children: [
      /* @__PURE__ */ jsx64("th", { rowSpan: 2, className: criterionCell, children: /* @__PURE__ */ jsxs47("div", { className: criterionCellContent, children: [
        /* @__PURE__ */ jsx64(Text43, { kind: "headingFive", inline: true, children: criterion.title }),
        /* @__PURE__ */ jsx64(Text43, { kind: "bodyLine", children: criterion.description }),
        maxScore != null && /* @__PURE__ */ jsx64(Text43, { kind: "label", as: "span", children: format2 === "PERCENTAGE" ? `Weighting ${maxScore}%` : `Max Points ${maxScore}` })
      ] }) }),
      criterion.scaleItems.map((scaleItem, index) => /* @__PURE__ */ jsx64("th", { className: scaleHeader, children: /* @__PURE__ */ jsx64(Text43, { kind: "headingSix", inline: true, children: scaleItem.scaleLabel }) }, `scale-item-${index}`))
    ] }),
    /* @__PURE__ */ jsx64("tr", { children: criterion.scaleItems.map((scaleItem, index) => /* @__PURE__ */ jsx64(
      ScaleCell,
      {
        scaleItem,
        reversedScale,
        hasAbsoluteFormat: format2 === "ABSOLUTE",
        hideScores
      },
      `scale-item-${index}`
    )) })
  ] });
}
function ScaleCell(props) {
  const { scaleItem, reversedScale, hasAbsoluteFormat, hideScores } = props;
  const startValue = reversedScale ? scaleItem.rangeTo : scaleItem.rangeFrom;
  const endValue = reversedScale ? scaleItem.rangeFrom : scaleItem.rangeTo;
  return /* @__PURE__ */ jsxs47("td", { className: scaleCell, children: [
    /* @__PURE__ */ jsx64("div", { className: scaleContent, children: !hideScores && /* @__PURE__ */ jsxs47(
      "div",
      {
        style: {
          display: "flex",
          columnGap: 8,
          alignItems: "center",
          marginBottom: 8
        },
        children: [
          !hasAbsoluteFormat && startValue != null && /* @__PURE__ */ jsxs47(Fragment24, { children: [
            /* @__PURE__ */ jsx64(
              Text43,
              {
                as: "div",
                kind: "bodyLine",
                color: "navy400",
                bold: true,
                className: scaleValue,
                children: startValue
              }
            ),
            /* @__PURE__ */ jsx64(Text43, { kind: "bodyLine", as: "span", children: "-" })
          ] }),
          endValue != null && /* @__PURE__ */ jsx64(
            Text43,
            {
              as: "div",
              kind: "bodyLine",
              color: "navy400",
              bold: true,
              className: scaleValue,
              children: endValue
            }
          )
        ]
      }
    ) }),
    /* @__PURE__ */ jsx64(Text43, { kind: "bodyLine", children: scaleItem.description })
  ] });
}

// src/components/video-response-playback/video-response-playback.tsx
import { forwardRef as forwardRef2, useImperativeHandle, useRef as useRef3, useState as useState6 } from "react";
import { Skeleton, Text as Text44 } from "@vericus/cadmus-ui";
import clsx7 from "clsx";

// src/components/video-response-playback/video-response-playback.css.ts
var container7 = "d68q5h0";
var hiddenVideo = "d68q5h3 d68q5h2";
var overlay = "d68q5h4";
var skeleton = "d68q5h1";
var video = "d68q5h2";

// src/components/video-response-playback/video-response-playback.tsx
import { jsx as jsx65, jsxs as jsxs48 } from "react/jsx-runtime";
var VideoResponsePlayback = forwardRef2(function VideoResponsePlayback2({ url, className, autoPlay, onPlay, onTimeUpdate }, ref) {
  const [loading, setLoading] = useState6(true);
  const [hasVideoTrack, setHasVideoTrack] = useState6(true);
  const [error, setError] = useState6(false);
  const videoRef = useRef3(null);
  const [prevUrl, setPrevUrl] = useState6(url);
  if (url !== prevUrl) {
    setPrevUrl(url);
    setLoading(true);
    setHasVideoTrack(true);
    setError(false);
  }
  useImperativeHandle(
    ref,
    () => ({
      seek: (seconds) => {
        if (videoRef.current) {
          videoRef.current.currentTime = seconds;
        }
      }
    }),
    []
  );
  return /* @__PURE__ */ jsxs48("div", { className: clsx7(container7, className), children: [
    loading && /* @__PURE__ */ jsx65(Skeleton, { className: skeleton }),
    /* @__PURE__ */ jsx65(
      "video",
      {
        ref: videoRef,
        src: url,
        preload: "auto",
        controls: true,
        autoPlay,
        "aria-label": "Recorded video response",
        className: loading ? hiddenVideo : video,
        onLoadedMetadata: (e) => {
          const v = e.currentTarget;
          setHasVideoTrack(v.videoWidth > 0 && v.videoHeight > 0);
        },
        onLoadedData: () => setLoading(false),
        onError: () => {
          setLoading(false);
          setError(true);
        },
        onPlay,
        onTimeUpdate: onTimeUpdate ? (e) => onTimeUpdate(e.currentTarget.currentTime) : void 0
      }
    ),
    !loading && error && /* @__PURE__ */ jsx65("div", { className: overlay, role: "alert", children: /* @__PURE__ */ jsx65(Text44, { kind: "bodySm", color: "white", children: "Video failed to load" }) }),
    !loading && !error && !hasVideoTrack && /* @__PURE__ */ jsx65("div", { className: overlay, children: /* @__PURE__ */ jsx65(Text44, { kind: "bodySm", color: "white", children: "Camera is off" }) })
  ] });
});
export {
  AutoMarkSummary,
  BlockAttachments,
  ChartLoading,
  CheckBoxFilters,
  CommentsChart,
  ENABLED_FLAGS_QUERY,
  EditorContentPreview,
  EditorPreviewer,
  FeatureFlag,
  FeatureFlagsContext,
  FeatureFlagsProvider,
  OptionList,
  PrintInstructionPreview,
  PrintRubricTable,
  QuestionPreview,
  QuestionPreviewNext,
  QuestionPrintPreview,
  STATIC_PREVIEW_EXTENSIONS,
  TaskSummaryChart,
  TimelineScrubber,
  UserPasteRatioChart,
  UserWordCountChart,
  VideoResponsePlayback,
  WorkManifestClassicPreview,
  WorkManifestMFAPreview,
  buildQuestionPreviewNextProps,
  buildQuestionPreviewProps,
  deserialisePMDoc,
  docContainsNodeType,
  drawDot,
  drawLabel,
  drawPolygon,
  formatBytes,
  formatDecimalNumber,
  formatNumber,
  formatNumberString,
  formatPoints,
  formatPointsSimple,
  formatReferencingStyle,
  formatSecondsAsTimer,
  getClosestPayloadItem,
  getTenantStyles,
  headerFormatPoints,
  isFootnotingEnabled,
  isHangingIndent,
  isReferencingStyle,
  referencesTitle,
  renderClassicWork,
  renderPointMarker,
  renderPrintInstruction,
  renderWorkManifest,
  requiresManualMarking,
  shuffleArray,
  shuffleChoices,
  shuffleWithSeed,
  structCumulativePasteRatio,
  useFeatureFlag,
  useFeatureFlagState,
  useHotspotCanvas,
  validateReferencingStyle,
  workDocumentPreviewMode
};
//# sourceMappingURL=index.mjs.map